/* eslint-disable max-lines */
// Auth types
import {
  VerifyFreeUserResponseSchema,
  type VerifyFreeUserResponse,
  type ResetPasswordRequest,
  type RegisterInviteRequest,
  type RegisterSSORequest,
  type LoginRequest,
  type ForgotPasswordRequest,
} from "@/pages/authentication/types";
// SKU Cost types
import {
  CostBreakdownResponse as SkuCostBreakdownResponse,
  CostBreakdownResponseSchema as SkuCostBreakdownResponseSchema,
  SkuBreakdownParams,
  SkuCostParams,
  ComputeEfficiencyResponse,
  ComputeEfficiencyResponseSchema,
  DeploymentTypeTrendsResponse,
  DeploymentTypeTrendsResponseSchema,
  EngineTypeTrendsResponse,
  EngineTypeTrendsResponseSchema,
  TypeTrendsParams,
} from "@/pages/sku/types";
// Clusters types (Databricks)
import {
  ClustersParams,
  ClustersResponse,
  ClustersResponseSchema,
  ClusterDetail,
  ClusterDetailSchema,
  ClusterDetailParams,
  ClusterTimeseriesParams,
  ClusterUtilizationResponse,
  ClusterUtilizationResponseSchema,
  ClusterCostResponse,
  ClusterCostResponseSchema,
  ClusterFilterOptions,
  ClusterFilterOptionsSchema,
  ClusterFilterParams,
  ClustersCostTrendsParams,
  ClustersCostTrendsResponse,
  ClustersCostTrendsResponseSchema,
} from "@/pages/clusters/types";
// Databricks cloud-cost types
import {
  CloudCostTrendsResponseSchema,
  CloudCostProjectionResponseSchema,
  type CloudCostChartRow,
  type CloudCostProjectionRow,
  type CloudCostTrendsParams,
  type CloudCostProjectionParams,
} from "@/pages/databricks/cloudCost/cloudCostTaxonomy";
// Jobs types (Databricks)
import {
  JobRunTrendsParams,
  JobRunTrendsResponse,
  JobRunTrendsResponseSchema,
  JobsParams,
  JobsResponse,
  JobsResponseSchema,
  JobsSummaryParams,
  JobsSummaryResponse,
  JobsSummaryResponseSchema,
  JobCostTrendsParams,
  JobCostTrendsResponse,
  JobCostTrendsResponseSchema,
  PaginatedFilterOptions,
  PaginatedFilterOptionsSchema,
  PaginatedJobCreatorsResponse,
  PaginatedJobCreatorsResponseSchema,
  JobTaskRunsResponse,
  JobTaskRunsResponseSchema,
  JobTasksInventoryResponse,
  JobTasksInventoryResponseSchema,
  DatabricksTaskRunGraphPoint,
  DatabricksTaskRunGraphPointSchema,
  DatabricksTaskRunsGraphParams,
  DatabricksTaskStats,
  DatabricksTaskStatsSchema,
  DatabricksTaskStatsParams,
  JobRunSparkUiResponse,
  JobRunSparkUiResponseSchema,
  JobDetailResponse,
  JobDetailResponseSchema,
  JobRunClustersResponse,
  JobRunClustersResponseSchema,
  JobRunsResponse,
  JobRunsResponseSchema,
  JobStatsResponse,
  JobStatsResponseSchema,
  DbxAutoRecommendationsResponse,
  DbxAutoRecommendationsResponseSchema,
  ToggleAutoTuneResponse,
  ToggleAutoTuneResponseSchema,
} from "@/pages/jobs/types";
// Cluster Auto Tune types (Databricks all-purpose clusters)
import {
  AutoTuneStatusResponse,
  AutoTuneStatusResponseSchema,
  AutoTuneHistoryResponse,
  AutoTuneHistoryResponseSchema,
  AutoTuneSavingsTrendsResponse,
  AutoTuneSavingsTrendsResponseSchema,
  ClusterAutoTuneRecommendationsResponse,
  ClusterAutoTuneRecommendationsResponseSchema,
  ClusterAutoTuneToggleResponse,
  ClusterAutoTuneToggleResponseSchema,
} from "@/pages/clusters/auto-tune/types";
// Spark Analysis types (Databricks)
import {
  SparkAnalysisSummaryResponse,
  SparkAnalysisSummaryResponseSchema,
  SparkOpportunitiesResponse,
  SparkOpportunitiesResponseSchema,
  SparkStageMetricsPaginatedResponse,
  SparkStageMetricsPaginatedResponseSchema,
  SparkTasksPaginatedResponse,
  SparkTasksPaginatedResponseSchema,
} from "@/pages/jobs/sparkTypes";
// Workspaces Cost types
import {
  CostTotalResponse,
  CostTotalResponseSchema,
  CostBreakdownResponse as WorkspacesCostBreakdownResponse,
  CostBreakdownResponseSchema as WorkspacesCostBreakdownResponseSchema,
  ProductTrendsResponse,
  ProductTrendsResponseSchema,
  WorkspacesResponse,
  WorkspacesResponseSchema,
  WorkspaceEfficiencyResponse,
  WorkspaceEfficiencyResponseSchema,
  WorkspacesCostParams,
  WorkspacesTrendsParams,
  WorkspacesBreakdownParams,
} from "@/pages/workspaces/types";
// Databricks page types
import {
  type CostAttributionResponse,
  CostAttributionResponseSchema,
  type DatabricksServingParams,
  type DatabricksServingTrendsParams,
  type DatabricksServingDetailParams,
  type DatabricksServingUsersParams,
  type EndpointUsersResponse,
  EndpointUsersResponseSchema,
  type OverallUsageTrendsResponse,
  OverallUsageTrendsResponseSchema,
  type ServingEndpointDetailResponse,
  ServingEndpointDetailResponseSchema,
  type ServingEndpointsResponse,
  ServingEndpointsResponseSchema,
  type DatabricksClusterFilterOptions,
  DatabricksClusterFilterOptionsSchema,
  type WarehouseFilterOptions,
  WarehouseFilterOptionsSchema,
  type UserFilterOptions,
  UserFilterOptionsSchema,
  type QueryFilterOptions,
  QueryFilterOptionsSchema,
  type QueryStatementIdsResponse,
  QueryStatementIdsResponseSchema,
  type QueryStatementIdSearchParams,
  type DatabricksQueriesParams,
  type DatabricksUnifiedQueriesParams,
  type QuerySummaryResponse,
  QuerySummaryResponseSchema,
  type QueryTrendsResponse,
  QueryTrendsResponseSchema,
  type QueriesResponse,
  QueriesResponseSchema,
  type SlowQueriesResponse,
  SlowQueriesResponseSchema,
  type SpillQueriesResponse,
  SpillQueriesResponseSchema,
  type FullScanQueriesResponse,
  FullScanQueriesResponseSchema,
  type FailedQueriesResponse,
  FailedQueriesResponseSchema,
  type StatementTypeBreakdownResponse,
  StatementTypeBreakdownResponseSchema,
  type UserQueryBreakdownResponse,
  UserQueryBreakdownResponseSchema,
  type UnifiedQueriesResponse,
  UnifiedQueriesResponseSchema,
  type QueryDetailResponse,
  QueryDetailResponseSchema,
  type QuerySqlLineageResponse,
  QuerySqlLineageResponseSchema,
  type DatabricksUserDetailParams,
  type DatabricksUsersParams,
  type DatabricksUserTrendsParams,
  type TopUsersByDataReadResponse,
  TopUsersByDataReadResponseSchema,
  type TopUsersByQueriesResponse,
  TopUsersByQueriesResponseSchema,
  type UserDetailResponse,
  UserDetailResponseSchema,
  type UsersResponse,
  UsersResponseSchema,
  type UserStatementBreakdownResponse,
  UserStatementBreakdownResponseSchema,
  type UserTrendsResponse,
  UserTrendsResponseSchema,
  type ServicePrincipalConfig,
  ServicePrincipalConfigSchema,
} from "@/pages/databricks/types";
// Databricks AI/ML serving types
import {
  type AiMlServicesParams,
  type AiMlServicesSummaryResponse,
  AiMlServicesSummaryResponseSchema,
  type UnifiedAIMLEndpointsParams,
  type UnifiedAIMLEndpointsListResponse,
  UnifiedAIMLEndpointsListResponseSchema,
  type AimlEndpointFiltersParams,
  type AimlEndpointFiltersResponse,
  AimlEndpointFiltersResponseSchema,
  type AimlEndpointNamesParams,
  type AimlEndpointNamesResponse,
  AimlEndpointNamesResponseSchema,
  type AiGatewayEndpointDetailParams,
  type AiGatewayEndpointDetail,
  AiGatewayEndpointDetailSchema,
  type AiGatewayEndpointTrendResponse,
  AiGatewayEndpointTrendResponseSchema,
  type ModelServingEndpointDetailParams,
  type ModelServingEndpointDetail,
  ModelServingEndpointDetailSchema,
  type ModelServingEndpointTrendResponse,
  ModelServingEndpointTrendResponseSchema,
  type VectorSearchEndpointDetailParams,
  type VectorSearchEndpointDetail,
  VectorSearchEndpointDetailSchema,
  type VectorSearchEndpointTrendResponse,
  VectorSearchEndpointTrendResponseSchema,
  type EndpointCallersParams,
  type EndpointCallersResponse,
  EndpointCallersResponseSchema,
  type AIFunctionUsageBreakdownParams,
  type AIFunctionUsageBreakdownResponse,
  AIFunctionUsageBreakdownResponseSchema,
} from "@/pages/databricks/aiml/types";
// Databricks Summary page types
import {
  type SummaryCostOverviewResponse,
  SummaryCostOverviewResponseSchema,
  type SummaryProductTrendsResponse,
  SummaryProductTrendsResponseSchema,
  type SummaryCostEstimateTrendsResponse,
  SummaryCostEstimateTrendsResponseSchema,
  type SummaryCostTotalEstimateResponse,
  SummaryCostTotalEstimateResponseSchema,
  type SummarySavingsOverallResponse,
  SummarySavingsOverallResponseSchema,
  type SummarySavingsOverallEstimateResponse,
  SummarySavingsOverallEstimateResponseSchema,
  type SummaryCostParams,
  type SummaryTrendsParams,
  type SummarySavingsParams,
} from "@/pages/databricks/summary/types";
// Subscription types - imported at top to avoid mid-file imports
import type {
  SubscriptionListResponse,
  SubscriptionV2Summary,
  SubscriptionHistoryResponse,
  BackendSubscriptionCreateRequest,
  SubscriptionResponse,
  SubscriptionV2Template,
  SubscriptionTemplatesListResponse,
  SubscriptionDeleteResponse,
  MetricFilterValuesResponse,
  MetricsCatalogResponse,
  NotificationTestRequest,
  NotificationTestResponse,
} from "@/pages/subscriptions/services/types";
import {
  SubscriptionListResponseSchema,
  SubscriptionV2SummarySchema,
  SubscriptionHistoryResponseSchema,
  SubscriptionResponseSchema,
  SubscriptionV2TemplateSchema,
  SubscriptionTemplatesListResponseSchema,
  MetricsCatalogResponseSchema,
  SubscriptionDeleteResponseSchema,
  MetricFilterValuesResponseSchema,
  NotificationTestResponseSchema,
} from "@/pages/subscriptions/services/types";
// Databricks Connection types
import {
  type DatabricksConnectionCreateRequest,
  type DatabricksConnectionListResponse,
  DatabricksConnectionListResponseSchema,
  type DatabricksConnectionResponse,
  DatabricksConnectionResponseSchema,
  type DatabricksConnectionTestRequest,
  type DatabricksConnectionTestResponse,
  DatabricksConnectionTestResponseSchema,
} from "@/pages/datastores/databricksTypes";
import type { SubscriptionFiltersResponse } from "@/pages/subscriptions/components/list/types";
import {
  SubscriptionFiltersResponseSchema,
  type SubscriptionListFilters,
  type SubscriptionSummaryFilters,
} from "@/pages/subscriptions/components/list/types";
import type {
  SubscriptionPreviewParams,
  SubscriptionPreviewResponse,
  MetricFilterOptionsResponse,
} from "@/pages/subscriptions/components/wizard/types";
// AI Services types
import {
  aiServicesSummaryResponseSchema,
  aiServiceGraphResponseSchema,
  aiServiceUsageResponseSchema,
  aiServiceBreakdownResponseSchema,
  aiServiceFilterValuesResponseSchema,
  type AIServicesSummaryResponse,
  type AIServicesSummaryParams,
  type AIServiceGraphResponse,
  type AIServiceGraphParams,
  type AIServiceUsageResponse,
  type AIServiceUsageParams,
  type AIServiceBreakdownResponse,
  type AIServiceBreakdownParams,
  type AIServiceFilterValuesResponse,
  type AIServiceFilterValuesParams,
} from "@/pages/query/ai-services/types";
import {
  SubscriptionPreviewResponseSchema,
  MetricFilterOptionsResponseSchema,
} from "@/pages/subscriptions/components/wizard/types";

import {
  AggregatedComputeGraphResponse,
  AggregatedComputeGraphResponseSchema,
  AggregatedServerlessGraphResponse,
  AggregatedServerlessGraphResponseSchema,
  AggregatedStorageGraphResponse,
  AggregatedStorageGraphResponseSchema,
  AggregatedCloudServicesGraphResponse,
  AggregatedCloudServicesGraphResponseSchema,
  AggregatedAiServicesGraphResponse,
  AggregatedAiServicesGraphResponseSchema,
  AssistedSavingsResponse,
  AssistedSavingsResponseSchema,
  AutoTuneSavingsSummaryResponse,
  AutoTuneSavingsSummaryResponseSchema,
  CostAnalysisSubscribeRequest,
  CostAnalysisSubscriptionResponse,
  CostAnalysisSubscriptionsListResponse,
  CostAnalysisUnsubscribeRequest,
  CostAnalysisUnsubscribeResponse,
  costAnalysisSubscriptionResponseSchema,
  costAnalysisSubscriptionsListResponseSchema,
  costAnalysisUnsubscribeResponseSchema,
  GetSummaryOpportunitiesDataResponse,
  GetSummaryOpportunitiesDataResponseSchema,
  OpportunitiesSummary,
  OverallCostSummaryResponse,
  OverallCostSummaryResponseSchema,
  OverallSavingsEstimateResponse,
  OverallSavingsEstimateResponseSchema,
  OverallSavingsResponse,
  OverallSavingsResponseSchema,
  SummaryCostGraphRequest,
  TotalCosts,
  TotalCostsSchema,
} from "../pages/summary/context/types";
import {
  GetExportLineageItem,
  GetExportLineageItemSchema,
  GetExportLineagePageSchema,
} from "../pages/exportLineage/types";
import {
  FilterResponse,
  FilterResponseSchema,
  GetOpportunitiesDataResponse,
  GetOpportunitiesDataResponseSchema,
  GetOpportunitiesRedirectionRequest,
  GetOpportunitiesRedirectResponse,
  GetOpportunitiesRedirectResponseSchema,
  GetOpportunitiesResourceNameFilterRequest,
  GetOpportunitiesResourceRequest,
  GetOpportunitiesResourceResponse,
  GetOpportunitiesResourceResponseSchema,
  GetOpportunitiesSummaryRequest,
  GetOpportunitiesSummaryResponse,
  GetOpportunitiesSummaryResponseSchema,
  GetOpportunitiesUserFilterRequest,
  GetOpportunitiesWarehouseFilterRequest,
  GetOpportunityUpdatesRequest,
  GetOpportunityUpdatesResponse,
  GetOpportunityUpdatesResponseSchema,
  GetQuerySQLLineageResponse,
  GetQuerySQLLineageResponseSchema,
  GetScheduledEventsOpportunityRequest,
  GetScheduledEventUpdatesRequest,
  GetScheduledEventUpdatesResponse,
  GetScheduledEventUpdatesResponseSchema,
  GetGroupedOpportunitiesDataResponseSchema,
  GroupedOpportunitiesFilters,
  OpportunitiesFilters,
  Page,
  PriorityOpportunityResponse,
  PriorityOpportunityResponseSchema,
  UpdateOpportunitiesDataRequest,
  GetGroupedOpportunitiesDataResponse,
  OpportunityDetailResponse,
  OpportunityDetailResponseSchema,
  OpportunityComment,
  CommentSchema,
  LabelSuggestionResponse,
  LabelSuggestionResponseSchema,
  SetLabelsRequest,
  AddCommentRequest,
  UpdateCommentRequest,
} from "../pages/explorer/types";
import {
  dbtJobRunItem,
  DbtJobRunItem,
  DbtJobListingItem,
  dbtJobListingItemSchema,
  DbtJobListingSummary,
  dbtJobListingSummarySchema,
  GetDbtJobListingParams,
  GetDbtJobListingSummaryParams,
  DbtJobFilterItem,
  dbtJobFilterItemSchema,
  DbtTagFilterItem,
  dbtTagFilterItemSchema,
  DbtModel,
  dbtModelRunSummary,
  DbtModelRunSummary,
  dbtModelSchema,
  DbtProjectAndIntegration,
  dbtProjectAndIntegrationSchema,
  DbtHealthCheckResponse,
  dbtHealthCheckResponseSchema,
  DbtTasksSummary,
  dbtTasksSummarySchema,
  GetDbtJobRunsListParams,
  GetDbtJobRunsListGraphDataParams,
  GetDbtModelRunSummaryParams,
  GetDbtTasksSummaryParams,
  GetDbtModelsListParams,
  GetSnowflakeJobGraphData,
  getSnowflakeJobGraphDataSchema,
  GetSnowflakeJobItem,
  getSnowflakeJobItemSchema,
  GetSnowflakeJobRunItem,
  getSnowflakeJobRunItemSchema,
  GetSnowflakeJobRunsGraphData,
  getSnowflakeJobRunsGraphDataSchema,
  GetSnowflakeJobSummary,
  getSnowflakeJobSummarySchema,
  GetSnowflakeJobTaskItem,
  getSnowflakeJobTaskItemSchema,
  QueryAllCSVFilters,
  QueryDetailsResponse,
  QueryDetailsResponseSchema,
  QueryGroupsFilters,
  QueryJobExecutionsFilters,
  QueryJobsFilters,
  QueryJobsResponse,
  QueryJobsResponseSchema,
  QueryTextIndicesResponse,
  QueryTextIndicesResponseSchema,
  QueryTextResponse,
  QueryTextResponseSchema,
  RunCompareDiff,
  runCompareDiffSchema,
  SnowflakeJobDefinitionResponse,
  snowflakeJobDefinitionResponseSchema,
  SnowflakeJobFilters,
  snowflakeJobFiltersSchema,
  SnowflakeJobsFilterPaginatedParams,
  SnowflakeJobsFilterStringPage,
  SnowflakeJobsFilterLabelValuePage,
  snowflakeJobsFilterStringPageSchema,
  snowflakeJobsFilterLabelValuePageSchema,
  WorkloadAttributeValues,
  workloadAttributeValuesSchema,
  WorkloadAttributes,
  workloadAttributesSchema,
  SnowflakeJobSettings,
  snowflakeJobSettingsSchema,
  StreamlitNotebookChildQueriesListResponse,
  StreamlitNotebookJobListResponse,
  CommonJobListSchema,
  StreamlitNotebookJobRunsListResponse,
  StreamlitNotebookJobRunsSummaryDataResponse,
  CommonRunsSummarySchema,
  StreamlitNotebookJobsGraphDataResponse,
  StreamlitNotebookJobsGraphDataResponseSchema,
  StoredProceduresJobsGraphDataSchema,
  StoredProceduresJobListResponse,
  StoredProceduresJobRunsSummaryDataResponse,
  StoredProceduresJobsGraphDataResponse,
  StoredProceduresJobRunsListResponse,
  StoredProceduresChildQueriesListResponse,
  StoredProceduresJobRunsGraphData,
  StoredProceduresChildQueriesSchema,
  StoredProceduresRunsListSchema,
  StreamlitNotebookChildQueriesSchema,
  StreamlitNotebookRunsListSchema,
  CommonRunsListGraphDataSchema,
  GetStoredProceduresChildQueriesParams,
  GetStoredProceduresJobsGraphDataParams,
  GetStoredProceduresJobsParams,
  GetStoredProcedureRunsListParams,
  GetStoredProceduresRunsGraphDataParams,
  GetStoredProceduresRunsSummaryParams,
  GetStoredProceduresJobsListFilterValuesParams,
  StoredProceduresJobsListFilterValuesResponse,
  StoredProceduresJobsListFilterValuesSchema,
  TaskListSchema,
  TaskListResponse,
  TasksGraphDataSchema,
  TasksGraphDataResponse,
  TasksListFilterValuesSchema,
  TasksListFilterValuesResponse,
  GetTasksJobsParams,
  GetTasksDateParams,
  TaskRunSchema,
  TaskRunResponse,
  GetTasksRunsParams,
  TaskRunsSummarySchema,
  TaskRunsSummaryResponse,
  TaskRunsGraphDataSchema,
  TaskRunsGraphDataResponse,
  QueryFiltersSchema,
  QueryFilters,
  GetQueryFiltersParams,
  FilterParams,
  QueryGroup,
  QueryGroupSchema,
  QueryItemSchema,
  UpdateQueryJobNameRequest,
  UpdateQueryJobNameResponse,
  UpdateQueryJobNameResponseSchema,
  UpdateQueryGroupNameRequest,
  UpdateQueryGroupNameResponse,
  UpdateQueryGroupNameResponseSchema,
  GetSnowflakeJobsParams,
  GetSnowflakeJobRunsParams,
  GetSnowflakeJobRunsGraphDataParams,
  GetSnowflakeJobRunTasksParams,
  GetSnowflakeJobFiltersParams,
  GetSnowflakeJobDefinitionParams,
  GetQueryJobFiltersParams,
  GetQueryJobDataParams,
  GetQueryGroupDataParams,
  QueryJobFiltersResponse,
  QueryJobFiltersResponseSchema,
  QueryGraphDataResponse,
  QueryGraphDataResponseSchema,
  QueryJobExecutionsResponse,
  QueryJobExecutionsResponseSchema,
  QueryJobDefinitionsResponse,
  QueryJobDefinitionsResponseSchema,
  QueryItem,
  CommonGraphSummarySchema,
  CommonGraphSummaryResponse,
  GetSnowflakeJobGraphDataRequestParams,
  QueryGroupStats,
  QueryGroupStatsSchema,
  flattenCustomTagFilters,
} from "../pages/query/types";
import {
  StartingNodeDBTParams,
  GetSQLLineageParams,
  PostColumnsDBTParams,
  PostConnectedColumnsDBTParams,
  TableSchemaResponseSchema,
  TableSchemaResponse,
  ColumnLineageResponseSchema,
  ColumnLineageResponse,
  ConnectedTablesResponseSchema,
  ConnectedTablesResponse,
  TableNodeResponseSchema,
  TableNodeResponse,
  SQLLineageResponseSchema,
  SQLLineageResponse,
  IntegrationEnvironmentsResponse,
  IntegrationEnvironmentsResponseSchema,
} from "@/pages/dbtModels/types";

import { APIClient } from "./api_helper";
import {
  FilterZodSchema,
  GetOpportunitiesDynamicFilterResponse,
  GetOpportunitiesDynamicFilterResponseSchema,
  GetTableLastRefreshedResponse,
  GetTableLastRefreshedResponseSchema,
  GetTenantInfoResponse,
  pageSchema,
  StandardResponse,
  StandardResponseSchema,
  StandardResponseWithWarning,
  StandardResponseWithWarningSchema,
  TableRkFilterResponse,
  TableRkFilterResponseSchema,
  tenantInfoSchema,
  UrlResponse,
  urlResponse,
  UserLoginOutResponse,
  userLoginOutSchema,
  CompanyInfoResponse,
  CompanyInfoResponseSchema,
  CompanyListResponse,
  CompanyListResponseSchema,
  DemoSessionResponse,
  demoSessionResponseSchema,
  EmailInstanceListResponseSchema,
  type EmailInstanceListResponse,
} from "./types";
import {
  CompanyRegisterResponseSchema,
  type CompanyRegisterRequest,
  type CompanyRegisterResponse,
} from "@/pages/settings/types";
import { downloadBlob } from "./utils";
import {
  FilterType,
  PresetIdResponseSchema,
  SavedFilter,
  savedFilterSchema,
} from "@/components/tags/types";
import { z } from "zod";
import {
  GetAllUserGroupsResponseSchema,
  UploadUserGroupResponse,
  UploadUserGroupResponseSchema,
  UploadUserResponse,
  UploadUserResponseSchema,
  UserGroupWithMembersAndRulesZodSchema,
} from "@/pages/settings/components/userGroups/types";
import {
  DbtModelsResponse,
  DbtModelsResponseSchema,
  DbtModelFiltersResponse,
  DbtModelFiltersResponseSchema,
  DbtModelByRkRequest,
  DbtModelByRkResponse,
  DbtModelByRkResponseSchema,
  GetDbtModelsParams,
} from "@/types/dbtModels";
import {
  PlaygroundAllSessionResponse,
  playgroundAllSessionResponseSchema,
  PlaygroundSessionDetailsResponse,
  playgroundSessionDetailsResponseSchema,
  ShareLinkResponse,
  shareLinkResponseSchema,
  ShareLinkListResponse,
  shareLinkListResponseSchema,
  SharedSessionResponse,
  sharedSessionResponseSchema,
  ShareLinkCreateRequest,
  PlaygroundScheduleResponse,
  playgroundScheduleResponseSchema,
  PlaygroundScheduleListResponse,
  playgroundScheduleListResponseSchema,
  PlaygroundScheduleRunsListResponse,
  playgroundScheduleRunsListResponseSchema,
  PlaygroundScheduleCreateRequest,
  PlaygroundScheduleUpdateRequest,
  ShareToSlackRequest,
  ShareToSlackResponse,
  shareToSlackResponseSchema,
  SessionLifecycleStatus,
  SessionLifecycleListFilter,
  SessionStatusUpdateResponse,
  sessionStatusUpdateResponseSchema,
} from "@/pages/playground/types";
import {
  SharedQueryResultListResponse,
  sharedQueryResultListResponseSchema,
  SharedQueryResultDetail,
  sharedQueryResultDetailSchema,
  DeleteQueryResultResponse,
  deleteQueryResultResponseSchema,
} from "@/pages/queryResult/types";
import {
  DbtDocsFiltersSchema,
  DbtDocsFilters,
  GetDbtDocsShareListParams,
  DbtDocsShareListResponse,
  DbtDocsShareListResponseSchema,
  DbtDocsShareUrlResponse,
  DbtDocsShareUrlResponseSchema,
  CurrentUserResponse,
  StandardApiResponseSchema as DbtDocsStandardApiResponseSchema,
  StandardApiResponse as DbtDocsStandardApiResponse,
} from "@/pages/dbtDocs/types";
import {
  GetAllExperimentsParams,
  GetAllExperimentsResponse,
  GetAllExperimentsResponseSchema,
  ExperimentDetail,
  ExperimentDetailSchema,
  ExperimentSummaryResponse,
  ExperimentSummaryResponseSchema,
  CreateExperimentRequest,
  UpdateExperimentStatusRequest,
  SchemaMetadataResponse,
  SchemaMetadataResponseSchema,
  StandardApiResponseSchema as ExperimentStandardApiResponseSchema,
} from "@/pages/experimentation/types";
import {
  GetMaskDataResponse,
  GetMaskDataResponseSchema,
  SetMaskDataRequest,
  SetMaskDataResponse,
  SetMaskDataResponseSchema,
} from "@/pages/security/types";
import {
  DBTConfigListResponse,
  DBTConfigListResponseSchema,
  DBTConfigFiltersResponse,
  DBTConfigFiltersResponseSchema,
  DBTConfigDetail,
  DBTConfigDetailSchema,
  DBTConfigRequest,
  DBTConfigMutationResponse,
  DBTConfigMutationResponseSchema,
  DBTConfigChecksResponse,
  DBTConfigChecksResponseSchema,
  DBTConfigChecksFiltersResponse,
  DBTConfigChecksFiltersResponseSchema,
} from "@/types/dbtconfig";
import {
  IntegrationListResponse,
  IntegrationListResponseSchema,
  IntegrationDetails,
  IntegrationDetailsSchema,
  PostIntegrationRequest,
  PostIntegrationResponse,
  PostIntegrationResponseSchema,
} from "@/types/integration";
import {
  User,
  UserSchema,
  UserListResponseSchema,
  UserListResponse,
  InviteUserResponse,
  InviteUserResponseSchema,
  UpdateUserResponse,
  UpdateUserResponseSchema,
  UpdateUserRoleResponse,
  UpdateUserRoleResponseSchema,
  DeleteUserResponse,
  DeleteUserResponseSchema,
  AssetSchema,
  Asset,
  SSOUserInfoResponse,
  SSOUserInfoResponseSchema,
  BaseUserSchema,
} from "@/types";

import {
  AnnualizedCost,
  AnnualizedCostSchema,
  CostBreakdown,
  CostBreakdownSchema,
  TableDataFilters,
  TableQueriesResponse,
  TableQueriesResponseSchema,
  TableQueryFilters,
  TableQueryFiltersResponse,
  TableQueryFiltersResponseSchema,
  TableDetailResponse,
  TableDetailResponseSchema,
  TableDescriptionResponse,
  TableDescriptionResponseSchema,
  UpdateTablePurposeRequest,
  TableInsightsResponse,
  TableInsightsResponseSchema,
  PostCustomTableInsightRequest,
  PostCustomTableInsightResponse,
  PostCustomTableInsightResponseSchema,
  GetTableIdRequest,
  GetTableIdResponse,
  GetTableIdResponseSchema,
  TableStorageOverTimeResponse,
  TableStorageOverTimeResponseSchema,
  GetColumnAccessHistoryParams,
  ColumnAccessHistoryResponse,
  ColumnAccessHistoryResponseSchema,
  ColumnUserAccessHistoryFiltersResponse,
  ColumnUserAccessHistoryFiltersResponseSchema,
  GetColumnUserAccessHistoryRequest,
  ColumnUserAccessHistoryResponse,
  ColumnUserAccessHistoryResponseSchema,
  DatasetListResponse,
  DatasetListResponseSchema,
  TableFiltersResponseSchema,
} from "@/pages/datasets/types";
import {
  GetScheduleResponseSchema,
  ScheduleApprovalRequest,
  ScheduleCreateRequest,
  ScheduleRejectionRequest,
  ScheduleResponse,
  ScheduleResponseSchema,
  ScheduleUpdateRequest,
  WarehouseCostSummarySchema,
  WarehouseCostSummaryResponse,
  WarehouseSummaryCostGraphSchema,
  WarehouseSummaryCostGraphResponse,
  WarehouseListResponse,
  WarehouseListResponseSchema,
  WarehouseFiltersResponse,
  WarehouseFiltersResponseSchema,
  GetWarehouseSummaryCostGraphParams,
  GetAllWarehousesParams,
  GetWarehouseFiltersParams,
  GetWarehouseParams,
  WarehouseDetailsSchema,
  WarehouseDetails,
  WarehouseAutoSavingsResponse,
  WarehouseAutoSavingsResponseSchema,
  GetWarehouseAutoSavingsParams,
  WarehouseAutoTuneConfiguration,
  WarehouseAutoTuneConfigurationSchema,
  SetWarehouseAutoTuneConfigurationRequest,
  WarehouseAutoTuneToggleResponse,
  WarehouseAutoTuneToggleResponseSchema,
  WarehouseDecisionDataResponse,
  WarehouseDecisionDataResponseSchema,
  GetWarehouseDecisionDataParams,
  WarehouseQueryExecutionTimesResponse,
  WarehouseQueryExecutionTimesResponseSchema,
  GetWarehouseQueryExecutionTimesParams,
  WarehouseAuditEventsResponse,
  WarehouseAuditEventsResponseSchema,
  GetWarehouseAuditEventsParams,
  GetWarehouseCostParams,
  WarehouseCostResponse,
  WarehouseCostResponseSchema,
  WarehouseClusterUtilizationResponse,
  WarehouseClusterUtilizationResponseSchema,
  WarehouseLastEventTimeResponse,
  WarehouseLastEventTimeResponseSchema,
  WarehouseMetadataResponse,
  WarehouseMetadataResponseSchema,
  WarehouseInsightsRequest,
  WarehouseInsightsResponse,
  WarehouseInsightsResponseSchema,
  ToggleWarehouseSchedulesResponse,
  ToggleWarehouseSchedulesResponseSchema,
  ToggleWarehouseAutoSavingsResponse,
  ToggleWarehouseAutoSavingsResponseSchema,
  WarehouseAutoSavingsConfigurationResponse,
  WarehouseAutoSavingsConfigurationResponseSchema,
  GetWarehouseSchedulerEventsParams,
} from "@/pages/warehouse/types";
import {
  RolesTableFilterParams,
  UsersTableFilterParams,
  GovernanceSummary,
  GovernanceSummarySchema,
  GovernanceFilters,
  GovernanceFiltersSchema,
  GovernanceUsersResponse,
  GovernanceUsersResponseSchema,
  GovernanceRolesResponse,
  GovernanceRolesResponseSchema,
  GovernanceUserDetails,
  GovernanceUserDetailsSchema,
  GovernanceRoleDetails,
  GovernanceRoleDetailsSchema,
  GovernanceCostGraph,
  GovernanceCostGraphSchema,
  GovernanceFilterParams,
} from "@/pages/governance/types";
import {
  AccessRuleCoveragePreviewResponseSchema,
  type AccessRuleCoveragePreviewResponse,
  type OwnershipCostPreviewResponse,
  OwnershipCostPreviewResponseSchema,
  type OwnershipConflictsResponse,
  OwnershipConflictsResponseSchema,
  type ConflictingTeamsResponse,
  ConflictingTeamsResponseSchema,
  type TeamConflictsResponse,
  TeamConflictsResponseSchema,
  type TeamConflictDetailsResponse,
  TeamConflictDetailsResponseSchema,
  type OwnershipRequestPayload,
  type OwnershipRequestsData,
} from "@/pages/settings/components/userGroups/types";
import {
  ApplyRecommendedEventsRequestSchema,
  ApproveScheduleResponseSchema,
} from "@/pages/warehouse/components/scheduler/types";
import { NotificationsResponseSchema } from "@/pages/settings/components/notifications/constants";
import { SSOProvider } from "./constants";
import {
  AddTagToNodeRequest,
  AssociateTagResponseSchema,
  GetTagsParams,
  Tag,
  TagSchema,
  TagsResponseSchema,
  TagTypeSchema,
} from "@/pages/settings/tags/types";
import {
  type FetchSnowflakeDatabasesRequest,
  type FetchSnowflakeDatabasesResponse,
  fetchSnowflakeDatabasesResponseSchema,
  type DatastoreResponseInferred,
  DatastoreResponseSchema,
  type DatastoreTypeResponse,
  DatastoreTypeResponseSchema,
  type TestConnectionResponse,
  TestConnectionResponseSchema,
  type SyncDatastoreResponse,
  SyncDatastoreResponseSchema,
  type ToggleSyncScheduleResponse,
  ToggleSyncScheduleResponseSchema,
  type AddDatastoreRequest,
  type AddDatabasesRequest,
  type AddDatastoreResponse,
  AddDatastoreResponseSchema,
  type TestDatastoreConnectionDuringCreationRequest,
  type TestDatastoreConnectionDuringCreationResponse,
  TestDatastoreConnectionDuringCreationResponseSchema,
  AvailableDatabasesResponseSchema,
  type AvailableDatabasesResponse,
  DatastoreDetailResponseSchema,
  type DatastoreDetailResponse,
  GetSyncHistoryResponseSchema,
  type GetSyncHistoryResponse,
  type GetSyncHistoryParams,
  // dbt Cloud types
  type DbtCloudConnectionCreateRequest,
  type DbtCloudConnectionListResponse,
  DbtCloudConnectionListResponseSchema,
  type DbtCloudConnectionResponse,
  DbtCloudConnectionResponseSchema,
  type DbtCloudDiscoveryResponse,
  DbtCloudDiscoveryResponseSchema,
  type DbtCloudEnvironmentResponse,
  DbtCloudEnvironmentResponseSchema,
  type DbtCloudEnvironmentUpdateRequest,
  type DbtCloudProjectResponse,
  DbtCloudProjectResponseSchema,
  type DbtCloudProjectUpdateRequest,
  type DbtCloudScheduleResponse,
  DbtCloudScheduleResponseSchema,
  type DbtCloudScheduleUpdateRequest,
  type DbtCloudConnectionUpdateRequest,
  type DbtCloudSyncHistoryResponse,
  DbtCloudSyncHistoryResponseSchema,
  type DbtCloudSyncResponse,
  DbtCloudSyncResponseSchema,
  type DbtCloudValidateTokenRequest,
  type DbtCloudValidationResponse,
  DbtCloudValidationResponseSchema,
  type AzureDatabricksConnectionRequest,
  type AzureDatabricksPrivateLinkConnectionRequest,
  type AzureDatabricksSavedConnectionResponse,
  AzureDatabricksSavedConnectionResponseSchema,
} from "@/pages/datastores/types";
import {
  TableauProject,
  TableauProjectSchema,
} from "@/pages/datastores/components/DatastoreAdd/DatastoreConnections/types";
import {
  Alert,
  AlertSchema,
  AlertEventsResponse,
  AlertEventsResponseSchema,
  AlertRulesResponse,
  AlertRulesResponseSchema,
  SlackChannelsResponse,
  SlackChannelsResponseSchema,
  GetAlertEventsParams,
  GetAlertRulesParams,
} from "@/pages/alerts/types";
import { SnowflakeStageMetadata } from "@altimateai/ui-components/lineage";
import {
  DbtInfoResponse,
  DbtInfoResponseSchema,
  snowflakeStageMetadataSchema,
} from "@/components/lineage/types";
import {
  CommonIntegrationInstance,
  CommonIntegrationInstanceSchema,
  Connection,
  ConnectionSchema,
  DatamateIntegrationSchema,
  GetDatamateWithStatsResponse,
  GetDatamateWithStatsResponseSchema,
  Teammate,
  TeammateSchema,
  TeammateCreateResponse,
  TeammateCreateResponseSchema,
  TeammateUpdateResponse,
  TeammateUpdateResponseSchema,
  Usage,
  UsageSchema,
  AssistAggregationsResponse,
  AssistAggregationsResponseSchema,
  GuardrailAggregationsResponse,
  GuardrailAggregationsResponseSchema,
} from "@/pages/mcp/mcpSlice";
import {
  KnowledgeCatalogItem,
  KnowledgeCatalogItemSchema,
  KnowledgeDocumentUploadResponseSchema,
  KnowledgeDocumentUploadResponse,
  KnowledgeDocumentUploadToExistingResponseSchema,
  KnowledgeDocumentUploadToExistingResponse,
  KnowledgeBaseDetailResponseSchema,
  KnowledgeBaseDetailResponse,
  KnowledgeDocumentHistoryItemSchema,
  KnowledgeDocumentHistoryItem,
  DocumentChunksResponseSchema,
  DocumentChunksResponse,
} from "@/pages/knowledgeCatalog/types";
import {
  teamSpendFilters,
  TeamSpendFilters,
  teamSpendGraphAndSummary,
  TeamSpendGraphAndSummary,
  TeamSpendQueryParams,
  TeamSpendTotalSpends,
  teamSpendTotalSpendsSchema,
} from "@/types/teamSpend";
import {
  LineageResponse,
  lineageResponseSchema,
  GetColumnsRequest,
  GetColumnsResponse,
  getColumnsResponseSchema,
  TerminalNodeRequest,
  TerminalNodeResponse,
  terminalNodeResponseSchema,
  ConnectedColumnsRequest,
  ConnectedColumnsResponse,
  connectedColumnsResponseSchema,
  UpstreamDownstreamTablesResponse,
  upstreamDownstreamTablesResponseSchema,
} from "@/components/lineage/types";
import {
  BiReportFilters,
  BiReportFiltersSchema,
  BiUserReportsFilters,
  BiUserReportsFiltersSchema,
  DashboardAccessActivityResponse,
  DashboardAccessActivityResponseSchema,
  DashboardDownloadFiltersSchema,
  DashboardRefreshActivityResponse,
  DashboardRefreshActivityResponseSchema,
  BiReport,
  BiReportSchema,
  BiReportsPaginatedResponse,
  BiReportsPaginatedResponseSchema,
  BiDashboardSummary,
  BiDashboardSummarySchema,
  BiDashboardsQueryParams,
  BiUserReport,
  BiUserReportSchema,
  BiUserSummary,
  BiUserSummarySchema,
  GetUserReportsParams,
} from "@/pages/biReports/types";
import {
  Memory,
  MemoryResponse,
  MemoryResponseSchema,
  MemorySchema,
} from "@/pages/teammate/dataMates/memory/types";
import {
  AccountsCostQueryParams,
  accountsListSchema,
  AccountsList,
  totalCostSchema,
  TotalCost,
  chartDataListSchema,
  ChartDataList,
  tableDataListSchema,
  TableDataList,
} from "@/types/accounts-cost";
import { MetabaseDashboard, MetabaseDashboardSchema } from "@/pages/spaces/types";
import {
  SpacesResponse,
  SpacesResponseSchema,
  SpacesRequest,
  SyncAllDashboardsResponse,
  SyncAllDashboardsResponseSchema,
} from "@/pages/spaces/types";

import {
  computePoolDailyCostItemSchema,
  ComputePoolEntry,
  computePoolEntrySchema,
  computePoolMetadataItemSchema,
  computePoolOverviewSchema,
  computePoolsGraphDataSchema,
  computePoolsSummarySchema,
} from "@/pages/computePools/types";

import {
  contractItemSchema,
  contractsSummarySchema,
  ContractDetailResponse,
  ContractDetailResponseSchema,
  ValidateContractRequest,
  ValidateContractResponse,
  ValidateContractResponseSchema,
} from "@/pages/contracts/types";
import {
  IncidentSchema,
  type Incident,
  GetIncidentsResponseSchema,
  type GetIncidentsResponse,
  type GetIncidentsParams,
  IncidentsSummarySchema,
  type IncidentsSummary,
  type GetIncidentsSummaryParams,
  SnoozeIncidentResponseSchema,
  type SnoozeIncidentResponse,
  type SnoozeIncidentParams,
  GetTimelineEventsResponseSchema,
  type GetTimelineEventsResponse,
  GetDebugSQLResponseSchema,
  type GetDebugSQLResponse,
} from "@/pages/incidents/types";
import {
  SlackWebhookResponseSchema,
  type SlackWebhookResponse,
  type SlackWebhookRequest,
} from "@/pages/settings/types";
import {
  MyUsageTrendsResponse,
  myUsageTrendsResponseSchema,
  OrgUsageTrendsResponse,
  orgUsageTrendsResponseSchema,
  Transaction,
  TransactionGroupedByUser,
  transactionGroupedByUserSchema,
  AggregatedTask,
  aggregatedTaskSchema,
  transactionSchema,
  PlansResponse,
  PlansResponseSchema,
  CheckoutSessionRequest,
  CreateCheckoutSessionResponse,
  CreateCheckoutSessionResponseSchema,
  PostCheckoutSessionRequest,
  DowngradePlanRequest,
  DowngradePlanFeedbackRequest,
  DowngradePlanFeedbackResponse,
  DowngradePlanFeedbackResponseSchema,
  CreditUsageResponse,
  CreditUsageResponseSchema,
  AllPlansResponse,
  AllPlansResponseSchema,
  WalletResponse,
  WalletResponseSchema,
  WalletDepositResponse,
  WalletDepositResponseSchema,
  TokenUsageResponse,
  TokenUsageResponseSchema,
} from "@/pages/credits/types";

import {
  CreateOrUpdatePromptRequest,
  PromptLibraryTagsResponse,
  PromptResponse,
  PromptResponseSchema,
  StarterQuestionsResponse,
  StarterQuestionsResponseSchema,
  EnhancedPromptResponse,
  EnhancedPromptResponseSchema,
} from "@/pages/playground/promptLibrary/types";
import {
  TablesResponse,
  TablesResponseSchema,
  TableFiltersResponse,
  TagsResponse,
  TagsResponseSchema as DatasetTagsResponseSchema,
  ContractsResponse,
  ContractsResponseSchema,
  RecentTablesResponse,
  RecentTablesResponseSchema,
  TableRkResponse,
  TableRkResponseSchema,
  UpdateColumnDescriptionRequest,
  GetTablesParams,
} from "@/pages/datasets/types";
import { UserView } from "@/modules/playground";
import { GetAIFeedbackResponse, GetAIFeedbackResponseSchema } from "@/components/feedback/types";
import { OtherGraphResponseSchema } from "@/pages/summary/context/schemas";
import {
  SSOTeamSyncConfigResponseSchema,
  SSOGroupMappingListSchema,
  SSOGroupMappingResponseSchema,
  SSOPreviewResponseSchema,
  SSOAuditEventListResponseSchema,
  SSOExternalGroupListResponseSchema,
  SSOBulkUploadResponseSchema,
  SSOBulkMappingUploadResponseSchema,
  SSOTargetsResponseSchema,
  type SSOTeamSyncConfigUpdatePayload,
  type SSOGroupMappingUpdatePayload,
} from "@/pages/settings/sso-group-sync/types";

export const api = new APIClient();

// Summary v2 - Cost endpoints
export const getOverallCost = (params: SummaryCostGraphRequest) =>
  api.get<OverallCostSummaryResponse>(
    "/summary/v2/cost/overall",
    params,
    0,
    OverallCostSummaryResponseSchema
  );

export const getOverallCostEstimate = (params: SummaryCostGraphRequest) =>
  api.get<OverallCostSummaryResponse>(
    "/summary/v2/cost/overall/estimate",
    params,
    0,
    OverallCostSummaryResponseSchema
  );

export const getComputeCost = (params: SummaryCostGraphRequest) =>
  api.get<AggregatedComputeGraphResponse>(
    "/summary/v2/cost/compute",
    params,
    0,
    AggregatedComputeGraphResponseSchema
  );

export const getComputeCostEstimate = (params: SummaryCostGraphRequest) =>
  api.get<AggregatedComputeGraphResponse>(
    "/summary/v2/cost/compute/estimate",
    params,
    0,
    AggregatedComputeGraphResponseSchema
  );

export const getStorageCost = (params: SummaryCostGraphRequest) =>
  api.get<AggregatedStorageGraphResponse>(
    "/summary/v2/cost/storage",
    params,
    0,
    AggregatedStorageGraphResponseSchema
  );

export const getStorageCostEstimate = (params: SummaryCostGraphRequest) =>
  api.get<AggregatedStorageGraphResponse>(
    "/summary/v2/cost/storage/estimate",
    params,
    0,
    AggregatedStorageGraphResponseSchema
  );

export const getServerlessCost = (params: SummaryCostGraphRequest) =>
  api.get<AggregatedServerlessGraphResponse>(
    "/summary/v2/cost/serverless",
    params,
    0,
    AggregatedServerlessGraphResponseSchema
  );

export const getServerlessCostEstimate = (params: SummaryCostGraphRequest) =>
  api.get<AggregatedServerlessGraphResponse>(
    "/summary/v2/cost/serverless/estimate",
    params,
    0,
    AggregatedServerlessGraphResponseSchema
  );

export const getTotalCost = (params: SummaryCostGraphRequest) =>
  api.get<TotalCosts>("/summary/v2/cost/total", params, 0, TotalCostsSchema);

export const getTotalCostEstimate = (params: SummaryCostGraphRequest) =>
  api.get<TotalCosts>("/summary/v2/cost/total/estimate", params, 0, TotalCostsSchema);

export const getOtherCost = (params: SummaryCostGraphRequest) =>
  api.get<z.infer<typeof OtherGraphResponseSchema>>(
    "/summary/v2/cost/other",
    params,
    0,
    OtherGraphResponseSchema
  );

export const getOtherCostEstimate = (params: SummaryCostGraphRequest) =>
  api.get<z.infer<typeof OtherGraphResponseSchema>>(
    "/summary/v2/cost/other/estimate",
    params,
    0,
    OtherGraphResponseSchema
  );

export const getCloudServicesCost = (params: SummaryCostGraphRequest) =>
  api.get<AggregatedCloudServicesGraphResponse>(
    "/summary/v2/cost/cloud_services",
    params,
    0,
    AggregatedCloudServicesGraphResponseSchema
  );

export const getCloudServicesCostEstimate = (params: SummaryCostGraphRequest) =>
  api.get<AggregatedCloudServicesGraphResponse>(
    "/summary/v2/cost/cloud_services/estimate",
    params,
    0,
    AggregatedCloudServicesGraphResponseSchema
  );

export const getAiServicesCost = (params: SummaryCostGraphRequest) =>
  api.get<AggregatedAiServicesGraphResponse>(
    "/summary/v2/cost/ai_services",
    params,
    0,
    AggregatedAiServicesGraphResponseSchema
  );

export const getAiServicesCostEstimate = (params: SummaryCostGraphRequest) =>
  api.get<AggregatedAiServicesGraphResponse>(
    "/summary/v2/cost/ai_services/estimate",
    params,
    0,
    AggregatedAiServicesGraphResponseSchema
  );

// Wrapper for backward compatibility - maps costType to specific function with Zod validation
type CostBreakdownResponse =
  | OverallCostSummaryResponse
  | AggregatedComputeGraphResponse
  | AggregatedStorageGraphResponse
  | AggregatedServerlessGraphResponse
  | AggregatedCloudServicesGraphResponse
  | AggregatedAiServicesGraphResponse
  | TotalCosts;

export const getCostBreakdown = <R extends CostBreakdownResponse>(
  path: string,
  params: SummaryCostGraphRequest
): Promise<R | undefined> => {
  switch (path) {
    case "overall":
      return getOverallCost(params) as Promise<R | undefined>;
    case "overall/estimate":
      return getOverallCostEstimate(params) as Promise<R | undefined>;
    case "compute":
      return getComputeCost(params) as Promise<R | undefined>;
    case "compute/estimate":
      return getComputeCostEstimate(params) as Promise<R | undefined>;
    case "storage":
      return getStorageCost(params) as Promise<R | undefined>;
    case "storage/estimate":
      return getStorageCostEstimate(params) as Promise<R | undefined>;
    case "serverless":
      return getServerlessCost(params) as Promise<R | undefined>;
    case "serverless/estimate":
      return getServerlessCostEstimate(params) as Promise<R | undefined>;
    case "cloud_services":
      return getCloudServicesCost(params) as Promise<R | undefined>;
    case "cloud_services/estimate":
      return getCloudServicesCostEstimate(params) as Promise<R | undefined>;
    case "ai_services":
      return getAiServicesCost(params) as Promise<R | undefined>;
    case "ai_services/estimate":
      return getAiServicesCostEstimate(params) as Promise<R | undefined>;
    case "total":
      return getTotalCost(params) as Promise<R | undefined>;
    case "total/estimate":
      return getTotalCostEstimate(params) as Promise<R | undefined>;
    case "other":
      return getOtherCost(params) as Promise<R | undefined>;
    case "other/estimate":
      return getOtherCostEstimate(params) as Promise<R | undefined>;
    default:
      console.warn(`Unknown cost path: ${path}`);
      return getOverallCost(params) as Promise<R | undefined>;
  }
};

// Summary v2 - Savings endpoints
export const getAutonomousSavings = (params: SummaryCostGraphRequest) =>
  api.get<AutoTuneSavingsSummaryResponse>(
    "/summary/v2/savings/autonomous",
    params,
    0,
    AutoTuneSavingsSummaryResponseSchema
  );

export const getAutonomousSavingsEstimate = (params: SummaryCostGraphRequest) =>
  api.get<AutoTuneSavingsSummaryResponse>(
    "/summary/v2/savings/autonomous/estimate",
    params,
    0,
    AutoTuneSavingsSummaryResponseSchema
  );

export const getAssistedSavings = (params: SummaryCostGraphRequest) =>
  api.get<AssistedSavingsResponse>(
    "/summary/v2/savings/assisted",
    params,
    0,
    AssistedSavingsResponseSchema
  );

export const getAssistedSavingsEstimate = (params: SummaryCostGraphRequest) =>
  api.get<AssistedSavingsResponse>(
    "/summary/v2/savings/assisted/estimate",
    params,
    0,
    AssistedSavingsResponseSchema
  );

export const getOverallSavings = (params: SummaryCostGraphRequest) =>
  api.get<OverallSavingsResponse>(
    "/summary/v2/savings/overall",
    params,
    0,
    OverallSavingsResponseSchema
  );

export const getOverallSavingsEstimate = (params: SummaryCostGraphRequest) =>
  api.get<OverallSavingsEstimateResponse>(
    "/summary/v2/savings/overall/estimate",
    params,
    0,
    OverallSavingsEstimateResponseSchema
  );

// Wrapper for backward compatibility - maps savingsType to specific function
export const getSavingsBreakdown = (
  path: string,
  params: SummaryCostGraphRequest
): Promise<
  | AutoTuneSavingsSummaryResponse
  | OverallSavingsResponse
  | OverallSavingsEstimateResponse
  | AssistedSavingsResponse
  | undefined
> => {
  switch (path) {
    case "autonomous":
      return getAutonomousSavings(params);
    case "autonomous/estimate":
      return getAutonomousSavingsEstimate(params);
    case "assisted":
      return getAssistedSavings(params);
    case "assisted/estimate":
      return getAssistedSavingsEstimate(params);
    case "overall":
      return getOverallSavings(params);
    case "overall/estimate":
      return getOverallSavingsEstimate(params);
    default:
      console.warn(`Unknown savings path: ${path}`);
      return getAutonomousSavings(params);
  }
};

export const getSummaryPageOpportunities = (params: OpportunitiesSummary) =>
  api.get<GetSummaryOpportunitiesDataResponse[]>(
    "/summary/v2/opportunities",
    params,
    0,
    z.array(GetSummaryOpportunitiesDataResponseSchema)
  );

export const getOpportunities = (data: GroupedOpportunitiesFilters) =>
  api.get<Page<GetOpportunitiesDataResponse>>(
    "/opportunities/",
    data,
    0,
    pageSchema(GetOpportunitiesDataResponseSchema)
  );

// NOTE: backend route is `/opportunities/implemented` (NO trailing slash).
// `/opportunities/` (the regular list) keeps its trailing slash to match
// its existing FastAPI route — don't "normalize" these two consistently
// or one of them will start hitting the wrong handler.
export const getImplementedOpportunities = (data: OpportunitiesFilters) =>
  api.get<Page<GetOpportunitiesDataResponse>>(
    "/opportunities/implemented",
    data,
    0,
    pageSchema(GetOpportunitiesDataResponseSchema)
  );

export const downloadImplementedOpportunitiesCSV = (params: OpportunitiesFilters) =>
  downloadCSV(`/opportunities/implemented`, params);

export const downloadOpportunitiesCSV = (params: OpportunitiesFilters) =>
  downloadCSV(`/opportunities/`, params);

export const getGroupedOpportunities = (data: GroupedOpportunitiesFilters) =>
  api.get<GetGroupedOpportunitiesDataResponse[]>(
    "/opportunities/grouped",
    data,
    0,
    z.array(GetGroupedOpportunitiesDataResponseSchema)
  );

export const getPriorityOpportunities = (data: GroupedOpportunitiesFilters) =>
  api.get<PriorityOpportunityResponse[]>(
    "/opportunities/priority",
    data,
    0,
    z.array(PriorityOpportunityResponseSchema)
  );

export const getOpportunityDetail = (params: { opportunity_id: string; instance_id: number }) =>
  api.get<OpportunityDetailResponse>(
    "/opportunities/detail",
    params,
    0,
    OpportunityDetailResponseSchema
  );

export const getOpportunityComments = (opportunity_id: string, instance_id: number) =>
  api.get<OpportunityComment[]>(
    "/opportunities/comments",
    { opportunity_id, instance_id },
    0,
    z.array(CommentSchema)
  );

export const postOpportunityComment = (data: AddCommentRequest) =>
  api.post<OpportunityComment>("/opportunities/comments", data, CommentSchema);

export const putOpportunityComment = (comment_id: string, data: UpdateCommentRequest) =>
  api.put<OpportunityComment, UpdateCommentRequest>(
    `/opportunities/comments/${comment_id}`,
    data,
    CommentSchema
  );

export const deleteOpportunityComment = (comment_id: string) =>
  api.delete<StandardResponse>(`/opportunities/comments/${comment_id}`, {}, StandardResponseSchema);

export const putOpportunityLabels = (data: SetLabelsRequest) =>
  api.put<string[], SetLabelsRequest>("/opportunities/labels", data, z.array(z.string()));

export const getLabelSuggestions = () =>
  api.get<LabelSuggestionResponse>(
    "/opportunities/labels/suggestions",
    {},
    0,
    LabelSuggestionResponseSchema
  );

export const downloadGroupedOpportunitiesCSV = (params: GroupedOpportunitiesFilters) =>
  downloadCSV(`/opportunities/grouped/`, params);

export const updateOpportunities = (data: UpdateOpportunitiesDataRequest) =>
  api.post<StandardResponse>("/opportunities/", data, StandardResponseSchema);

export const getOpportunitiesSummary = (data: GetOpportunitiesSummaryRequest) =>
  api.get<GetOpportunitiesSummaryResponse[]>(
    "/opportunities/summary",
    data,
    0,
    z.array(GetOpportunitiesSummaryResponseSchema)
  );

export const getOpportunitiesResourceNameFilters = (
  data: GetOpportunitiesResourceNameFilterRequest
) =>
  api.get<GetOpportunitiesDynamicFilterResponse>(
    "/opportunities/resource_name_filter",
    data,
    10000,
    GetOpportunitiesDynamicFilterResponseSchema
  );

export const getOpportunitiesUserFilters = (data: GetOpportunitiesUserFilterRequest) =>
  api.get<GetOpportunitiesDynamicFilterResponse>(
    "/opportunities/filters/user",
    data,
    10000,
    GetOpportunitiesDynamicFilterResponseSchema
  );

export const getOpportunitiesWarehouseFilters = (data: GetOpportunitiesWarehouseFilterRequest) =>
  api.get<GetOpportunitiesDynamicFilterResponse>(
    "/opportunities/filters/warehouse",
    data,
    10000,
    GetOpportunitiesDynamicFilterResponseSchema
  );

export const getOpportunityNameFilters = (params: { workspace_id?: string[] } = {}) =>
  api.get<FilterResponse[]>(
    "/opportunities/filters/opportunity_name",
    params,
    0,
    z.array(FilterResponseSchema)
  );

export const getOpportunitiesResource = (data: GetOpportunitiesResourceRequest) =>
  api.get<GetOpportunitiesResourceResponse>(
    "/opportunities/resource",
    data,
    0,
    GetOpportunitiesResourceResponseSchema
  );

export const getScheduledEventsOpportunityData = (data: GetScheduledEventsOpportunityRequest) =>
  api.get<GetOpportunitiesDataResponse[]>(
    "/opportunities/scheduled_events_opportunity_data",
    data,
    0,
    z.array(GetOpportunitiesDataResponseSchema)
  );

export const getOpportunitiesRedirection = (data: GetOpportunitiesRedirectionRequest) =>
  api.get<GetOpportunitiesRedirectResponse>(
    "/opportunities/redirection",
    data,
    0,
    GetOpportunitiesRedirectResponseSchema
  );

export const getOpportunityUpdates = (data: GetOpportunityUpdatesRequest) =>
  api.get<GetOpportunityUpdatesResponse[]>(
    "/opportunities/updates",
    data,
    0,
    z.array(GetOpportunityUpdatesResponseSchema)
  );

export const getQueryById = (id: string, params: Record<string, string>) =>
  api.get<QueryDetailsResponse>(`/query/v2/items/${id}`, params, 0, QueryDetailsResponseSchema);

// Get all queries with pagination and filtering
export const getAllQueries = (params: Partial<FilterParams>) =>
  api.get<Page<QueryItem>>("/query/v2/", params, 0, pageSchema(QueryItemSchema));

// Get last fetched time for queries
export const getLastFetchedTime = () =>
  api.get<{ time_string: string }>(
    "/query/last_fetched_time",
    {},
    0,
    z.object({ time_string: z.string() })
  );

// Download all queries as CSV file
export const getAllQueriesCSVFile = (params: Partial<FilterParams>) =>
  api
    // @ts-expect-error responseType 'blob' not in api client definition
    .get("/query/v2/download", params, undefined, undefined, {
      responseType: "blob",
      headers: {
        Accept: "text/csv",
      },
    })
    .then(blob => {
      return downloadBlob(blob as Blob);
    });

// Get query group details by hash
export const getQueryGroupDetail = (
  hash: string,
  params?: { start_date?: string; end_date?: string }
) => api.get(`/query/v2/groups/${hash}`, params || {}, 0, QueryGroupSchema);

// Update query group name
export const updateQueryGroupName = (hash: string, data: UpdateQueryGroupNameRequest) =>
  api.update<UpdateQueryGroupNameResponse>(
    `/query/groups/${hash}`,
    data,
    UpdateQueryGroupNameResponseSchema
  );

export const getQuerySqlLineageById = (id: string, params: Record<string, string>) =>
  api.get<GetQuerySQLLineageResponse>(
    `/query/v2/sql_lineage/${id}`,
    params,
    60000,
    GetQuerySQLLineageResponseSchema
  );

export const getQueryGroupSqlLineageById = (id: string, params: Record<string, string>) =>
  api.get<GetQuerySQLLineageResponse>(
    `/query/v2/sql_lineage_group/${id}`,
    params,
    60000,
    GetQuerySQLLineageResponseSchema
  );

export const getQueryGroupDetailsByHash = (hash: string, params: Record<string, string>) =>
  api.get<QueryGroupStats>(`/query/v2/query_group/stats/${hash}`, params, 0, QueryGroupStatsSchema);

export const getQueryTextByHash = (hash: string) =>
  api.get<QueryTextResponse>(
    `/query/v2/query_group/query_text/${hash}`,
    {},
    0,
    QueryTextResponseSchema
  );

export const getQueryTextAndIndicesByHash = (hash: string, data: { spans: string[] }) =>
  api.post<QueryTextIndicesResponse>(
    `/query/v2/indices/${hash}`,
    data,
    QueryTextIndicesResponseSchema
  );

export const getTenantInfo = () =>
  api.get<GetTenantInfoResponse>(`/auth/tenant-info`, {}, 10000, tenantInfoSchema);

export const getSSOUserInfo = () =>
  api.get<SSOUserInfoResponse>("/auth/supertokens_user_info", {}, 0, SSOUserInfoResponseSchema);

export const postRegisterSSO = (data: RegisterSSORequest) =>
  api.post<User>("/auth/register-sso", data, UserSchema);
export const updateTenantInfo = (data: Partial<GetTenantInfoResponse>) =>
  api.update<StandardResponse>(`/auth/tenant-info`, data, StandardResponseSchema);

// Register invite API - registers user from invite token
export const postRegisterInvite = (data: RegisterInviteRequest) =>
  api.post<User>("/auth/register-invite", data, UserSchema);

// Login API - authenticates user and returns user data with auth token
export const postLogin = (data: LoginRequest) => api.post<User>("/auth/login", data, UserSchema);

// Verify free user API - verifies a free user token
export const verifyFreeUser = (data: { token: string }) =>
  api.post<VerifyFreeUserResponse>("/companies/verify-user", data, VerifyFreeUserResponseSchema);

// User onboard API - marks user as onboarded
export const postUserOnboard = () =>
  api.post<StandardResponse>("/users/onboard", {}, StandardResponseSchema);

// Reset password API - resets user password with token
export const postResetPassword = (data: ResetPasswordRequest) =>
  api.post<StandardResponse>("/auth/reset-password", data, StandardResponseSchema);

// Get email instance list API - returns list of companies/instances associated with an email
export const getEmailInstanceList = (email: string) =>
  api.get<EmailInstanceListResponse>(
    `/companies/get-all-companies/${email}`,
    {},
    0,
    EmailInstanceListResponseSchema
  );

// Check company availability API - checks if company name is available for registration
export const companyAvailable = (company: string) =>
  api.get<StandardResponse>(`/companies/available/${company}`, {}, 0, StandardResponseSchema);

// Company register API - registers a new company/instance
export const companyRegister = (data: CompanyRegisterRequest) =>
  api.post<CompanyRegisterResponse>("/companies/register", data, CompanyRegisterResponseSchema);

// Incidents APIs
// Get incidents list API - retrieves paginated list of incidents/violations
export const getIncidents = (params: GetIncidentsParams) =>
  api.get<GetIncidentsResponse>("/violations/", params, 0, GetIncidentsResponseSchema);

// Get incidents summary API - retrieves summary statistics of incidents
export const getSummary = (params: GetIncidentsSummaryParams) =>
  api.get<IncidentsSummary>("/violations/summary", params, 0, IncidentsSummarySchema);

// Get incident by ID API - retrieves a single incident by its ID
export const getIncidentById = (id: string) =>
  api.get<Incident>(`/violations/id/${id}`, {}, 0, IncidentSchema);

// Snooze incident API - snoozes an incident until a specified time
export const postSnoozeIncident = (params: SnoozeIncidentParams) =>
  api.post<SnoozeIncidentResponse>(
    `/violations/${params.id}/snooze/${params.snoozed_till}`,
    {},
    SnoozeIncidentResponseSchema
  );

// Timeline events API - retrieves timeline events for a contract
export const getTimelineEventsByContractId = (id: string) =>
  api.get<GetTimelineEventsResponse>(`/events/${id}`, {}, 0, GetTimelineEventsResponseSchema);

// Debug SQL API - retrieves debug SQL information for an incident
export const getDebugSQLByIncidentId = (id: string) =>
  api.get<GetDebugSQLResponse>(`/debug_sql/${id}`, {}, 0, GetDebugSQLResponseSchema);

// Datastore APIs
// Get available databases API - retrieves list of available databases for a datastore
export const getAvailableDatabases = (datastoreId: string) =>
  api.get<AvailableDatabasesResponse>(
    `/data_stores/${datastoreId}/available_databases`,
    {},
    0,
    AvailableDatabasesResponseSchema
  );

// Get datastore detail API - retrieves details for a specific datastore
export const getDatastoreDetail = (datastoreId: string) =>
  api.get<DatastoreDetailResponse>(
    `/data_stores/${datastoreId}`,
    {},
    0,
    DatastoreDetailResponseSchema
  );

// Notification API
// Post Slack webhook API - creates/updates a Slack webhook notification channel
export const postSlackWebhook = (data: SlackWebhookRequest) =>
  api.post<SlackWebhookResponse>("/notification", data, SlackWebhookResponseSchema);

// Sync history API - retrieves sync history for a datastore
export const getSyncHistory = (datastoreId: string, params: GetSyncHistoryParams) =>
  api.get<GetSyncHistoryResponse>(
    `/events/sync_history/${datastoreId}`,
    params,
    0,
    GetSyncHistoryResponseSchema
  );

// Lineage API
// Altimate to Amundsen V2 API - retrieves terminal/starting node for lineage visualization
export const altimateToAmundsenV2 = (params: TerminalNodeRequest) =>
  api.get<Asset>("/lineage/v2/terminal", params, 0, AssetSchema);

export const postSendDatamateRequest = (requestType: string) =>
  api.post<StandardResponse>(
    "/notification/send_datamate_request",
    { request_type: requestType },
    StandardResponseSchema
  );

export const getExportLineage = (params: { page: number; size: number }) =>
  api.get<Page<GetExportLineageItem>>(
    `/dbt/v4/export-lineage`,
    params,
    0,
    GetExportLineagePageSchema
  );

export const getExportLineageItem = (id: string) =>
  api.get<GetExportLineageItem>(`/dbt/v4/export-lineage/${id}`, {}, 0, GetExportLineageItemSchema);

export const deleteExportLineageItem = (id: string) =>
  api.delete<StandardResponse>(`/dbt/v4/export-lineage/${id}`, {}, StandardResponseSchema);

export const getDownloadUrl = (s3_path: string) =>
  api.post<UrlResponse>("/dbt/v1/download_signed_url", { s3_path }, urlResponse);

export const getSavedFilters = (data: { applicableFor: string; type: FilterType }) => {
  return api.get<{ presets: SavedFilter[] }>(
    `/preset/`,
    { applicable_for: data.applicableFor, type: data.type },
    0,
    z.object({
      presets: z.array(savedFilterSchema.extend({ id: z.unknown() })),
    })
  );
};

export const createFilter = (data: Omit<SavedFilter, "id">) => {
  return api.post<{ preset_id: number }>(`/preset`, data, PresetIdResponseSchema);
};

export const updateFilter = (data: SavedFilter) => {
  return api.update<{ preset_id: number }>(`/preset/${data.id}`, data, PresetIdResponseSchema);
};

export const deleteFilter = (id: string) => {
  return api.delete<StandardResponse>(`/preset/${id}`, {}, StandardResponseSchema);
};

export const getLastTableRefreshedTime = (table_rk: string) =>
  api.get<GetTableLastRefreshedResponse>(
    `/tables/last_refreshed/?table_rk=${table_rk}`,
    {},
    10000,
    GetTableLastRefreshedResponseSchema
  );

export const regenerateToken = () =>
  api.post<UserLoginOutResponse>("/auth/regenerate_token", undefined, userLoginOutSchema);

export const downloadAutoTuneHistoryCSV = (
  instance_id: string,
  warehouse_rk: string,
  params: { period: string; event_types?: string[] }
) =>
  downloadCSV(
    `/warehouse/audit-events/csv/instance/${instance_id}/warehouse_rk/${warehouse_rk}`,
    params
  );

const downloadCSV = (endpoint: string, params: Record<string, unknown>) =>
  api
    // @ts-expect-error responseType 'blob' not in api client definition
    .get(endpoint, params, undefined, undefined, {
      responseType: "blob",
      headers: {
        Accept: "text/csv",
      },
    })
    .then(blob => {
      downloadBlob(blob as Blob);
      return true;
    });

const downloadCSVPost = (
  endpoint: string,
  data: Record<string, unknown>,
  params?: Record<string, string>
) => {
  const queryString = params ? new URLSearchParams(params).toString() : "";
  const urlWithParams = queryString ? `${endpoint}?${queryString}` : endpoint;
  return (
    api
      // @ts-expect-error responseType 'blob' not in api client definition
      .post(urlWithParams, data, undefined, {
        responseType: "blob",
        headers: {
          Accept: "text/csv",
        },
      })
      .then(blob => {
        downloadBlob(blob as Blob);
        return true;
      })
  );
};

export const downloadAltimateUsersCSV = (params: {
  company: string;
  withServiceUser: boolean;
  allUsers: boolean;
  type: string;
}) => downloadCSV(`/users/download`, params);

export const downloadBiUserReportsCSV = (
  params: Omit<GetUserReportsParams, "page" | "page_size">
) => downloadCSV(`/business_intelligence/users/download`, params);

export const downloadAllTableDataCSV = (params: TableDataFilters) =>
  downloadCSV(`/tables/tabledata`, params);

export const downloadWarehouseCSV = (params: GetAllWarehousesParams) =>
  downloadCSV(`/warehouse`, params as Record<string, unknown>);

export const downloadAllQueryCSV = (params: QueryAllCSVFilters) =>
  downloadCSV(`/query/v2/`, params);

export const downloadAllQueryGroupsCSV = (params: QueryGroupsFilters) =>
  downloadCSV(`/query/v2/groups/`, params);

export const downloadAllQueryJobsCSV = (params: QueryJobsFilters) =>
  downloadCSV(`/query/jobs/`, params);

export const downloadAllQueryJobExecutionsCSV = (
  job_id: string,
  params: QueryJobExecutionsFilters
) => downloadCSV(`/query/jobs/executions/${job_id}`, params);

// Governance APIs
export const getGovernanceSummary = () =>
  api.get<GovernanceSummary>("/governance/summary", {}, 0, GovernanceSummarySchema);

export const getGovernanceFilters = (params: GovernanceFilterParams) =>
  api.get<GovernanceFilters>("/governance/filters", params, 0, GovernanceFiltersSchema);

export const getGovernanceUsers = (params: UsersTableFilterParams) =>
  api.get<GovernanceUsersResponse>("/governance/users", params, 0, GovernanceUsersResponseSchema);

export const getGovernanceRoles = (params: RolesTableFilterParams) =>
  api.get<GovernanceRolesResponse>("/governance/roles", params, 0, GovernanceRolesResponseSchema);

export const getGovernanceUserDetails = (user_name: string) =>
  api.get<GovernanceUserDetails>(
    `/governance/users/details/${user_name}`,
    {},
    0,
    GovernanceUserDetailsSchema
  );

export const getGovernanceRoleDetails = (role_name: string) =>
  api.get<GovernanceRoleDetails>(
    `/governance/roles/details/${role_name}`,
    {},
    0,
    GovernanceRoleDetailsSchema
  );

export const getGovernanceUsersCostGraph = (params: { period: number; user_name: string }) =>
  api.get<GovernanceCostGraph>("/governance/users/cost", params, 0, GovernanceCostGraphSchema);

export const getGovernanceRolesCostGraph = (params: { period: number; role_name: string }) =>
  api.get<GovernanceCostGraph>("/governance/roles/cost", params, 0, GovernanceCostGraphSchema);

export const downloadUsersCSV = (params: UsersTableFilterParams) =>
  downloadCSV(`/governance/users`, params);

export const downloadRolesCSV = (params: RolesTableFilterParams) =>
  downloadCSV(`/governance/roles`, params);

export const downloadExecutionHistoryCSV = (all_time: string) =>
  downloadCSV(`/events/execution_history/`, {
    all_time,
  });

export const downloadDetailedExecutionHistoryCSV = (all_time: string) =>
  downloadCSV(`/events/credit_details/`, {
    all_time,
  });

export const downloadDashboardsCSV = (params: z.infer<typeof DashboardDownloadFiltersSchema>) =>
  downloadCSV(`/business_intelligence/dashboards/download_csv`, params);

export const getUserGroups = ({
  search = "",
  page = 1,
  size = 50,
}: {
  search?: string;
  page?: number;
  size?: number;
}) =>
  api.get<z.infer<typeof GetAllUserGroupsResponseSchema>>(
    "/groups/",
    { search, page, size },
    0,
    GetAllUserGroupsResponseSchema
  );

export const getUserGroupDetails = (id: string) =>
  api.get<z.infer<typeof UserGroupWithMembersAndRulesZodSchema>>(
    `/groups/${id}`,
    {},
    0,
    UserGroupWithMembersAndRulesZodSchema
  );

export const uploadUserGroups = (data: FormData) =>
  api.post<UploadUserGroupResponse>(
    "/groups/group_rule_file",
    data,
    UploadUserGroupResponseSchema,
    { headers: { "Content-Type": "multipart/form-data" } }
  );

export const uploadUsersToGroups = (data: FormData) =>
  api.post<UploadUserResponse>("/groups/user_group_file", data, UploadUserResponseSchema, {
    headers: { "Content-Type": "multipart/form-data" },
  });

export const deleteRuleInUserGroup = (ruleId: string, groupId: string) => {
  return api.delete<StandardResponse>(
    `/groups/${groupId}/rules/${ruleId}`,
    {},
    StandardResponseSchema
  );
};

export const deleteUserInUserGroup = (userId: string, groupId: string) => {
  return api.delete<StandardResponseWithWarning>(
    `/groups/${groupId}/users/${userId}`,
    {},
    StandardResponseWithWarningSchema
  );
};

export const deleteUserGroup = (groupId: string) => {
  return api.delete<StandardResponse>(`/groups/${groupId}`, {}, StandardResponseSchema);
};

export const getWarehouseSchedulerEvents = (params: GetWarehouseSchedulerEventsParams) =>
  api.get<ScheduleResponse[]>("/warehouse/schedules", { ...params }, 0, GetScheduleResponseSchema);

export const getWarehouseDynamicSchedulerEvents = (params: GetWarehouseSchedulerEventsParams) =>
  api.get<ScheduleResponse[]>(
    "/warehouse/schedules/dynamic",
    { ...params },
    0,
    GetScheduleResponseSchema
  );

export const createWarehouseSchedulerEvent = (data: ScheduleCreateRequest) =>
  api.post<ScheduleResponse>(`/warehouse/schedules`, data, ScheduleResponseSchema);

export const createWarehouseSchedulerEventForDynamicResizing = (data: ScheduleCreateRequest) =>
  api.post<ScheduleResponse>(`/warehouse/schedules/dynamic/create`, data, ScheduleResponseSchema);

export const editWarehouseSchedulerEvent = (scheduleId: string, data: ScheduleUpdateRequest) =>
  api.post<ScheduleResponse>(`/warehouse/schedules/${scheduleId}`, data, ScheduleResponseSchema);

export const deleteWarehouseSchedulerEvent = (scheduleId: string) =>
  api.delete<StandardResponse>(`/warehouse/schedules/${scheduleId}`, {}, StandardResponseSchema);

export const approveScheduleEvent = (scheduleId: string, data: ScheduleApprovalRequest) =>
  api.post<ScheduleResponse>(
    `/warehouse/schedules/${scheduleId}/approve`,
    data,
    ScheduleResponseSchema
  );

export const rejectScheduleEvent = (scheduleId: string, data: ScheduleRejectionRequest) =>
  api.post<ScheduleResponse>(
    `/warehouse/schedules/${scheduleId}/reject`,
    data,
    ScheduleResponseSchema
  );

export const getScheduledEventUpdates = (data: GetScheduledEventUpdatesRequest) =>
  api.get<GetScheduledEventUpdatesResponse[]>(
    `/warehouse/schedules/${data.schedule_id}/event_history`,
    data,
    0,
    z.array(GetScheduledEventUpdatesResponseSchema)
  );

export const getSnowflakeJobs = (params: GetSnowflakeJobsParams) =>
  api.get<Page<GetSnowflakeJobItem>>(
    "/query/v2/snowflake_jobs",
    flattenCustomTagFilters(params),
    0,
    pageSchema(getSnowflakeJobItemSchema)
  );

export const getSnowflakeJobRuns = (id: string, params: GetSnowflakeJobRunsParams) =>
  api.get<Page<GetSnowflakeJobRunItem>>(
    `/query/v2/snowflake_jobs/runs/${id}`,
    params,
    0,
    pageSchema(getSnowflakeJobRunItemSchema)
  );

export const getSnowflakeJobRunsGraphData = (
  id: string,
  params: GetSnowflakeJobRunsGraphDataParams
) =>
  api.get<GetSnowflakeJobRunsGraphData[]>(
    `/query/v2/snowflake_jobs/runs_graph/${id}`,
    params,
    0,
    z.array(getSnowflakeJobRunsGraphDataSchema)
  );

export const getDbtJobRunsList = (id: string, params: GetDbtJobRunsListParams) =>
  api.get<Page<DbtJobRunItem>>(
    `/query/v2/snowflake_jobs/run_list/${id}`,
    params,
    0,
    pageSchema(dbtJobRunItem)
  );

export const getDbtJobRunsListGraphData = (id: string, params: GetDbtJobRunsListGraphDataParams) =>
  api.get<DbtJobRunItem[]>(
    `/query/v2/snowflake_jobs/run_list_graph/${id}`,
    params,
    0,
    z.array(dbtJobRunItem)
  );

export const getDbtModelRunSummary = (workflowId: string, params: GetDbtModelRunSummaryParams) =>
  api.get<DbtModelRunSummary>(
    `/query/v2/snowflake_jobs/tasks_summary/${workflowId}`,
    params,
    0,
    dbtModelRunSummary
  );

export const getDbtTasksSummary = (workflowId: string, params: GetDbtTasksSummaryParams) =>
  api.get<DbtTasksSummary>(
    `/query/v2/dbt-jobs/tasks/summary/${workflowId}`,
    params,
    0,
    dbtTasksSummarySchema
  );

export const getDbtModelsList = (id: string, params: GetDbtModelsListParams) =>
  api.get<Page<DbtModel>>(
    `/query/v2/snowflake_jobs/tasks/${id}`,
    params,
    0,
    pageSchema(dbtModelSchema)
  );

export const getDbtProjectAndIntegrationDetails = (id: string) =>
  api.get<DbtProjectAndIntegration>(
    `/query/v2/project_details/${id}`,
    {},
    0,
    dbtProjectAndIntegrationSchema
  );

export const getDbtHealthChecks = (params: {
  integration_id?: number;
  environment_id?: number;
  name?: string;
  start_date?: string;
  end_date?: string;
  limit?: number;
}) =>
  api.get<DbtHealthCheckResponse>(`/dbt/health-checks`, params, 0, dbtHealthCheckResponseSchema);

export const getSnowflakeJobRunTasks = (
  workflow_id: string,
  params: GetSnowflakeJobRunTasksParams
) =>
  api.get<Page<GetSnowflakeJobTaskItem>>(
    `/query/v2/snowflake_jobs/tasks_by_run/${workflow_id}`,
    params,
    0,
    pageSchema(getSnowflakeJobTaskItemSchema)
  );

export const getSnowflakeJobGraphData = (params: GetSnowflakeJobGraphDataRequestParams) =>
  api.get<GetSnowflakeJobGraphData[]>(
    `/query/v2/snowflake_jobs/graph_data`,
    flattenCustomTagFilters(params),
    0,
    z.array(getSnowflakeJobGraphDataSchema)
  );

export const getSnowflakeJobGraphSummary = (params: GetSnowflakeJobGraphDataRequestParams) =>
  api.get<CommonGraphSummaryResponse>(
    `/query/v2/snowflake_jobs/graph_summary`,
    flattenCustomTagFilters(params),
    0,
    CommonGraphSummarySchema
  );

export const getSnowflakeJobsSummary = (
  id: string,
  params: { start_date: string; end_date?: string; workflow_type?: string }
) =>
  api.get<GetSnowflakeJobSummary>(
    `/query/v2/snowflake_jobs/summary/${id}`,
    params,
    0,
    getSnowflakeJobSummarySchema
  );

export const getDbtJobListing = (id: string, params: GetDbtJobListingParams) =>
  api.get<Page<DbtJobListingItem>>(
    `/query/v2/snowflake_jobs/dbt_jobs/${id}`,
    params,
    0,
    pageSchema(dbtJobListingItemSchema)
  );

export const getDbtJobListingSummary = (id: string, params: GetDbtJobListingSummaryParams) =>
  api.get<DbtJobListingSummary>(
    `/query/v2/snowflake_jobs/dbt_jobs_summary/${id}`,
    params,
    0,
    dbtJobListingSummarySchema
  );

export const getDbtJobFilters = (
  id: string,
  params: { page: number; size: number; prefix?: string }
) =>
  api.get<Page<DbtJobFilterItem>>(
    `/query/v2/snowflake_jobs/dbt_filters/jobs/${id}`,
    params,
    0,
    pageSchema(dbtJobFilterItemSchema)
  );

export const getDbtTagFilters = (
  id: string,
  params: { page: number; size: number; prefix?: string }
) =>
  api.get<Page<DbtTagFilterItem>>(
    `/query/v2/snowflake_jobs/dbt_filters/tags/${id}`,
    params,
    0,
    pageSchema(dbtTagFilterItemSchema)
  );

export const getSnowflakeJobSettings = () =>
  api.get<SnowflakeJobSettings>(
    "/query/v2/snowflake_jobs/settings",
    {},
    0,
    snowflakeJobSettingsSchema
  );

export const postSnowflakeJobSettings = (data: SnowflakeJobSettings) =>
  api.post<SnowflakeJobSettings>(
    "/query/v2/snowflake_jobs/settings",
    data,
    snowflakeJobSettingsSchema
  );

export const getWorkloadAttributes = () =>
  api.get<WorkloadAttributes>("/workload/attributes", {}, 0, workloadAttributesSchema);

export const getWorkloadAttributeValues = (params: {
  attribute_key: string;
  start_date: string;
  end_date: string;
  workflow_type?: string;
  page?: number;
  size?: number;
  search?: string;
}) => {
  const { attribute_key, ...queryParams } = params;
  return api.get<WorkloadAttributeValues>(
    `/workload/attributes/${encodeURIComponent(attribute_key)}/values`,
    queryParams,
    0,
    workloadAttributeValuesSchema
  );
};

export const getSnowflakeJobFilters = (params: GetSnowflakeJobFiltersParams) =>
  api.get<SnowflakeJobFilters>(
    "/query/v2/snowflake_jobs/filters",
    params,
    0,
    snowflakeJobFiltersSchema
  );

// Per-facet paginated filter endpoints. The bulk endpoint above stays for the
// bounded facets; these handle the unbounded ones (workflow_ids hit 3.5M
// observed, CUSTOM apps hit 17k observed). Backend rejects the wrong
// workflow_type combination with a 400.
//
// - /workflow_ids: CUSTOM, DBT (TABLEAU → 400)
// - /workflow_ids_with_labels: TABLEAU only (others → 400)
// - /teams, /apps: CUSTOM only (TABLEAU/DBT read these from bulk)
// - /custom_tags/{tag_key}: tag_key must appear in bulk.custom_tag_keys

export const getSnowflakeJobsFilterWorkflowIds = (params: SnowflakeJobsFilterPaginatedParams) =>
  api.get<SnowflakeJobsFilterStringPage>(
    "/query/v2/snowflake_jobs/filters/workflow_ids",
    params,
    0,
    snowflakeJobsFilterStringPageSchema
  );

export const getSnowflakeJobsFilterWorkflowIdsWithLabels = (
  params: SnowflakeJobsFilterPaginatedParams
) =>
  api.get<SnowflakeJobsFilterLabelValuePage>(
    "/query/v2/snowflake_jobs/filters/workflow_ids_with_labels",
    params,
    0,
    snowflakeJobsFilterLabelValuePageSchema
  );

export const getSnowflakeJobsFilterTeams = (params: SnowflakeJobsFilterPaginatedParams) =>
  api.get<SnowflakeJobsFilterStringPage>(
    "/query/v2/snowflake_jobs/filters/teams",
    params,
    0,
    snowflakeJobsFilterStringPageSchema
  );

export const getSnowflakeJobsFilterApps = (params: SnowflakeJobsFilterPaginatedParams) =>
  api.get<SnowflakeJobsFilterStringPage>(
    "/query/v2/snowflake_jobs/filters/apps",
    params,
    0,
    snowflakeJobsFilterStringPageSchema
  );

export const getSnowflakeJobsFilterCustomTagValues = (
  tagKey: string,
  params: SnowflakeJobsFilterPaginatedParams
) =>
  api.get<SnowflakeJobsFilterStringPage>(
    `/query/v2/snowflake_jobs/filters/custom_tags/${encodeURIComponent(tagKey)}`,
    params,
    0,
    snowflakeJobsFilterStringPageSchema
  );

export const compareSnowflakeJobRuns = (data: {
  workflow_id: string;
  run1_id: string;
  run2_id: string;
}) => api.post<RunCompareDiff[]>("/query/v2/snowflake_jobs/compare", data, runCompareDiffSchema);

export const getSnowflakeJobDefinition = (id: string, params: GetSnowflakeJobDefinitionParams) =>
  api.get<SnowflakeJobDefinitionResponse>(
    `/query/v2/snowflake_jobs/definition/${id}`,
    params,
    0,
    snowflakeJobDefinitionResponseSchema
  );

export const createAccessRule = (
  groupId: string,
  data: {
    rules: { entity_type: string; entity: string; type: string }[];
  }
) => {
  return api.post<StandardResponse>(
    `/groups/${groupId}/rules`,
    { rules: data.rules },
    StandardResponseSchema
  );
};

export const previewAccessRuleCoverage = (data: {
  rules: { entity_type: string; entity: string; type: string }[];
}) => {
  return api.post<AccessRuleCoveragePreviewResponse>(
    "/groups/access-rules/entity-assignment-coverage",
    data,
    AccessRuleCoveragePreviewResponseSchema
  );
};

export const createOwnership = (groupId: string, data: OwnershipRequestPayload) => {
  return api.post<StandardResponse>(`/groups/${groupId}/ownership`, data, StandardResponseSchema);
};

export const previewOwnershipCostAttribution = (
  data: OwnershipRequestsData,
  params: {
    start_date: string;
    end_date: string;
  }
) => {
  return api.postWithParams<OwnershipCostPreviewResponse>(
    "/groups/ownership-rules/cost-preview",
    params,
    data,
    OwnershipCostPreviewResponseSchema
  );
};

export const getOwnershipConflicts = (
  data: OwnershipRequestsData,
  params: {
    start_date: string;
    end_date: string;
  }
) => {
  return api.postWithParams<OwnershipConflictsResponse>(
    "/groups/ownership-rules/conflicts",
    params,
    data,
    OwnershipConflictsResponseSchema
  );
};

export const getConflictingTeams = (
  data: OwnershipRequestsData,
  params: {
    start_date: string;
    end_date: string;
  }
) => {
  return api.postWithParams<ConflictingTeamsResponse>(
    "/groups/ownership-rules/conflicting-team",
    params,
    data,
    ConflictingTeamsResponseSchema
  );
};

export const downloadConflictingTeamsCSV = (
  data: OwnershipRequestsData,
  params: {
    start_date: string;
    end_date: string;
  }
) => {
  return downloadCSVPost("/groups/ownership-rules/conflicting-team/download", data, params);
};

export const deleteOwnershipInUserGroup = (ownershipId: string, groupId: string) => {
  return api.delete<StandardResponse>(
    `/groups/${groupId}/ownership/${ownershipId}`,
    {},
    StandardResponseSchema
  );
};

export const getTeamConflicts = (teamId: string) => {
  return api.get<TeamConflictsResponse>(
    `/groups/ownership-rules/conflicts/${teamId}`,
    {},
    0,
    TeamConflictsResponseSchema
  );
};

export const getTeamConflictDetails = (
  teamId: string,
  params?: {
    page?: number;
    size?: number;
    sort_key?: string;
    sort_order?: string;
    filter_team_name?: string;
    filter_warehouse_name?: string;
    filter_database?: string;
    filter_schema?: string;
  }
) => {
  return api.get<TeamConflictDetailsResponse>(
    `/groups/ownership-rules/conflicts/${teamId}/details`,
    params ?? {},
    0,
    TeamConflictDetailsResponseSchema
  );
};

export const downloadTeamConflictDetailsCSV = (
  teamId: string,
  params?: {
    sort_key?: string;
    sort_order?: string;
    filter_team_name?: string;
    filter_warehouse_name?: string;
    filter_database?: string;
    filter_schema?: string;
  }
) => {
  return downloadCSV(`/groups/ownership-rules/conflicts/${teamId}/details/download`, params ?? {});
};

export const createTeam = (data: { name: string; description?: string }) => {
  return api.post<StandardResponse>("/groups/group_create", data, StandardResponseSchema);
};

export const addMemberToGroup = (groupId: string, data: { email: string }) => {
  return api.post<StandardResponse>(`/groups/${groupId}/members`, data, StandardResponseSchema);
};

// Update the approveRecommendations API function to use the renamed schema
export const approveRecommendations = (
  data: z.infer<typeof ApplyRecommendedEventsRequestSchema>
) => {
  return api.post<z.infer<typeof ApproveScheduleResponseSchema>[]>(
    "/warehouse/schedules/approve-recommendations",
    data,
    ApproveScheduleResponseSchema.array()
  );
};
export const getSlackWebhook = () =>
  api.get<z.infer<typeof NotificationsResponseSchema>>(
    "/notification",
    {},
    0,
    NotificationsResponseSchema
  );

export const getTags = (params: GetTagsParams) =>
  api.get<z.infer<typeof TagsResponseSchema>>(`/custom/tags`, params, 0, TagsResponseSchema);

export const createTag = (data: { rk: string; description: string; display_name: string }) =>
  api.post<Tag>("/custom/tags", { ...data, tag_type: TagTypeSchema.Enum.custom }, TagSchema);

export const deleteTag = (id: string) =>
  api.delete<StandardResponse>(`/custom/tags/${id}`, {}, StandardResponseSchema);

export const updateTag = (
  data: { rk: string } & Omit<Tag, "usage" | "created_by" | "tag_type">
) => {
  return api.update<Tag>(`/custom/tags/${data.rk}`, data, TagSchema);
};

export const deleteDBTConfig = (configId: string) =>
  api.delete<StandardResponse>(`/dbtconfig/${configId}`, {}, StandardResponseSchema);

export const getAllDBTConfigs = (params: {
  page: number;
  size: number;
  sort_key: string;
  sort_order: string;
  name?: string[];
  created_by?: (string | number)[];
}) => api.get<DBTConfigListResponse>("/dbtconfig/", params, 0, DBTConfigListResponseSchema);

export const getDBTConfigFilters = () =>
  api.get<DBTConfigFiltersResponse>(
    "/dbtconfig/filters/list",
    {},
    0,
    DBTConfigFiltersResponseSchema
  );

export const getDBTConfigById = (id: string | number) =>
  api.get<DBTConfigDetail>(`/dbtconfig/${id}`, {}, 0, DBTConfigDetailSchema);

export const addDBTConfig = (data: DBTConfigRequest) =>
  api.post<DBTConfigMutationResponse>("/dbtconfig/", data, DBTConfigMutationResponseSchema);

export const updateDBTConfig = (id: string | number, data: DBTConfigRequest) =>
  api.update<DBTConfigMutationResponse>(`/dbtconfig/${id}`, data, DBTConfigMutationResponseSchema);

export const getDBTConfigChecks = (params: { check_type?: string[]; files_required?: string[] }) =>
  api.get<DBTConfigChecksResponse>(
    "/dbtconfig/checks/list",
    params,
    0,
    DBTConfigChecksResponseSchema
  );

export const getDBTConfigChecksFilters = () =>
  api.get<DBTConfigChecksFiltersResponse>(
    "/dbtconfig/checks/filters",
    {},
    0,
    DBTConfigChecksFiltersResponseSchema
  );

export const addTagToNode = (data: AddTagToNodeRequest) =>
  api.post<z.infer<typeof AssociateTagResponseSchema>>(
    `/custom/tags/associate`,
    data,
    AssociateTagResponseSchema
  );

export const removeTagFromNode = (tagId: string, entity: AddTagToNodeRequest["entity"]) =>
  api.delete<z.infer<typeof AssociateTagResponseSchema>>(
    `/custom/tags/associate/${tagId}`,
    { data: entity },
    AssociateTagResponseSchema
  );

export const fetchSnowflakeDatabases = (data: FetchSnowflakeDatabasesRequest) =>
  api.post<FetchSnowflakeDatabasesResponse>(
    "/data_stores/form/fetch_databases/",
    data,
    fetchSnowflakeDatabasesResponseSchema
  );
// Datastores API
export const getDatastores = () =>
  api.get<DatastoreResponseInferred>(`/data_stores/`, {}, 0, DatastoreResponseSchema);

export const addDatastore = (data: AddDatastoreRequest | AddDatabasesRequest) =>
  api.post<AddDatastoreResponse>("/data_stores/form/", data, AddDatastoreResponseSchema);

export const testDatastoreConnectionDuringCreation = (
  data: Partial<TestDatastoreConnectionDuringCreationRequest>
) =>
  api.post<TestDatastoreConnectionDuringCreationResponse>(
    "/data_stores/form/test_connection/",
    data,
    TestDatastoreConnectionDuringCreationResponseSchema
  );

export const getDatastoreTypes = () =>
  api.get<DatastoreTypeResponse>("/data_stores/types/", {}, 0, DatastoreTypeResponseSchema);

export const testDatastoreConnection = (id: number) =>
  api.get<TestConnectionResponse>(
    `/data_stores/${id}/validate`,
    {},
    0,
    TestConnectionResponseSchema
  );

export const syncDatastore = (datastoreId: number) =>
  api.post<SyncDatastoreResponse>(`/sync/${datastoreId}`, {}, SyncDatastoreResponseSchema);

export const toggleDatastoreSyncSchedule = (data_store_id: number) =>
  api.post<ToggleSyncScheduleResponse>(
    `/scheduler/sync/${data_store_id}`,
    {},
    ToggleSyncScheduleResponseSchema
  );

export const saveAzureDatabricksConnection = (data: AzureDatabricksConnectionRequest) =>
  api.post<AzureDatabricksSavedConnectionResponse>(
    "/azure-databricks-connections/",
    data,
    AzureDatabricksSavedConnectionResponseSchema
  );

export const saveAzureDatabricksPrivateLinkConnection = (
  data: AzureDatabricksPrivateLinkConnectionRequest
) =>
  api.post<AzureDatabricksSavedConnectionResponse>(
    "/azure-databricks-connections/private-link/",
    data,
    AzureDatabricksSavedConnectionResponseSchema
  );

// Alert APIs
export const getAlertEvents = (data: GetAlertEventsParams) =>
  api.get<AlertEventsResponse>("/events/alerts/", data, 0, AlertEventsResponseSchema);

export const getAlertRules = (data: GetAlertRulesParams) =>
  api.get<AlertRulesResponse>("/alerts/rules/", data, 0, AlertRulesResponseSchema);

export const getAllSlackChannels = () =>
  api.get<SlackChannelsResponse>(
    "/notification/slack_channels",
    {},
    0,
    SlackChannelsResponseSchema
  );

export const getAlert = (alertId: string) =>
  api.get<Alert>(`/alerts/rules/${alertId}`, {}, 0, AlertSchema);

export const deleteAlert = (alertId: string) =>
  api.delete<StandardResponse>(`/alerts/alerts/${alertId}`, {}, StandardResponseSchema);

export const getSnowflakeStage = (data: { table_rk: string }) =>
  api.get<SnowflakeStageMetadata>(
    `/lineage/snowflake_stage`,
    data,
    0,
    snowflakeStageMetadataSchema
  );

export const getFinalLineageColumns = (params: {
  column_rk: string;
  upstream: boolean;
  downstream: boolean;
  direct: boolean;
  indirect: boolean;
}) => downloadCSV(`lineage/export_final_columns`, params);

export const getFinalLineageTables = (params: {
  table_rk: string;
  upstream: boolean;
  downstream: boolean;
}) => downloadCSV(`lineage/export_final_tables`, params);

export const getTableTagFilters = () =>
  api.get<FilterResponse[]>(`/tables/tabledata/filters/tags`, {}, 10000, FilterZodSchema.array());

export const getTableOwnerFilters = (params: { prefix?: string; page?: number; size?: number }) =>
  api
    .get<GetOpportunitiesDynamicFilterResponse>(
      `/tables/tabledata/filters/owners`,
      params,
      10000,
      GetOpportunitiesDynamicFilterResponseSchema
    )
    .then(response => {
      // Ensure each owner has a properly formatted label
      if (response && response.items) {
        response.items = response.items.map(item => ({
          ...item,
          label: item.label || item.value,
        }));
      }
      return response;
    });

// New specific table filter API functions
export const getTableNameFilters = (params: { prefix?: string; page?: number; size?: number }) =>
  api
    .get<TableRkFilterResponse>(
      `/tables/tabledata/filters/rks/tablename`,
      params,
      10000,
      TableRkFilterResponseSchema
    )
    .then(response => {
      // Format the response to extract just the table name from the path
      if (response && response.items) {
        response.items = response.items.map(item => {
          const path = item.value;
          // Extract table name from snowflake://{db}.{schema}/{table}
          const tableName = path.split("/").pop() || path;
          return {
            ...item,
            label: tableName,
            value: path,
          };
        });
      }
      return response;
    });

export const getSchemaFilters = (params: { prefix?: string; page?: number; size?: number }) =>
  api
    .get<TableRkFilterResponse>(
      `/tables/tabledata/filters/rks/schema`,
      params,
      10000,
      TableRkFilterResponseSchema
    )
    .then(response => {
      // Format the response to extract just the schema from the path
      if (response && response.items) {
        response.items = response.items.map(item => {
          const path = item.value;
          // Extract schema from snowflake://{db}.{schema}/{table}
          let schema = path;
          if (path.includes(".")) {
            const parts = path.split(".");
            if (parts.length > 1) {
              const schemaWithTable = parts[1];
              schema = schemaWithTable.split("/")[0];
            }
          }
          return {
            ...item,
            label: schema,
          };
        });
      }
      return response;
    });

export const getDatabaseFilters = (params: { prefix?: string; page?: number; size?: number }) =>
  api
    .get<TableRkFilterResponse>(
      `/tables/tabledata/filters/rks/db`,
      params,
      10000,
      TableRkFilterResponseSchema
    )
    .then(response => {
      // Format the response to extract just the database from the path
      if (response && response.items) {
        response.items = response.items.map(item => {
          const path = item.value;
          // Extract database from snowflake://{db}.{schema}/{table}
          let db = path;
          if (path.includes("://")) {
            const parts = path.split("://");
            if (parts.length > 1 && parts[1].includes(".")) {
              db = parts[1].split(".")[0];
            }
          }
          return {
            ...item,
            label: db,
          };
        });
      }
      return response;
    });

export const getTableQueries = (tableId: string, params: TableQueryFilters) => {
  return api.get<TableQueriesResponse>(
    `/tables/queries/${tableId}`,
    params,
    0,
    TableQueriesResponseSchema
  );
};

export const getTableQueriesFilters = (
  tableId: string,
  params: { startDate: string; endDate: string }
) => {
  return api.get<TableQueryFiltersResponse>(
    `/tables/queries/filters/${tableId}`,
    params,
    0,
    TableQueryFiltersResponseSchema
  );
};

export const createDatamate = (data: Teammate) =>
  api.post<TeammateCreateResponse>("/datamates", data, TeammateCreateResponseSchema);

export const getDatamates = () =>
  api.get<{ datamates: Teammate[] }>(
    "/datamates/",
    {},
    0,
    z.object({ datamates: TeammateSchema.array() })
  );

export const getDatamatesWithStats = (params?: { start_date: string; end_date: string }) => {
  return api.get<{ datamates: Teammate[]; usage: Usage }>(
    "/datamates/summary",
    params,
    0,
    z.object({ datamates: TeammateSchema.array(), usage: UsageSchema })
  );
};

export const getDatamateByIdWithStats = (
  id: string | undefined,
  params: {
    start_date: string;
    end_date: string;
  }
) => {
  if (!id) return;
  return api.get<GetDatamateWithStatsResponse>(
    `/datamates/${id}/summary`,
    params,
    0,
    GetDatamateWithStatsResponseSchema
  );
};

// edit datamate
export const editDatamate = (id: string, data: Teammate) =>
  api.update<TeammateUpdateResponse>(`/datamates/${id}`, data, TeammateUpdateResponseSchema);

// delete datamate
export const deleteDatamate = (id: string) =>
  api.delete<StandardResponse>(`/datamates/${id}`, {}, StandardResponseSchema);

// get datamate integrations
export const getDatamateIntegrations = () =>
  api.get<z.infer<typeof DatamateIntegrationSchema>[]>(
    `/datamate_integrations/`,
    {},
    0,
    z.array(DatamateIntegrationSchema)
  );

// crud /connections
export const createConnection = (data: Connection) =>
  api.post<Connection>("/connections", data, ConnectionSchema);

export const getConnections = () =>
  api.get<{ connections: Connection[] }>(
    "/connections/",
    {},
    0,
    z.object({ connections: ConnectionSchema.array() })
  );

export const deleteConnection = (id: string) =>
  api.delete<StandardResponse>(`/connections/${id}`, {}, StandardResponseSchema);

export const updateConnection = (id: string, data: Connection) =>
  api.update<Connection>(`/connections/${id}`, data, ConnectionSchema);

export const testConnection = (data: Partial<Connection>) =>
  api.post<{ success: boolean; message: string }>(
    "/connections/test",
    data,
    z.object({ success: z.boolean(), message: z.string() })
  );

export const getCustomIntegrationsPaginated = (params: { page: number; size: number }) =>
  api.get<Page<CommonIntegrationInstance>>(
    `/datamate_integrations/custom`,
    params,
    0,
    pageSchema(CommonIntegrationInstanceSchema)
  );

export const getAssistAggregations = (
  aggregateBy: string,
  startDate: Date,
  endDate: Date,
  datamateId: string | undefined
) => {
  if (!datamateId) return [];
  return api.get<AssistAggregationsResponse>(
    `/datamates/audit/${datamateId}/aggregations`,
    {
      aggregation_type: "assist",
      aggregateBy,
      start_date: startDate.toISOString(),
      end_date: endDate.toISOString(),
    },
    0,
    AssistAggregationsResponseSchema
  );
};

export const getGuardrailAggregations = (
  aggregateBy: string,
  startDate: Date,
  endDate: Date,
  datamateId: string | undefined
) => {
  if (!datamateId) {
    return [];
  }
  return api.get<GuardrailAggregationsResponse>(
    `/datamates/audit/${datamateId}/aggregations`,
    {
      aggregation_type: "guard_rail",
      aggregateBy,
      start_date: startDate.toISOString(),
      end_date: endDate.toISOString(),
    },
    0,
    GuardrailAggregationsResponseSchema
  );
};

export const createNewCustomIntegration = (instance: Partial<CommonIntegrationInstance>) =>
  api.post<CommonIntegrationInstance>(
    `/datamate_integrations/custom`,
    instance,
    CommonIntegrationInstanceSchema
  );

// TODO: figure out why PUT doesn't work
export const updateCustomIntegration = (id: string, instance: Partial<CommonIntegrationInstance>) =>
  api.post<CommonIntegrationInstance>(
    `/datamate_integrations/custom/${id}`,
    instance,
    CommonIntegrationInstanceSchema
  );

export const deleteCustomIntegration = (id: string) =>
  api.delete<StandardResponse>(`/datamate_integrations/custom/${id}`, {}, StandardResponseSchema);

export const createJiraTicket = (
  opportunityId: string,
  queryId: string | null = null,
  createJiraTicket?: boolean,
  action?: string
) => {
  return api.post<{
    url: string;
    issue_key: string;
    content?: string;
  }>(
    `/opportunities/jira`,
    {
      opportunity_id: opportunityId,
      query_id: queryId,
      create_jira_ticket: createJiraTicket,
      action,
    },
    z.object({
      url: z.string().url(),
      issue_key: z.string(),
      content: z.string().optional(),
    })
  );
};

// Knowledge Catalog
export const createKnowledgeSource = (formData: Partial<KnowledgeCatalogItem>) =>
  api.post<KnowledgeCatalogItem>("/knowledge_bases", formData, KnowledgeCatalogItemSchema);

export const getKnowledgeBaseTemplates = () =>
  api.get<KnowledgeCatalogItem[]>(
    "/knowledge_bases/templates",
    {},
    0,
    KnowledgeCatalogItemSchema.array()
  );

export const getKnowledgeBaseSources = () =>
  api.get<KnowledgeCatalogItem[]>("/knowledge_bases/", {}, 0, KnowledgeCatalogItemSchema.array());

export const getKnowledgeSourceById = (id: string) =>
  api.get<KnowledgeCatalogItem>(`/knowledge_bases/${id}`, {}, 0, KnowledgeCatalogItemSchema);

export const forkKnowledgeSource = (template_name: string, new_name: string) =>
  api.post<KnowledgeCatalogItem>(
    `/knowledge_bases/fork-template`,
    { template_name: template_name, new_name: new_name },
    KnowledgeCatalogItemSchema
  );

/**
 * Fork a knowledge base template with V2 processing (S3 upload + Celery task)
 * Used when enableKnowledgeEngine feature flag is enabled
 * @param template_name - Name of the template to fork
 * @param new_name - Name for the new knowledge base
 * @returns Response with KB and document details, processing status
 */
export const forkKnowledgeSourceV2 = (template_name: string, new_name: string) =>
  api.post<KnowledgeDocumentUploadResponse>(
    `/knowledge_bases/v2/fork-template`,
    { template_name: template_name, new_name: new_name },
    KnowledgeDocumentUploadResponseSchema
  );

export const deleteKnowledgeSource = (id: number) =>
  api.delete<StandardResponse>(`/knowledge_bases/${id}`, {}, StandardResponseSchema);

export const getDbtModelsFilters = () =>
  api.get<DbtModelFiltersResponse>(`/dbt/models/filters`, {}, 0, DbtModelFiltersResponseSchema, {
    // setting group id as undefined explicitly to get all available integrations to show in add access rule dialog
    headers: { "x-user-group-id": undefined },
  });

export const getDbtModels = (params: GetDbtModelsParams) =>
  api.get<DbtModelsResponse>(`/dbt/models/`, params, 0, DbtModelsResponseSchema);

export const getDbtModelByRk = (data: DbtModelByRkRequest) =>
  api.post<DbtModelByRkResponse>(`/dbt/models/items`, data, DbtModelByRkResponseSchema);

// DBT Model Schema and Lineage APIs
export const postColumnsDBT = (data: PostColumnsDBTParams) =>
  api.get<TableSchemaResponse>(`/dbt/models/schema`, data, 0, TableSchemaResponseSchema);

export const postConnectedColumnsDBT = (data: PostConnectedColumnsDBTParams) =>
  api.post<ColumnLineageResponse>(`/dbt/models/connected`, data, ColumnLineageResponseSchema);

export const upstreamTablesDBT = (fqn: string) =>
  api.post<ConnectedTablesResponse>(`/dbt/models/upstream`, { fqn }, ConnectedTablesResponseSchema);

export const downstreamTablesDBT = (fqn: string) =>
  api.post<ConnectedTablesResponse>(
    `/dbt/models/downstream`,
    { fqn },
    ConnectedTablesResponseSchema
  );

export const startingNodeDBT = (params: StartingNodeDBTParams) =>
  api.get<TableNodeResponse>(`/dbt/models/terminal`, params, 0, TableNodeResponseSchema);

export const getSQLLineage = (params: GetSQLLineageParams) =>
  api.post<SQLLineageResponse>(`/dbt/models/sql_lineage`, params, SQLLineageResponseSchema);

// Integration APIs
export const getIntegrations = () =>
  api.get<IntegrationListResponse>(
    `/dbt/v1/project_integrations`,
    {},
    0,
    IntegrationListResponseSchema
  );

export const getIntegrationByID = (integrationId: string | number, integrationEnv: string) =>
  api.get<IntegrationDetails>(
    `/dbt/v1/project_integrations/${integrationId}/${integrationEnv}`,
    {},
    0,
    IntegrationDetailsSchema
  );

export const postIntegration = (data: PostIntegrationRequest) =>
  api.post<PostIntegrationResponse>(
    `/dbt/v1/project_integration`,
    data,
    PostIntegrationResponseSchema
  );

export const deleteDBTIntegration = (id: string | number) =>
  api.delete<StandardResponse>(`/dbt/v1/project_integration/${id}`, {}, StandardResponseSchema);

export const getKnowledgeSourceByPublicId = (id: string) =>
  api.get<KnowledgeCatalogItem>(`/knowledge_bases/public/${id}`, {}, 0, KnowledgeCatalogItemSchema);

export const updateKnowledgeSource = (id: string, formData: Partial<KnowledgeCatalogItem>) =>
  api.update<KnowledgeCatalogItem>(`/knowledge_bases/${id}`, formData, KnowledgeCatalogItemSchema);

const KnowledgeBasePrivateLinkSettingSchema = z.object({
  config: z.object({
    enabled: z.boolean(),
    port: z.number().optional(),
  }),
});

export const getKnowledgeBasePrivateLinkSetting = () =>
  api.get<z.infer<typeof KnowledgeBasePrivateLinkSettingSchema>>(
    "/knowledge_bases/private-link-setting",
    {},
    0,
    KnowledgeBasePrivateLinkSettingSchema
  );

// Knowledge Base V2 - Document Upload & Versioning APIs

/**
 * Upload a document and create a new knowledge base (V2)
 * Supports both file upload and markdown content
 * @param formData - FormData containing name, description, privacy, chunking_strategy, icon, and either file or markdown_content
 * @returns Response with KB and document details, processing status
 */
export const uploadKnowledgeBaseWithDocument = (formData: FormData) =>
  api.post<KnowledgeDocumentUploadResponse>(
    "/knowledge_bases/v2/upload",
    formData,
    KnowledgeDocumentUploadResponseSchema,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );

/**
 * Upload a new document version to an existing knowledge base (V2)
 * Supports both file upload and markdown content
 * @param kbId - Knowledge base ID
 * @param formData - FormData containing chunking_strategy and either file or markdown_content
 * @returns Response with document details and processing status
 */
export const uploadDocumentToKnowledgeBase = (kbId: number, formData: FormData) =>
  api.post<KnowledgeDocumentUploadToExistingResponse>(
    `/knowledge_bases/v2/${kbId}/upload`,
    formData,
    KnowledgeDocumentUploadToExistingResponseSchema,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );

/**
 * Get knowledge base details with current document processing status (V2)
 * Returns KB info along with active document metadata and user-friendly status messages
 * @param kbId - Knowledge base ID
 * @returns Detailed KB response with document status
 */
export const getKnowledgeBaseWithDocumentStatus = (kbId: number) =>
  api.get<KnowledgeBaseDetailResponse>(
    `/knowledge_bases/v2/${kbId}`,
    {},
    0,
    KnowledgeBaseDetailResponseSchema
  );

/**
 * Get all document versions/history for a knowledge base (V2)
 * Returns list of all documents ordered by version descending (newest first)
 * @param kbId - Knowledge base ID
 * @returns Array of document history items
 */
export const getKnowledgeBaseDocumentHistory = (kbId: number) =>
  api.get<KnowledgeDocumentHistoryItem[]>(
    `/knowledge_bases/v2/${kbId}/documents`,
    {},
    0,
    z.array(KnowledgeDocumentHistoryItemSchema)
  );

/**
 * Get document chunks for a specific knowledge base document (V2)
 * Returns all chunks for the specified document in order
 * @param kbId - Knowledge base ID
 * @param docId - Document ID
 * @returns Document chunks response with metadata and chunks array
 */
export const getKnowledgeBaseDocumentChunks = (kbId: number, docId: number) =>
  api.get<DocumentChunksResponse>(
    `/knowledge_bases/v2/${kbId}/documents/${docId}/chunks`,
    {},
    0,
    DocumentChunksResponseSchema
  );

/**
 * Retry processing for a failed document (V2)
 * Resets the document status and queues it for re-processing
 * @param kbId - Knowledge base ID
 * @param docId - Document ID
 * @returns Response with document details and new processing status
 */
export const retryKnowledgeDocumentProcessing = (kbId: number, docId: number) =>
  api.post<KnowledgeDocumentUploadResponse>(
    `/knowledge_bases/v2/${kbId}/documents/${docId}/retry`,
    {},
    KnowledgeDocumentUploadResponseSchema
  );

/**
 * Submit feedback for a knowledge base chunk/learning
 * @param kbId - Knowledge base ID
 * @param chunkId - Chunk ID
 * @param feedback - 'helpful' or 'not_helpful'
 * @param comment - Optional comment
 */
export const submitKnowledgeFeedback = (
  kbId: number,
  chunkId: number,
  feedback: "helpful" | "not_helpful",
  comment?: string
) =>
  api.post<StandardResponse>(
    `/knowledge_bases/v2/${kbId}/feedback`,
    { chunk_id: chunkId, feedback, comment },
    StandardResponseSchema
  );

// Global config query tags schema
const GlobalConfigQueryTagsResponseSchema = z.array(z.string());

export const getGlobalConfigQueryTags = () =>
  api.get<z.infer<typeof GlobalConfigQueryTagsResponseSchema>>(
    "/global_config/",
    {},
    0,
    GlobalConfigQueryTagsResponseSchema
  );

export const getBiReportFilters = () =>
  api.get<BiReportFilters>("business_intelligence/filters", {}, 0, BiReportFiltersSchema);

// BI Dashboards (Reports) API Functions
export const getBiDashboardsPaginated = (params?: BiDashboardsQueryParams) =>
  api.get<BiReportsPaginatedResponse>(
    "business_intelligence/dashboards",
    params || {},
    0,
    BiReportsPaginatedResponseSchema
  );

export const getBiDashboardsSummary = (params?: {
  start_date?: string;
  end_date?: string;
  site_name?: string[];
  instance_name?: string[];
}) =>
  api.get<BiDashboardSummary>(
    "business_intelligence/summary",
    params || {},
    0,
    BiDashboardSummarySchema
  );

export const getBiDashboardById = (dashboardId: string) => {
  const decodedId = decodeURIComponent(dashboardId);
  return api.get<BiReport>(
    "business_intelligence/dashboards/get_by_id",
    { dashboard_id: decodedId },
    0,
    BiReportSchema
  );
};

// User Reports API Functions
export const getUserReports = (params?: GetUserReportsParams) =>
  api.get<{ items: BiUserReport[]; total: number; pages: number; page: number; size: number }>(
    "business_intelligence/users",
    params || {},
    0,
    z.object({
      items: z.array(BiUserReportSchema),
      total: z.number(),
      pages: z.number(),
      page: z.number(),
      size: z.number(),
    })
  );

export const getUserSummary = () =>
  api.get<BiUserSummary[]>(
    "business_intelligence/users/summary",
    {},
    0,
    z.array(BiUserSummarySchema)
  );

export const getUserReportsFilters = () =>
  api.get<BiUserReportsFilters>(
    "business_intelligence/users/filters",
    {},
    0,
    BiUserReportsFiltersSchema
  );

export const getUserByRk = (userRk: string) => {
  return api.get<BiUserReport>(
    "business_intelligence/users/get_by_rk",
    { user_rk: userRk },
    0,
    BiUserReportSchema
  );
};

// BI Reports Dashboard Activity API Functions
export const getDashboardAccessActivity = (
  dashboardId: string,
  params: { page?: number; page_size?: number; sort_by?: string; sort?: string } = {}
) =>
  api.get<DashboardAccessActivityResponse>(
    `/business_intelligence/dashboards/access_activity`,
    {
      dashboard_id: dashboardId,
      ...params,
    },
    0,
    DashboardAccessActivityResponseSchema
  );

export const getDashboardRefreshActivity = (
  dashboardId: string,
  params: { page?: number; page_size?: number; sort_by?: string; sort?: string } = {}
) =>
  api.get<DashboardRefreshActivityResponse>(
    `/business_intelligence/dashboards/refresh_activity`,
    {
      dashboard_id: dashboardId,
      ...params,
    },
    0,
    DashboardRefreshActivityResponseSchema
  );

export const getTableauProjects = (credentials: {
  url: string;
  site_name?: string;
  // PAT becomes optional when ``datastore_id`` is provided — backend then
  // falls back to credentials stored on that data store. Used by the
  // "Add database" modal on the data store detail page.
  pat_name?: string;
  pat_secret?: string;
  datastore_id?: number;
  // Sibling site name (in the same instance as ``datastore_id``) whose PAT
  // should be reused. Sent alongside ``datastore_id`` (page context) so the
  // backend resolves the sibling via instance_id + site_name.
  source_site_name?: string;
}) =>
  api.post<TableauProject[]>(
    `/business_intelligence/tableau/projects`,
    credentials,
    z.array(TableauProjectSchema)
  );

export const getTeamSpendGraphAndSummaryData = (params: TeamSpendQueryParams) => {
  return api.get<TeamSpendGraphAndSummary>(
    "/teams/cost/graph",
    params,
    0,
    teamSpendGraphAndSummary
  );
};

export const getTeamSpendTotalSpendsData = (params: TeamSpendQueryParams) => {
  return api.get<TeamSpendTotalSpends>(
    "/teams/cost/period-comparison",
    params,
    0,
    teamSpendTotalSpendsSchema
  );
};

export const getTeamSpendFilters = (params: TeamSpendQueryParams) => {
  return api.get<TeamSpendFilters>("/teams/filters", params, 0, teamSpendFilters);
};

// Memory Management APIs
export const listMemories = (
  params: { page?: number; page_size?: number; datamate_id?: string[] } = {}
) => api.get<Memory[]>("/datamates/memory/list", params, 0, z.array(MemorySchema));

export const searchMemories = (query: string, params: { datamate_id?: string[] } = {}) =>
  api.get<Memory[]>("/datamates/memory/search", { query, ...params }, 0, z.array(MemorySchema));

export const updateMemory = (
  id: string,
  data: { memory: string; metadata: Record<string, unknown> }
) => api.update<MemoryResponse>(`/datamates/memory/${id}`, data, MemoryResponseSchema);

export const deleteMemory = (id: string) =>
  api.delete<MemoryResponse>(`/datamates/memory/${id}`, {}, MemoryResponseSchema);

export const bulkDeleteMemories = (ids: string[]) =>
  api.delete<MemoryResponse>("/datamates/memory/batch", { data: ids }, MemoryResponseSchema);

export const getAnnualizedCost = (params: Record<string, string | string[] | number>) => {
  return api.get<AnnualizedCost>(
    `/tables/tabledata/costs-summary`,
    params,
    0,
    AnnualizedCostSchema
  );
};

export const getAnnualizedCostBreakdown = (params: Record<string, string | string[] | number>) => {
  return api.get<CostBreakdown>(
    `/tables/tabledata/costs-breakdown`,
    params,
    0,
    CostBreakdownSchema
  );
};

// Accounts Cost APIs
export const getAccountsList = () => {
  return api.get<AccountsList>("/account_costs/accounts", {}, 0, accountsListSchema);
};

export const getAccountsTotalCost = (params: AccountsCostQueryParams) => {
  return api.get<TotalCost>("/account_costs/total", params, 0, totalCostSchema);
};

export const getAccountsChartData = (params: AccountsCostQueryParams) => {
  return api.get<ChartDataList>("/account_costs/chart", params, 0, chartDataListSchema);
};

export const getAccountsTableData = (params: AccountsCostQueryParams) => {
  return api.get<TableDataList>("/account_costs/table", params, 0, tableDataListSchema);
};

// User Management APIs
export const getUserList = (params: {
  company: string;
  with_service_user?: boolean;
  allUsers?: boolean;
  page?: number;
  pageSize?: number;
  search_term?: string;
  include_deleted?: boolean;
  sortColumn?: string;
  sortOrder?: string;
}) =>
  api.get<UserListResponse>(
    "/users/",
    {
      company: params.company,
      with_service_user: params.with_service_user ?? false,
      all_users: params.allUsers ?? false,
      page: params.page ?? 0,
      size: params.pageSize ?? 20000,
      search_term: params.search_term ?? "",
      include_deleted: params.include_deleted ?? true,
      sortColumn: params.sortColumn ?? "created_at",
      sortOrder: params.sortOrder ?? "desc",
    },
    10000,
    UserListResponseSchema
  );

export const addNewUser = (params: { email: string; company: string; role: string }) =>
  api.post<InviteUserResponse>("/users/invite", params, InviteUserResponseSchema);

export const updateUser = (params: {
  _id: string;
  is_active?: boolean;
  is_verified?: boolean;
  attributes?: { deactivated?: boolean };
}) => api.update<UpdateUserResponse>(`/users/${params._id}`, params, UpdateUserResponseSchema);

export const updateUserRole = (params: { user_uuid: string; role_title: string }) =>
  api.post<UpdateUserRoleResponse>("/users/role_edit/", params, UpdateUserRoleResponseSchema);

export const resolveRoleConflict = (params: {
  user_uuid: string;
  action: "accept_sso" | "dismiss";
}) => api.post<StandardResponse>("/users/resolve_role_conflict", params, StandardResponseSchema);

export const deleteUser = (params: { uuid: string; company: string }) =>
  api.delete<DeleteUserResponse>(
    `/users/${params.uuid}?company=${params.company}`,
    {},
    DeleteUserResponseSchema
  );

// Improve Prompt API
export const getImprovedPrompt = (userPrompt: string, agentName: string) => {
  return api.post<EnhancedPromptResponse>(
    "/studio/prompt/enhance",
    {
      user_prompt: userPrompt,
      agent_name: agentName,
    },
    EnhancedPromptResponseSchema
  );
};

// Prompt Library APIs
export const getPrompts = (params: {
  type?: string[];
  teams?: string[];
  owners?: string[];
  tags?: string[];
  sort?: "created_at" | "last_used_at" | "updated_at";
  search?: string;
  page?: number;
  pageSize?: number;
}) => {
  return api.get<Page<PromptResponse>>("/prompts", params, 0, pageSchema(PromptResponseSchema));
};

export const getPromptById = (id: string) => {
  return api.get<PromptResponse>(`/prompts/${id}`, {}, 0, PromptResponseSchema);
};

export const createPrompt = (data: CreateOrUpdatePromptRequest) => {
  return api.post<PromptResponse, CreateOrUpdatePromptRequest>(
    "/prompts",
    data,
    PromptResponseSchema
  );
};

export const updatePrompt = (id: string, data: CreateOrUpdatePromptRequest) => {
  return api.post<PromptResponse, CreateOrUpdatePromptRequest>(
    `/prompts/${id}`,
    data,
    PromptResponseSchema
  );
};

export const deletePrompt = (id: string) => {
  return api.delete<StandardResponse>(`/prompts/${id}`, {}, StandardResponseSchema);
};

export const getPromptTags = () => {
  return api.get<PromptLibraryTagsResponse>("/prompts/tags", {}, 0, TagsResponseSchema);
};

export const getStarterQuestions = ({
  tenantMax,
  publicMax,
  integrationType,
  userView: user_view,
}: {
  tenantMax: number;
  publicMax: number;
  integrationType: string;
  userView?: UserView;
}) =>
  api.get<StarterQuestionsResponse>(
    "/prompts/starter",
    {
      tenant_max: tenantMax,
      public_max: publicMax,
      integration_type: integrationType,
      ...user_view,
    },
    0,
    StarterQuestionsResponseSchema
  );

// Playground Session APIs
export const getPlaygroundSessions = (params?: {
  search_text?: string;
  page?: number;
  page_size?: number;
  status?: SessionLifecycleListFilter;
}) => {
  return api.get<PlaygroundAllSessionResponse>(
    "/studio/sessions",
    params || {},
    0,
    playgroundAllSessionResponseSchema
  );
};

export const getPlaygroundSessionDetails = (sessionId: string) => {
  return api.get<PlaygroundSessionDetailsResponse>(
    `/studio/sessions/${sessionId}`,
    {},
    0,
    playgroundSessionDetailsResponseSchema
  );
};

export const updatePlaygroundSessionStatus = (
  sessionId: string,
  status: SessionLifecycleStatus
) => {
  return api.update<SessionStatusUpdateResponse>(
    `/studio/sessions/${sessionId}`,
    { status },
    sessionStatusUpdateResponseSchema
  );
};

// ========== Share Links (US-003) ==========
export const createShareLink = (data: ShareLinkCreateRequest) =>
  api.post<ShareLinkResponse>("/playground/share", data, shareLinkResponseSchema);

export const getShareLinks = (sessionId: string) =>
  api.get<ShareLinkListResponse>(
    `/playground/share/session/${sessionId}`,
    {},
    0,
    shareLinkListResponseSchema
  );

export const deleteShareLink = (shareId: string) =>
  api.delete<StandardResponse>(`/playground/share/${shareId}`, {}, StandardResponseSchema);

// Get shared session - auth is optional (backend handles both cases)
export const getSharedSession = (shareId: string) =>
  api.get<SharedSessionResponse>(
    `/playground/share/shared/${shareId}`,
    {},
    0,
    sharedSessionResponseSchema
  );

// Email a Studio session report to a mailing list. AI-6343: surfaced as
// the Email tab in `ShareDialog`. Backend route is the one used by the
// legacy chatbotV2 EmailDialog (`studio/session_to_email_mailing_list`).
export const sendSessionToEmail = (data: {
  session_id: string;
  recipient_type: "specific_emails" | "all_altimate_users" | "team";
  emails?: string[];
  team_ids?: number[];
}) =>
  api.post<StandardResponse>("/studio/session_to_email_mailing_list", data, StandardResponseSchema);

// ========== Scheduled Reports (US-005) ==========
export const createPlaygroundSchedule = (data: PlaygroundScheduleCreateRequest) =>
  api.post<PlaygroundScheduleResponse>(
    "/playground/schedules",
    data,
    playgroundScheduleResponseSchema
  );

export const getPlaygroundSchedules = (params?: { session_id?: string }) =>
  api.get<PlaygroundScheduleListResponse>(
    "/playground/schedules",
    params || {},
    0,
    playgroundScheduleListResponseSchema
  );

export const getPlaygroundSchedule = (scheduleId: string) =>
  api.get<PlaygroundScheduleResponse>(
    `/playground/schedules/${scheduleId}`,
    {},
    0,
    playgroundScheduleResponseSchema
  );

export const updatePlaygroundSchedule = (
  scheduleId: string,
  data: Partial<PlaygroundScheduleUpdateRequest>
) =>
  api.put<PlaygroundScheduleResponse, Partial<PlaygroundScheduleUpdateRequest>>(
    `/playground/schedules/${scheduleId}`,
    data,
    playgroundScheduleResponseSchema
  );

export const deletePlaygroundSchedule = (scheduleId: string) =>
  api.delete<StandardResponse>(`/playground/schedules/${scheduleId}`, {}, StandardResponseSchema);

export const togglePlaygroundSchedule = (scheduleId: string) =>
  api.post<PlaygroundScheduleResponse>(
    `/playground/schedules/${scheduleId}/toggle`,
    {},
    playgroundScheduleResponseSchema
  );

export const sendNowPlaygroundSchedule = (scheduleId: string) =>
  api.post<StandardResponse>(
    `/playground/schedules/${scheduleId}/send-now`,
    {},
    StandardResponseSchema
  );

export const getPlaygroundScheduleRuns = (scheduleId: string, params?: { limit?: number }) =>
  api.get<PlaygroundScheduleRunsListResponse>(
    `/playground/schedules/${scheduleId}/runs`,
    params || {},
    0,
    playgroundScheduleRunsListResponseSchema
  );

// ========== Slack Webhook Sharing (US-004) ==========

export const shareToSlack = (data: ShareToSlackRequest) =>
  api.post<ShareToSlackResponse>("/playground/share/slack", data, shareToSlackResponseSchema);

// Cost Analysis Subscription APIs
export const createCostAnalysisSubscription = (data: CostAnalysisSubscribeRequest) =>
  api.post<CostAnalysisSubscriptionResponse>(
    "/summary/v2/cost-analysis/subscribe",
    data,
    costAnalysisSubscriptionResponseSchema
  );

export const deleteCostAnalysisSubscription = (data: CostAnalysisUnsubscribeRequest) =>
  api.post<CostAnalysisUnsubscribeResponse>(
    "/summary/v2/cost-analysis/unsubscribe",
    data,
    costAnalysisUnsubscribeResponseSchema
  );

// Get all subscriptions for the user
export const getCostAnalysisSubscriptions = (params: { instance_id?: string }) =>
  api.get<CostAnalysisSubscriptionsListResponse>(
    "/summary/v2/cost-analysis/subscriptions",
    params,
    0,
    costAnalysisSubscriptionsListResponseSchema
  );

// Delete a specific subscription by ID
export const deleteCostAnalysisSubscriptionById = (subscriptionId: string) =>
  api.delete<CostAnalysisUnsubscribeResponse>(
    `/summary/v2/cost-analysis/subscriptions/${subscriptionId}`,
    {},
    costAnalysisUnsubscribeResponseSchema
  );

// SSO Configuration API
export const configureSSO = (
  data: {
    client_id: string;
    client_secret: string;
    oidc_url: string;
  },
  provider: (typeof SSOProvider)[keyof typeof SSOProvider]
) => api.post<StandardResponse>(`/supertokens/configure/${provider}`, data, StandardResponseSchema);

export const getCompanyInfo = () =>
  api.get<CompanyInfoResponse>("/auth/company-info", {}, 0, CompanyInfoResponseSchema);

// Streamlit, notebook workload APIs
export const getStreamlitNotebookJobs = (params: Record<string, string | number | undefined>) => {
  return api.get<Page<StreamlitNotebookJobListResponse>>(
    "/query/v2/workloads",
    params,
    0,
    pageSchema(CommonJobListSchema)
  );
};

export const getStreamlitNotebookJobsGraphData = (
  params: Record<string, string | number | undefined>
) => {
  return api.get<StreamlitNotebookJobsGraphDataResponse>(
    "/query/v2/workloads/graph_data",
    params,
    0,
    StreamlitNotebookJobsGraphDataResponseSchema
  );
};

export const getStreamlitNotebookJobsGraphSummary = (
  params: Record<string, string | number | undefined>
) => {
  return api.get<CommonGraphSummaryResponse>(
    "/query/v2/workloads/graph_summary",
    params,
    0,
    CommonGraphSummarySchema
  );
};

export const getStreamlitNotebookJobRunsList = (
  workload_hash: string,
  params: Record<string, string | number | undefined>
) => {
  return api.get<Page<StreamlitNotebookJobRunsListResponse>>(
    `/query/v2/workloads/jobs/${workload_hash}`,
    params,
    0,
    pageSchema(StreamlitNotebookRunsListSchema)
  );
};

export const getStreamlitNotebookJobRunsSummary = (
  workload_hash: string,
  params: Record<string, string | number | undefined>
) => {
  return api.get<StreamlitNotebookJobRunsSummaryDataResponse>(
    `/query/v2/workloads/summary/${workload_hash}`,
    params,
    0,
    CommonRunsSummarySchema
  );
};

export const getStreamlitNotebookJobChildQueries = (
  session_id: string,
  params: Record<string, string | number | undefined>
) => {
  return api.get<Page<StreamlitNotebookChildQueriesListResponse>>(
    `/query/v2/workloads/child_queries/${session_id}`,
    params,
    0,
    pageSchema(StreamlitNotebookChildQueriesSchema)
  );
};

// Stored Procedures APIs
export const getStoredProceduresJobs = (params: GetStoredProceduresJobsParams) => {
  return api.get<Page<StoredProceduresJobListResponse>>(
    "/query/v2/workloads/stored-procedures/list",
    params,
    0,
    pageSchema(CommonJobListSchema)
  );
};

export const getStoredProceduresJobsGraphData = (
  params: GetStoredProceduresJobsGraphDataParams
) => {
  return api.get<StoredProceduresJobsGraphDataResponse>(
    "/query/v2/workloads/stored-procedures/graph_data",
    params,
    0,
    StoredProceduresJobsGraphDataSchema
  );
};

export const getStoredProceduresJobsGraphSummary = (
  params: GetStoredProceduresJobsGraphDataParams
) => {
  return api.get<CommonGraphSummaryResponse>(
    "/query/v2/workloads/stored-procedures/graph_summary",
    params,
    0,
    CommonGraphSummarySchema
  );
};

export const getStoredProceduresJobsListFilterValues = (
  params: GetStoredProceduresJobsListFilterValuesParams
) => {
  return api.get<StoredProceduresJobsListFilterValuesResponse>(
    "/query/v2/workloads/stored-procedures/filters",
    params,
    0,
    StoredProceduresJobsListFilterValuesSchema
  );
};

export const getStoredProceduresRunsSummary = (
  workload_hash: string,
  params: GetStoredProceduresRunsSummaryParams
) => {
  return api.get<StoredProceduresJobRunsSummaryDataResponse>(
    `/query/v2/workloads/stored-procedures/jobs_summary/${workload_hash}`,
    params,
    0,
    CommonRunsSummarySchema
  );
};

export const getStoredProceduresRunsGraphData = (
  workload_hash: string,
  params: GetStoredProceduresRunsGraphDataParams
) => {
  return api.get<StoredProceduresJobRunsGraphData[]>(
    `/query/v2/workloads/stored-procedures/jobs_graph_data/${workload_hash}`,
    params,
    0,
    z.array(CommonRunsListGraphDataSchema)
  );
};

export const getStoredProcedureRunsList = (
  workload_hash: string,
  params: GetStoredProcedureRunsListParams
) => {
  return api.get<Page<StoredProceduresJobRunsListResponse>>(
    `/query/v2/workloads/stored-procedures/jobs/${workload_hash}`,
    params,
    0,
    pageSchema(StoredProceduresRunsListSchema)
  );
};

export const getStoredProceduresChildQueries = (
  queryId: string,
  params: GetStoredProceduresChildQueriesParams
) => {
  return api.get<Page<StoredProceduresChildQueriesListResponse>>(
    `/query/v2/workloads/stored-procedures/child_queries/${queryId}`,
    params,
    0,
    pageSchema(StoredProceduresChildQueriesSchema)
  );
};

// Tasks (Snowflake tasks workload) APIs
export const getTasksJobs = (params: GetTasksJobsParams) => {
  return api.get<Page<TaskListResponse>>(
    "/query/v2/tasks/list",
    params,
    0,
    pageSchema(TaskListSchema)
  );
};

export const getTasksGraphData = (params: GetTasksDateParams) => {
  return api.get<TasksGraphDataResponse>(
    "/query/v2/tasks/graph_data",
    params,
    0,
    TasksGraphDataSchema
  );
};

export const getTasksGraphSummary = (params: GetTasksDateParams) => {
  return api.get<CommonGraphSummaryResponse>(
    "/query/v2/tasks/graph_summary",
    params,
    0,
    CommonGraphSummarySchema
  );
};

export const getTasksListFilterValues = (params: GetTasksDateParams) => {
  return api.get<TasksListFilterValuesResponse>(
    "/query/v2/tasks/filters",
    params,
    0,
    TasksListFilterValuesSchema
  );
};

export const getTasksRuns = (params: GetTasksRunsParams) => {
  return api.get<Page<TaskRunResponse>>(
    "/query/v2/tasks/runs",
    params,
    0,
    pageSchema(TaskRunSchema)
  );
};

export const getTasksRunsSummary = (params: GetTasksRunsParams) => {
  return api.get<TaskRunsSummaryResponse>(
    "/query/v2/tasks/runs_summary",
    params,
    0,
    TaskRunsSummarySchema
  );
};

export const getTasksRunsGraphData = (params: GetTasksRunsParams) => {
  return api.get<TaskRunsGraphDataResponse>(
    "/query/v2/tasks/runs_graph_data",
    params,
    0,
    TaskRunsGraphDataSchema
  );
};

export const getMetabaseDashboard = (dashboardId: number) => {
  return api.get<MetabaseDashboard>(
    `/metabase/dashboards/${dashboardId}`,
    {},
    0,
    MetabaseDashboardSchema
  );
};

// Spaces APIs (using existing dashboard endpoint)
export const getSpaces = (
  params: SpacesRequest = {
    page: 1,
    page_size: 20,
    sort_by: "updated_at",
    sort_order: "desc",
    archived: false,
  }
) => {
  // Remove undefined/null keys so we don't send `key=undefined`
  const cleanParams = Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null)
  ) as SpacesRequest;
  return api.get<SpacesResponse>("/metabase/dashboards", cleanParams, 0, SpacesResponseSchema);
};

export const syncAllDashboards = () => {
  return api.post<SyncAllDashboardsResponse>(
    "/metabase/dashboards/sync-all",
    {},
    SyncAllDashboardsResponseSchema
  );
};

// Snowflake Compute Pools APIs
export const getComputePoolsSummary = (params?: { start_date?: string; end_date?: string }) =>
  api.get<z.infer<typeof computePoolsSummarySchema>>(
    "/compute-pools/summary",
    params || {},
    0,
    computePoolsSummarySchema
  );

export const getComputePoolDailyCost = (
  poolName: string,
  params?: { start_date?: string; end_date?: string }
) =>
  api.get<z.infer<typeof computePoolDailyCostItemSchema>[]>(
    `/compute-pools/${encodeURIComponent(poolName)}/daily-cost`,
    params || {},
    0,
    z.array(computePoolDailyCostItemSchema)
  );

export const getComputePoolsGraphData = (params: { start_date: string; end_date: string }) =>
  api.get<z.infer<typeof computePoolsGraphDataSchema>>(
    `/compute-pools/daily-cost`,
    params,
    0,
    computePoolsGraphDataSchema
  );

export const getComputePoolsMetadata = (params?: { start_date?: string; end_date?: string }) =>
  api.get<z.infer<typeof computePoolMetadataItemSchema>[]>(
    "/compute-pools/metadata",
    params || {},
    0,
    z.array(computePoolMetadataItemSchema)
  );

export const getComputePoolOverview = (
  poolName: string,
  params?: { start_date?: string; end_date?: string }
) =>
  api.get<z.infer<typeof computePoolOverviewSchema>>(
    `/compute-pools/${encodeURIComponent(poolName)}/overview`,
    params || {},
    0,
    computePoolOverviewSchema
  );

export const getComputePoolEntries = (
  poolName: string,
  params?: { start_date?: string; end_date?: string }
) =>
  api.get<Page<ComputePoolEntry>>(
    `/compute-pools/${encodeURIComponent(poolName)}/entries`,
    params || {},
    0,
    pageSchema(computePoolEntrySchema)
  );

// Snowflake Contracts APIs
export const getContractsSummary = () =>
  api.get<z.infer<typeof contractsSummarySchema>>(
    "/contracts/summary",
    {},
    0,
    contractsSummarySchema
  );

export const getActiveContracts = () =>
  api
    .get<{
      contracts: z.infer<typeof contractItemSchema>[];
    }>("/contracts/active", {}, 0, z.object({ contracts: z.array(contractItemSchema) }))
    .then(r => r?.contracts);

// Credits Usage APIs
export const getMyUsage = (params: {
  start_date: string;
  end_date: string;
  page?: number;
  size?: number;
}) => {
  return api.get<Page<Transaction>>(
    "/credits/my-usage/table-data",
    params,
    0,
    pageSchema(transactionSchema)
  );
};

export const getMyUsageTrends = (params: { start_date: string; end_date: string }) => {
  return api.get<MyUsageTrendsResponse>(
    "/credits/my-usage/trends",
    params,
    0,
    myUsageTrendsResponseSchema
  );
};

export const getOrgUsageGraphData = (params: { start_date: string; end_date: string }) => {
  return api.get<OrgUsageTrendsResponse>(
    "/credits/org-usage/trends",
    params,
    0,
    orgUsageTrendsResponseSchema
  );
};

export const getOrgUsageTransactions = (params: {
  start_date: string;
  end_date: string;
  page?: number;
  size?: number;
}) => {
  return api.get<Page<AggregatedTask>>(
    "/credits/org-usage/table-data/aggregated-by-task",
    params,
    0,
    pageSchema(aggregatedTaskSchema)
  );
};

export const getOrgUsageTransactionsGroupedByUser = (params: {
  start_date: string;
  end_date: string;
  page?: number;
  size?: number;
}) => {
  return api.get<Page<TransactionGroupedByUser>>(
    "/credits/org-usage/table-data/by-users",
    params,
    0,
    pageSchema(transactionGroupedByUserSchema)
  );
};

export const getPlans = () => {
  return api.get<PlansResponse>("/credits/plans", {}, 0, PlansResponseSchema);
};

export const downloadMyUsageCSV = (params: { start_date: string; end_date: string }) =>
  downloadCSV("/credits/my-usage/table-data", params);

export const downloadOrgUsageCSV = (params: { start_date: string; end_date: string }) =>
  downloadCSV("/credits/org-usage/table-data", params);

export const downloadOrgUsageByUsersCSV = (params: { start_date: string; end_date: string }) =>
  downloadCSV("/credits/org-usage/table-data/by-users", params);

export const getImpersonationDetails = (body: { email: string }) => {
  return api.post<UserLoginOutResponse>("/impersonate", body, userLoginOutSchema);
};

// Warehouse Cost Summary API
export const getWarehouseCostSummary = (params: GetAllWarehousesParams) =>
  api.get<WarehouseCostSummaryResponse>(
    `/warehouse/total_savings/`,
    params,
    0,
    WarehouseCostSummarySchema
  );

// Warehouse Summary Cost Graph API
export const getWarehouseSummaryCostGraph = (params: GetWarehouseSummaryCostGraphParams) =>
  api.get<WarehouseSummaryCostGraphResponse>(
    `/warehouse/cost/v2/`,
    params,
    0,
    WarehouseSummaryCostGraphSchema
  );

// Warehouse APIs
export const getAllWarehouses = (params: GetAllWarehousesParams = {}) => {
  return api.get<WarehouseListResponse>(`/warehouse/`, params, 0, WarehouseListResponseSchema);
};

export const getWarehouseFilters = (params: GetWarehouseFiltersParams = {}) =>
  api.get<WarehouseFiltersResponse>(
    `/warehouse/filters/`,
    params,
    0,
    WarehouseFiltersResponseSchema
  );

// Get Warehouse Details API
export const getWarehouse = (
  instance_id: string,
  warehouse_rk: string,
  params: GetWarehouseParams = {}
) =>
  api.get<WarehouseDetails>(
    `/warehouse/instance/${instance_id}/warehouse_rk/${warehouse_rk}/`,
    params,
    0,
    WarehouseDetailsSchema
  );

// Get Warehouse Auto Savings API V2
export const getWarehouseAutoSavingsDataV2 = (
  instance_id: string,
  warehouse_rk: string,
  params: GetWarehouseAutoSavingsParams = {}
) =>
  api.get<WarehouseAutoSavingsResponse>(
    `/warehouse/auto_savings/v2/instance/${instance_id}/warehouse_rk/${warehouse_rk}`,
    params,
    0,
    WarehouseAutoSavingsResponseSchema
  );

// Get Warehouse Auto Tune Configuration API
export const getWarehouseAutoTuneConfiguration = (instance_id: string, warehouse_rk: string) =>
  api.get<WarehouseAutoTuneConfiguration>(
    `/warehouse/auto-tune-configuration/instance/${instance_id}/warehouse_rk/${warehouse_rk}`,
    {},
    0,
    WarehouseAutoTuneConfigurationSchema
  );

// Set Warehouse Auto Tune Configuration API
export const setWarehouseAutoTuneConfiguration = (
  instance_id: string,
  warehouse_rk: string,
  body: SetWarehouseAutoTuneConfigurationRequest
) =>
  api.post<WarehouseAutoTuneToggleResponse>(
    `/warehouse/auto-tune/toggle-auto-manage?instance_id=${instance_id}&warehouse_rk=${warehouse_rk}`,
    body,
    WarehouseAutoTuneToggleResponseSchema
  );

// Get Warehouse Decision Data API
export const getWarehouseDecisionData = (
  instance_id: string,
  warehouse_rk: string,
  params?: GetWarehouseDecisionDataParams
) =>
  api.get<WarehouseDecisionDataResponse>(
    `/warehouse/decision-chart/instance/${instance_id}/warehouse_rk/${warehouse_rk}`,
    params || {},
    0,
    WarehouseDecisionDataResponseSchema
  );

// Get Warehouse Query Execution Times API
export const getWarehouseQueryExecutionTimes = (
  instance_id: string,
  warehouse_rk: string,
  params?: GetWarehouseQueryExecutionTimesParams
) =>
  api.get<WarehouseQueryExecutionTimesResponse>(
    `/warehouse/query-execution/instance/${instance_id}/warehouse_rk/${warehouse_rk}`,
    params || {},
    0,
    WarehouseQueryExecutionTimesResponseSchema
  );

// Toggle warehouse auto savings on/off
export const toggleWarehouseAutoSavings = (instance_id: string, warehouse_rk: string) =>
  api.post<ToggleWarehouseAutoSavingsResponse>(
    `/warehouse/toggle-auto-manage/instance/${instance_id}/warehouse_rk/${warehouse_rk}`,
    {},
    ToggleWarehouseAutoSavingsResponseSchema
  );

// Get warehouse auto savings configuration
export const getWarehouseAutoSavings = (instance_id: string, warehouse_rk: string) =>
  api.get<WarehouseAutoSavingsConfigurationResponse>(
    `/warehouse/auto-savings-config/instance/${instance_id}/warehouse_rk/${warehouse_rk}`,
    {},
    0,
    WarehouseAutoSavingsConfigurationResponseSchema
  );

// Toggle warehouse schedules on/off
export const toggleWarehouseSchedules = (instance_id: string, warehouse_name: string) =>
  api.post<ToggleWarehouseSchedulesResponse>(
    `/warehouse/schedules/toggle-schedules/`,
    {
      instance_id,
      warehouse_name,
    },
    ToggleWarehouseSchedulesResponseSchema
  );

// Get Warehouse Auto Savings Audit Events V2 API
export const getWarehouseAutoSavingsAuditEventsV2 = (
  instance_id: string,
  warehouse_rk: string,
  params?: GetWarehouseAuditEventsParams
) =>
  api.get<WarehouseAuditEventsResponse>(
    `/warehouse/audit-events/v2/instance/${instance_id}/warehouse_rk/${warehouse_rk}`,
    params || {},
    0,
    WarehouseAuditEventsResponseSchema
  );

// Get Warehouse Cost API
export const getWarehouseCost = (
  instance_id: string,
  warehouse_rk: string,
  params?: GetWarehouseCostParams
) =>
  api.get<WarehouseCostResponse>(
    `/warehouse/cost/instance/${instance_id}/warehouse_rk/${warehouse_rk}/`,
    params || {},
    0,
    WarehouseCostResponseSchema
  );

// Get Warehouse Cost V2 API
export const getWarehouseCostV2 = (
  instance_id: string,
  warehouse_rk: string,
  params?: GetWarehouseCostParams
) =>
  api.get<WarehouseCostResponse>(
    `/warehouse/cost/instance/${instance_id}/warehouse_rk/${warehouse_rk}/v2/`,
    params || {},
    0,
    WarehouseCostResponseSchema
  );

// Get Warehouse Cluster Utilization API
export const getWarehouseClusterWarehouseUtilization = (
  instance_id: string,
  warehouse_rk: string,
  params?: GetWarehouseCostParams
) =>
  api.get<WarehouseClusterUtilizationResponse>(
    `/warehouse/cluster_utilization/instance/${instance_id}/warehouse_rk/${warehouse_rk}/`,
    params || {},
    0,
    WarehouseClusterUtilizationResponseSchema
  );

// Get Warehouse Last Event Time API
export const getWarehouseLastEventTime = () =>
  api.get<WarehouseLastEventTimeResponse>(
    "/warehouse/last_event_time",
    {},
    0,
    WarehouseLastEventTimeResponseSchema
  );

// Get Warehouse Metadata API
export const getWarehouseMetadata = () =>
  api.get<WarehouseMetadataResponse>("/warehouse/metadata", {}, 0, WarehouseMetadataResponseSchema);

// AI Feedback APIs
// Get AI Feedback API
export const getAIFeedback = (params: { feedback_id: string; feedback_type: string }) =>
  api.get<GetAIFeedbackResponse>("/feedbacks/ai", params, 0, GetAIFeedbackResponseSchema);

// Post AI Feedback API
export const postAIFeedback = (params: { feedback_id: string; feedback_type: string }) => {
  return (data: {
    feedback_value: string;
    full_form: {
      good_form: Array<{ label: string; type: string; value: string }>;
      bad_form: Array<{ label: string; type: string; value: string }>;
    };
  }) =>
    api.postWithParams<{ is_submitted: boolean }>(
      "/feedbacks/ai",
      params,
      data,
      z.object({
        is_submitted: z.boolean(),
      })
    );
};

// Payment APIs
// Create Checkout Session API
export const createCheckoutSession = (data: CheckoutSessionRequest) =>
  api.post<CreateCheckoutSessionResponse>(
    "/payment/checkout",
    data,
    CreateCheckoutSessionResponseSchema
  );

// Post Checkout Session API
export const postCheckoutSession = (data: PostCheckoutSessionRequest) =>
  api.post<StandardResponse>("/payment/checkout/update", data, StandardResponseSchema);

// Downgrade Plan API
export const downgradePlan = (data: DowngradePlanRequest) =>
  api.post<StandardResponse>("/payment/downgrade", data, StandardResponseSchema);

// Cancel Plan Downgrade API
export const cancelPlanDowngrade = () =>
  api.post<StandardResponse>("/payment/downgrade/cancel", {}, StandardResponseSchema);

// Downgrade Plan Feedback API
export const downgradePlanFeedback = (data: DowngradePlanFeedbackRequest) =>
  api.post<DowngradePlanFeedbackResponse>(
    "/payment/downgrade/feedback",
    data,
    DowngradePlanFeedbackResponseSchema
  );

// Get Credit Usage API
export const getCreditUsage = () =>
  api.get<CreditUsageResponse>("/payment/credits", {}, 0, CreditUsageResponseSchema);

// Get All Plans API
export const getAllPlans = (tenure: string) =>
  api.get<AllPlansResponse>(`/payment/plans/${tenure}`, {}, 0, AllPlansResponseSchema);

// Wallet APIs
export const getWallet = () =>
  api.get<WalletResponse>("/payment/wallet", {}, 0, WalletResponseSchema);

export const getTokenUsage = () =>
  api.get<TokenUsageResponse>("/payment/token-usage", {}, 0, TokenUsageResponseSchema);

export const createWalletDeposit = (data: { amount_cents: number }) =>
  api.post<WalletDepositResponse>("/payment/wallet/deposit", data, WalletDepositResponseSchema);

export const confirmWalletDeposit = (data: { checkout_session_id: string }) =>
  api.post<StandardResponse>("/payment/wallet/deposit/confirm", data, StandardResponseSchema);

export const updateWalletSettings = (data: {
  spend_cap_cents: number | null;
  auto_reload_enabled: boolean;
  auto_reload_amount_cents: number;
  auto_reload_threshold_cents: number;
}) => api.put<StandardResponse>("/payment/wallet/settings", data, StandardResponseSchema);

// Metabase Pulse Preview API
export const getMetabasePulsePreview = (dashboardId: number) =>
  api.get<{
    html: string;
  }>(
    `/metabase/pulse/preview_dashboard/${dashboardId}`,
    {},
    0,
    z.object({
      html: z.string(),
    })
  );

export const fetchDbtInfo = (params: { table_rk: string }) =>
  api.get<DbtInfoResponse>(`/lineage/dbt_info`, params, 0, DbtInfoResponseSchema);

// Lineage APIs
export const upstreamTables = (fqn: string) =>
  api.post<LineageResponse>("/lineage/upstream", { fqn }, lineageResponseSchema);

export const downstreamTables = (fqn: string) =>
  api.post<LineageResponse>("/lineage/downstream", { fqn }, lineageResponseSchema);

export const postColumns = (data: GetColumnsRequest) =>
  api.get<GetColumnsResponse>("/lineage/schema", data, 0, getColumnsResponseSchema);

export const altimateToAmundsen = (params: TerminalNodeRequest) =>
  api.get<TerminalNodeResponse>("/lineage/terminal", params, 0, terminalNodeResponseSchema);

export const postConnectedColumns = (data: ConnectedColumnsRequest) =>
  api.post<ConnectedColumnsResponse>("/lineage/v2/connected", data, connectedColumnsResponseSchema);

// Datasets/Tables APIs
export const getTables = (params: GetTablesParams) =>
  api.get<TablesResponse>("/tables/", params, 0, TablesResponseSchema);

export const getTableFilter = () =>
  api.get<TableFiltersResponse>("/tables/filters", {}, 0, TableFiltersResponseSchema);

export const getDatasetTags = () =>
  api.get<TagsResponse>("/tables/columns/tags/", {}, 0, DatasetTagsResponseSchema);

export const getContracts = () =>
  api.get<ContractsResponse>("/contracts/", {}, 0, ContractsResponseSchema);

export const getTablesAccessed = () =>
  api.get<RecentTablesResponse>("/tables/recent", {}, 0, RecentTablesResponseSchema);

export const getTableRk = (tableId: string) =>
  api.get<TableRkResponse>(`/tables/table_rk/${tableId}`, {}, 0, TableRkResponseSchema);

export const updateTableSchemaDesc = (columnId: number, data: UpdateColumnDescriptionRequest) =>
  api.update<StandardResponse>(`/tables/column/${columnId}`, data, StandardResponseSchema);

// Contract APIs
// Get contract details by ID
export const getContractDetail = (contractId: string) =>
  api.get<ContractDetailResponse>(
    `/contracts/details/${contractId}`,
    {},
    0,
    ContractDetailResponseSchema
  );

// Validate a contract
export const validateContract = (params: ValidateContractRequest) =>
  api.post<ValidateContractResponse>(
    `/registries/${params.registryId}/contracts/${params.contractId}/validate`,
    {},
    ValidateContractResponseSchema
  );

// Toggle contract schedule on/off
export const toggleContractSchedule = (contractId: string) =>
  api.post<{ ok: boolean }>(`/scheduler/${contractId}`, {}, z.object({ ok: z.boolean() }));

// Mask Data APIs
// Get mask data configuration
export const getMaskData = () =>
  api.get<GetMaskDataResponse>("/mask/", {}, 0, GetMaskDataResponseSchema);

// Set/update mask data configuration
export const setMaskData = (data: SetMaskDataRequest) =>
  api.post<SetMaskDataResponse>("/mask/", data, SetMaskDataResponseSchema);

// Table Detail and Management APIs
// Get table by ID
export const getTableById = (id: string) =>
  api.get<TableDetailResponse>(`/tables/items/${id}`, {}, 0, TableDetailResponseSchema);

// Get table description by ID
export const getTableDescriptionById = (id: string) =>
  api.get<TableDescriptionResponse>(
    `/tables/table_description/${id}`,
    {},
    0,
    TableDescriptionResponseSchema
  );

// Update table purpose/description
export const updateTablePurpose = (id: string, data: UpdateTablePurposeRequest) =>
  api.update<StandardResponse, UpdateTablePurposeRequest>(
    `/tables/${id}`,
    data,
    StandardResponseSchema
  );

// Generate AI description for table
export const generateDescription = (id: string, data: Record<string, string>) =>
  api.post<StandardResponse>(`/recommendations/description/${id}`, data, StandardResponseSchema);

// Get table insights (storage breakdown)
export const getTableInsightsById = (id: string) =>
  api.get<TableInsightsResponse>(`/tables/insights/${id}`, {}, 0, TableInsightsResponseSchema);

// Post custom table insight
export const postCustomTableInsight = (params: PostCustomTableInsightRequest) =>
  api.post<PostCustomTableInsightResponse>(
    `/tables/custom/${params.id}`,
    params.data,
    PostCustomTableInsightResponseSchema
  );

// Get table ID from table resource key
export const getTableId = (data: GetTableIdRequest) =>
  api.post<GetTableIdResponse>(`/tables/table_id`, data, GetTableIdResponseSchema);

// Get table storage details over time
export const getTableStorageOverTimeById = (id: string) =>
  api.get<TableStorageOverTimeResponse>(
    `/tables/storage_details/${id}`,
    {},
    0,
    TableStorageOverTimeResponseSchema
  );

// Column Access History APIs
// Get column access history with pagination
export const getColumnAccessHistory = (params: GetColumnAccessHistoryParams) =>
  api.post<ColumnAccessHistoryResponse>(
    `/tables/access_history/?page=${params.page}&size=${params.size}`,
    {
      table_id: params.table_id,
      column_rk: params.column_rk,
      order_by: params.order_by,
      sort_order: params.sort_order,
    },
    ColumnAccessHistoryResponseSchema
  );

// Get column access history filters
export const getColumnUserAccessHistoryFilters = (tableID: string) =>
  api.get<ColumnUserAccessHistoryFiltersResponse>(
    `/tables/access_history/filters/?table_id=${tableID}`,
    {},
    0,
    ColumnUserAccessHistoryFiltersResponseSchema
  );

// Get user-specific column access history
export const getColumnUserAccessHistory = (data: GetColumnUserAccessHistoryRequest) =>
  api.post<ColumnUserAccessHistoryResponse>(
    `/tables/access_history/column/`,
    data,
    ColumnUserAccessHistoryResponseSchema
  );

// Get dataset/table list with filters
export const getDatasetList = (params: TableDataFilters) =>
  api.get<DatasetListResponse>("/tables/tabledata/", params, 0, DatasetListResponseSchema);

// Lineage V2 APIs
// Get upstream tables (V2 endpoint)
export const upstreamTablesV2 = (fqn: string) =>
  api.post<UpstreamDownstreamTablesResponse>(
    "/lineage/v2/downstream",
    { fqn },
    upstreamDownstreamTablesResponseSchema
  );

// Get downstream tables (V2 endpoint)
export const downstreamTablesV2 = (fqn: string) =>
  api.post<UpstreamDownstreamTablesResponse>(
    "/lineage/v2/upstream",
    { fqn },
    upstreamDownstreamTablesResponseSchema
  );

// Demo Session API
export const createDemoSession = () =>
  api.post<DemoSessionResponse>("/demo/create-demo-session", {}, demoSessionResponseSchema);

export const authenticateDemo = (sessionId: string) =>
  api.post<User>(`/demo/authenticate?session_id=${sessionId}`, {}, UserSchema);

// Query Filters API with Pagination
export const getQueryFilters = (params: GetQueryFiltersParams = {}) =>
  api.get<QueryFilters>("/query/v2/filters/", params, 0, QueryFiltersSchema);

// Query Groups API
export const getAllQueryGroups = (params: QueryGroupsFilters) =>
  api.get<Page<QueryGroup>>("/query/v2/groups", params, 0, pageSchema(QueryGroupSchema));

// Shared Query Result APIs
export const getSharedQueryResult = () =>
  api.get<SharedQueryResultListResponse>(
    "/dbt/v3/query-result/list",
    {},
    0,
    sharedQueryResultListResponseSchema
  );

export const deleteSharedQueryResultById = (id: string) =>
  api.delete<DeleteQueryResultResponse>(
    `/dbt/v3/query-result/list/${id}`,
    {},
    deleteQueryResultResponseSchema
  );

export const getSharedQueryResultById = (id: string) =>
  api.get<SharedQueryResultDetail>(
    `/dbt/v3/query-result/list/${id}`,
    {},
    0,
    sharedQueryResultDetailSchema
  );

export const updateSharedQueryResultById = (
  id: string,
  data: { name?: string; sharing_status?: boolean }
) => api.update<StandardResponse>(`/dbt/v3/query-result/list/${id}`, data, StandardResponseSchema);

// Company APIs
export const getCompanyList = () =>
  api.get<CompanyListResponse>("/companies/", {}, 0, CompanyListResponseSchema);

export const addNewCompany = (company: { name: string; country: string }) =>
  api.post<StandardResponse>("/companies/", company, StandardResponseSchema);

// Subscriptions APIs
// Types are imported at the top of the file from the subscription feature module

export const fetchSubscriptionsV2 = (params: SubscriptionListFilters = {}) =>
  api.get<SubscriptionListResponse>(
    "/api/v1/alerts/rules",
    params,
    0,
    SubscriptionListResponseSchema
  );

export const fetchSubscriptionSummary = (params: SubscriptionSummaryFilters = {}) =>
  api.get<SubscriptionV2Summary>(
    "/api/v1/alerts/rules/summary",
    params,
    0,
    SubscriptionV2SummarySchema
  );

export const fetchSubscriptionFilters = () =>
  api.get<SubscriptionFiltersResponse>(
    "/api/v1/alerts/rules/filters",
    {},
    0,
    SubscriptionFiltersResponseSchema
  );

export const deleteSubscription = (id: string) =>
  api.delete<SubscriptionDeleteResponse>(
    `/api/v1/alerts/rules/${id}`,
    {},
    SubscriptionDeleteResponseSchema
  );

export const toggleSubscriptionStatus = (id: string, status: "active" | "inactive") =>
  api.post<SubscriptionResponse>(
    `/api/v1/alerts/rules/${id}`,
    { status },
    SubscriptionResponseSchema
  );

export const fetchSubscriptionById = (id: string) =>
  api.get<SubscriptionResponse>(`/api/v1/alerts/rules/${id}`, {}, 0, SubscriptionResponseSchema);

export const fetchSubscriptionHistory = (
  id: string,
  params: Record<string, string | number | undefined> = {}
) =>
  api.get<SubscriptionHistoryResponse>(
    `/api/v1/alerts/rules/${id}/runs`,
    params,
    0,
    SubscriptionHistoryResponseSchema
  );

export const createSubscriptionV2 = (data: BackendSubscriptionCreateRequest) =>
  api.post<SubscriptionResponse>("/api/v1/alerts/rules", data, SubscriptionResponseSchema);

export const updateSubscriptionV2 = (id: string, data: BackendSubscriptionCreateRequest) =>
  api.post<SubscriptionResponse>(`/api/v1/alerts/rules/${id}`, data, SubscriptionResponseSchema);

export const cloneSubscription = (id: string) =>
  api.post<SubscriptionResponse>(
    `/api/v1/alerts/rules/${id}/clone`,
    {},
    SubscriptionResponseSchema
  );

export const fetchSubscriptionTemplates = () =>
  api.get<SubscriptionTemplatesListResponse>(
    "/api/v1/alerts/templates",
    {},
    0,
    SubscriptionTemplatesListResponseSchema
  );

export const fetchSubscriptionTemplateById = (templateId: string) =>
  api.get<SubscriptionV2Template>(
    `/api/v1/alerts/templates/${templateId}`,
    {},
    0,
    SubscriptionV2TemplateSchema
  );

export const fetchMetricFilterOptions = (params: {
  category: string;
  metric_name: string;
  filter_key: string;
}) =>
  api.get<MetricFilterValuesResponse>(
    "/subscriptions/metric-filter-options",
    params,
    0,
    MetricFilterValuesResponseSchema
  );

export const fetchSubscriptionMetrics = (params: { platform?: "snowflake" | "databricks" } = {}) =>
  api.get<MetricsCatalogResponse>(
    "/api/v1/alerts/metrics/catalog",
    params,
    0,
    MetricsCatalogResponseSchema
  );

export const fetchSubscriptionPreview = (params?: SubscriptionPreviewParams) =>
  api.post<SubscriptionPreviewResponse>(
    "/api/v1/alerts/preview",
    params ?? {},
    SubscriptionPreviewResponseSchema
  );

export const testNotification = (data: NotificationTestRequest) =>
  api.post<NotificationTestResponse>(
    "/api/v1/alerts/notifications/test",
    data,
    NotificationTestResponseSchema
  );

export const fetchMetricFilterOptionsById = (filterName: string, metricName?: string) =>
  api.get<MetricFilterOptionsResponse>(
    `/api/v1/alerts/metrics/filters/${filterName}/options`,
    { metric_name: metricName },
    0,
    MetricFilterOptionsResponseSchema
  );

export const searchMetricFilterOptions = (params: {
  filterName: string;
  metricName: string;
  search: string;
  page: number;
  size: number;
}) =>
  api.get<MetricFilterOptionsResponse>(
    `/api/v1/alerts/metrics/filters/${params.filterName}/options`,
    {
      metric_name: params.metricName,
      search: params.search,
      page: params.page,
      size: params.size,
    },
    0,
    MetricFilterOptionsResponseSchema
  );

// Auth APIs

// Warehouse Insights API
export const getWarehouseInsights = (data: WarehouseInsightsRequest) =>
  api.post<WarehouseInsightsResponse>("/warehouse/insights", data, WarehouseInsightsResponseSchema);

// Integration Environments API
export const getIntegrationEnvironments = (integrationId: string) =>
  api.get<IntegrationEnvironmentsResponse>(
    `/dbt/v1/integration_environments/${integrationId}`,
    {},
    0,
    IntegrationEnvironmentsResponseSchema
  );

// DBT Docs Share APIs
export const getDbtDocsShareList = (params: GetDbtDocsShareListParams) =>
  api.get<DbtDocsShareListResponse>(
    "dbt/dbt_docs_share",
    params,
    0,
    DbtDocsShareListResponseSchema
  );

export const getDbtDocsShareListFilters = () =>
  api.get<DbtDocsFilters>("dbt/dbt_docs_share/filters", {}, 0, DbtDocsFiltersSchema);

export const getDbtDocsShareUrl = (shareId: string) =>
  api.get<DbtDocsShareUrlResponse>(
    `dbt/dbt_docs_share/${shareId}`,
    {},
    0,
    DbtDocsShareUrlResponseSchema
  );

export const deleteDbtDocsShare = (shareId: string) =>
  api.delete<DbtDocsStandardApiResponse>(
    `dbt/dbt_docs_share/${shareId}`,
    {},
    DbtDocsStandardApiResponseSchema
  );

// Current User API
export const getCurrentUser = () =>
  api.get<CurrentUserResponse>("/dbt/dbt_docs_share/user/details", {}, 0, BaseUserSchema);

// Experimentation APIs
export const getAllExperiments = (params: GetAllExperimentsParams) =>
  api.get<GetAllExperimentsResponse>(
    "/experimentation",
    params,
    0,
    GetAllExperimentsResponseSchema
  );

export const getExperimentById = (id: string) =>
  api.get<ExperimentDetail>(`/experimentation/${id}`, {}, 0, ExperimentDetailSchema);

export const getExperimentSummary = () =>
  api.get<ExperimentSummaryResponse>(
    "/experimentation/metadata/summary",
    {},
    0,
    ExperimentSummaryResponseSchema
  );

export const createExperiment = (data: CreateExperimentRequest) =>
  api.post<{ ok: boolean }>("/experimentation", data, ExperimentStandardApiResponseSchema);

export const updateExperimentStatus = (id: string, data: UpdateExperimentStatusRequest) =>
  api.update<{ ok: boolean }>(
    `/experimentation/${id}/status`,
    data,
    ExperimentStandardApiResponseSchema
  );

export const getSchemaMetadata = () =>
  api.get<SchemaMetadataResponse>(
    "/experimentation/schema/metadata",
    {},
    0,
    SchemaMetadataResponseSchema
  );

// Query Jobs API
export const getQueryJobs = (params: QueryJobsFilters) =>
  api.get<QueryJobsResponse>("/query/jobs", params, 60000, QueryJobsResponseSchema);

// Get query job executions by ID
export const getQueryJobExecutions = (id: string, params: QueryJobExecutionsFilters) =>
  api.get<QueryJobExecutionsResponse>(
    `/query/jobs/executions/${id}`,
    params,
    0,
    QueryJobExecutionsResponseSchema
  );

// Get job definitions by ID
export const getJobDefinitions = (id: string) =>
  api.get<QueryJobDefinitionsResponse>(
    `/query/jobs/definitions/${id}`,
    {},
    0,
    QueryJobDefinitionsResponseSchema
  );

// Update query job name
export const updateQueryJobName = (id: string, data: UpdateQueryJobNameRequest) =>
  api.post<UpdateQueryJobNameResponse>(
    `/query/job/edit_name/${id}`,
    data,
    UpdateQueryJobNameResponseSchema
  );

// Get query job filters
export const getQueryJobFilters = (params: GetQueryJobFiltersParams) =>
  api.get<QueryJobFiltersResponse>("/query/job/filters/", params, 0, QueryJobFiltersResponseSchema);

// Get query job graph data
export const getQueryJobData = (id: string, params: GetQueryJobDataParams) =>
  api.get<QueryGraphDataResponse>(
    `/query/jobs/graph_data/${id}`,
    params,
    0,
    QueryGraphDataResponseSchema
  );

// Get query group graph data
export const getQueryGroupData = (id: string, params: GetQueryGroupDataParams) =>
  api.get<QueryGraphDataResponse>(
    `/query/groups/graph_data/${id}`,
    params,
    0,
    QueryGraphDataResponseSchema
  );

export const postForgotPassword = (data: ForgotPasswordRequest) =>
  api.post<StandardResponse>("/auth/forgot-password", data, StandardResponseSchema);

// dbt Cloud APIs
const DBT_CLOUD_API_BASE = "/api/v1/dbt/cloud";

// Validate dbt Cloud API token
export const validateDbtCloudToken = (request: DbtCloudValidateTokenRequest) =>
  api.post<DbtCloudValidationResponse>(
    `${DBT_CLOUD_API_BASE}/validate`,
    request,
    DbtCloudValidationResponseSchema
  );

// Test an existing dbt Cloud connection using stored credentials
export const testDbtCloudConnection = (connectionId: number) =>
  api.post<DbtCloudValidationResponse>(
    `${DBT_CLOUD_API_BASE}/connections/${connectionId}/test`,
    {},
    DbtCloudValidationResponseSchema
  );

// Create a new dbt Cloud connection
export const createDbtCloudConnection = (request: DbtCloudConnectionCreateRequest) =>
  api.post<DbtCloudConnectionResponse>(
    `${DBT_CLOUD_API_BASE}/connections`,
    request,
    DbtCloudConnectionResponseSchema
  );

// List all dbt Cloud connections
export const listDbtCloudConnections = () =>
  api.get<DbtCloudConnectionListResponse>(
    `${DBT_CLOUD_API_BASE}/connections`,
    null,
    0,
    DbtCloudConnectionListResponseSchema
  );

// Get a specific dbt Cloud connection
export const getDbtCloudConnection = (connectionId: number) =>
  api.get<DbtCloudConnectionResponse>(
    `${DBT_CLOUD_API_BASE}/connections/${connectionId}`,
    null,
    0,
    DbtCloudConnectionResponseSchema
  );

// Delete a dbt Cloud connection
export const deleteDbtCloudConnection = (connectionId: number) =>
  api.delete(
    `${DBT_CLOUD_API_BASE}/connections/${connectionId}`,
    {},
    DbtCloudConnectionResponseSchema
  );

// Update a dbt Cloud connection
export const updateDbtCloudConnection = (
  connectionId: number,
  request: DbtCloudConnectionUpdateRequest
) =>
  api.update<DbtCloudConnectionResponse>(
    `${DBT_CLOUD_API_BASE}/connections/${connectionId}`,
    request,
    DbtCloudConnectionResponseSchema
  );

// Refresh (re-discover) projects and environments
export const refreshDbtCloudConnection = (connectionId: number) =>
  api.post<DbtCloudDiscoveryResponse>(
    `${DBT_CLOUD_API_BASE}/connections/${connectionId}/refresh`,
    {},
    DbtCloudDiscoveryResponseSchema
  );

// Update a project (enable/disable)
export const updateDbtCloudProject = (projectId: number, request: DbtCloudProjectUpdateRequest) =>
  api.update<DbtCloudProjectResponse>(
    `${DBT_CLOUD_API_BASE}/projects/${projectId}`,
    request,
    DbtCloudProjectResponseSchema
  );

// Update an environment (enable/disable)
export const updateDbtCloudEnvironment = (
  environmentId: number,
  request: DbtCloudEnvironmentUpdateRequest
) =>
  api.update<DbtCloudEnvironmentResponse>(
    `${DBT_CLOUD_API_BASE}/environments/${environmentId}`,
    request,
    DbtCloudEnvironmentResponseSchema
  );

// Trigger a sync
export const triggerDbtCloudSync = (connectionId: number) =>
  api.post<DbtCloudSyncResponse>(
    `${DBT_CLOUD_API_BASE}/connections/${connectionId}/sync`,
    {},
    DbtCloudSyncResponseSchema
  );

// Get sync history params interface
export interface GetDbtCloudSyncHistoryParams {
  page?: number;
  size?: number;
}

// Get sync history
export const getDbtCloudSyncHistory = (
  connectionId: number,
  params?: GetDbtCloudSyncHistoryParams
) =>
  api.get<DbtCloudSyncHistoryResponse>(
    `${DBT_CLOUD_API_BASE}/connections/${connectionId}/sync-history`,
    params,
    0,
    DbtCloudSyncHistoryResponseSchema
  );

// Update schedule
export const updateDbtCloudSchedule = (
  connectionId: number,
  request: DbtCloudScheduleUpdateRequest
) =>
  api.update<DbtCloudScheduleResponse>(
    `${DBT_CLOUD_API_BASE}/connections/${connectionId}/schedule`,
    request,
    DbtCloudScheduleResponseSchema
  );

// =====================================
// Databricks APIs
// =====================================
const DATABRICKS_API_BASE = "/api/v1/databricks";

// SKU Cost APIs
export const getSkuCostBreakdown = (params: SkuBreakdownParams) => {
  const { group_by = "sku", ...restParams } = params;
  return api.get<SkuCostBreakdownResponse>(
    `${DATABRICKS_API_BASE}/cost/breakdown/${group_by}`,
    restParams,
    0,
    SkuCostBreakdownResponseSchema
  );
};

export const getComputeEfficiency = (params: SkuCostParams) => {
  return api.get<ComputeEfficiencyResponse>(
    `${DATABRICKS_API_BASE}/cost/compute-efficiency`,
    params,
    0,
    ComputeEfficiencyResponseSchema
  );
};

export const getDeploymentTypeTrends = (params: TypeTrendsParams) =>
  api.get<DeploymentTypeTrendsResponse>(
    `${DATABRICKS_API_BASE}/cost/trends/by-deployment-type`,
    params,
    0,
    DeploymentTypeTrendsResponseSchema
  );

export const getEngineTypeTrends = (params: TypeTrendsParams) =>
  api.get<EngineTypeTrendsResponse>(
    `${DATABRICKS_API_BASE}/cost/trends/by-engine-type`,
    params,
    0,
    EngineTypeTrendsResponseSchema
  );

// =====================================
// Workspaces Cost APIs
// =====================================

export const getWorkspaces = () =>
  api.get<WorkspacesResponse>(
    `${DATABRICKS_API_BASE}/cost/workspaces`,
    {},
    0,
    WorkspacesResponseSchema
  );

export const getWorkspacesCostTotal = (params: WorkspacesCostParams) =>
  api.get<CostTotalResponse>(
    `${DATABRICKS_API_BASE}/cost/total`,
    params,
    0,
    CostTotalResponseSchema
  );

export const getWorkspacesCostBreakdown = (params: WorkspacesBreakdownParams) => {
  const { group_by = "workspace", ...restParams } = params;
  return api.get<WorkspacesCostBreakdownResponse>(
    `${DATABRICKS_API_BASE}/cost/breakdown/${group_by}`,
    restParams,
    0,
    WorkspacesCostBreakdownResponseSchema
  );
};

export const getWorkspacesCostTrendsByProduct = (params: WorkspacesTrendsParams) =>
  api.get<ProductTrendsResponse>(
    `${DATABRICKS_API_BASE}/cost/trends/by-product`,
    params,
    0,
    ProductTrendsResponseSchema
  );

export const getWorkspacesEfficiency = (params: WorkspacesCostParams) =>
  api.get<WorkspaceEfficiencyResponse>(
    `${DATABRICKS_API_BASE}/cost/workspace-efficiency`,
    params,
    0,
    WorkspaceEfficiencyResponseSchema
  );

// AI Services APIs
export const getAIServicesSummary = (params: AIServicesSummaryParams) =>
  api.get<AIServicesSummaryResponse>(
    "/ai_services/summary",
    params,
    0,
    aiServicesSummaryResponseSchema
  );

export const getAIServiceGraph = (serviceType: string, params: AIServiceGraphParams) =>
  api.get<AIServiceGraphResponse>(
    `/ai_services/${serviceType}/graph`,
    params,
    0,
    aiServiceGraphResponseSchema
  );

export const getAIServiceUsage = (serviceType: string, params: AIServiceUsageParams) =>
  api.get<AIServiceUsageResponse>(
    `/ai_services/${serviceType}/usage`,
    params,
    0,
    aiServiceUsageResponseSchema
  );

export const getAIServiceBreakdown = (serviceType: string, params: AIServiceBreakdownParams) =>
  api.get<AIServiceBreakdownResponse>(
    `/ai_services/${serviceType}/breakdown`,
    params,
    0,
    aiServiceBreakdownResponseSchema
  );

export const getAIServiceFilterValues = (
  serviceType: string,
  params: AIServiceFilterValuesParams
) =>
  api.get<AIServiceFilterValuesResponse>(
    `/ai_services/${serviceType}/filter_values`,
    params,
    0,
    aiServiceFilterValuesResponseSchema
  );

export const getClusters = (params: ClustersParams = {}) =>
  api.get<ClustersResponse>(`${DATABRICKS_API_BASE}/clusters`, params, 0, ClustersResponseSchema);

export const getClusterDetail = (params: ClusterDetailParams) =>
  api.get<ClusterDetail>(
    `${DATABRICKS_API_BASE}/clusters/${params.cluster_id}`,
    {},
    0,
    ClusterDetailSchema
  );

export const getClusterUtilization = (params: ClusterTimeseriesParams) =>
  api.get<ClusterUtilizationResponse>(
    `${DATABRICKS_API_BASE}/clusters/${params.cluster_id}/utilization`,
    {
      start_date: params.start_date,
      end_date: params.end_date,
      granularity: params.granularity,
    },
    0,
    ClusterUtilizationResponseSchema
  );

export const getClusterCost = (params: ClusterTimeseriesParams) =>
  api.get<ClusterCostResponse>(
    `${DATABRICKS_API_BASE}/clusters/${params.cluster_id}/cost`,
    {
      start_date: params.start_date,
      end_date: params.end_date,
      granularity: params.granularity,
    },
    0,
    ClusterCostResponseSchema
  );

export const getClusterFilters = (params: ClusterFilterParams = {}) =>
  api.get<ClusterFilterOptions>(
    `${DATABRICKS_API_BASE}/clusters/filters`,
    params,
    0,
    ClusterFilterOptionsSchema
  );

export const getClustersCostTrends = (params: ClustersCostTrendsParams) =>
  api.get<ClustersCostTrendsResponse>(
    `${DATABRICKS_API_BASE}/clusters/cost-trends`,
    params,
    0,
    ClustersCostTrendsResponseSchema
  );

// =====================================
// SSO Group Sync APIs
// =====================================
export const getSSOTeamSyncConfig = () =>
  api.get<z.infer<typeof SSOTeamSyncConfigResponseSchema>>(
    "/sso-group-mappings/config",
    {},
    0,
    SSOTeamSyncConfigResponseSchema
  );

export const updateSSOTeamSyncConfig = (data: SSOTeamSyncConfigUpdatePayload) =>
  api.put<z.infer<typeof SSOTeamSyncConfigResponseSchema>, SSOTeamSyncConfigUpdatePayload>(
    "/sso-group-mappings/config",
    data,
    SSOTeamSyncConfigResponseSchema
  );

export const getSSOGroupMappings = (params?: { mapping_type?: string }) =>
  api.get<z.infer<typeof SSOGroupMappingListSchema>>(
    "/sso-group-mappings/mappings",
    params ?? {},
    0,
    SSOGroupMappingListSchema
  );

export const createSSOGroupMapping = (data: {
  external_group_identifier: string;
  match_type: string;
  mapping_type: string;
  target_id: number;
}) =>
  api.post<z.infer<typeof SSOGroupMappingResponseSchema>>(
    "/sso-group-mappings/mappings",
    data,
    SSOGroupMappingResponseSchema
  );

export const updateSSOGroupMapping = (mappingId: number, data: SSOGroupMappingUpdatePayload) =>
  api.put<z.infer<typeof SSOGroupMappingResponseSchema>, SSOGroupMappingUpdatePayload>(
    `/sso-group-mappings/mappings/${mappingId}`,
    data,
    SSOGroupMappingResponseSchema
  );

export const deleteSSOGroupMapping = (mappingId: number) =>
  api.delete<z.infer<typeof StandardResponseWithWarningSchema>>(
    `/sso-group-mappings/mappings/${mappingId}`,
    {},
    StandardResponseWithWarningSchema
  );

export const previewSSOGroupMappings = (data: { external_groups: string[] }) =>
  api.post<z.infer<typeof SSOPreviewResponseSchema>>(
    "/sso-group-mappings/preview",
    data,
    SSOPreviewResponseSchema
  );

export const getSSOAuditEvents = (params: {
  user_id?: number;
  event_type?: string;
  start_date?: string;
  end_date?: string;
  limit?: number;
  offset?: number;
}) =>
  api.get<z.infer<typeof SSOAuditEventListResponseSchema>>(
    "/sso-group-mappings/audit-events",
    params,
    0,
    SSOAuditEventListResponseSchema
  );

export const getSSOTargets = () =>
  api.get<z.infer<typeof SSOTargetsResponseSchema>>(
    "/sso-group-mappings/targets",
    {},
    0,
    SSOTargetsResponseSchema
  );

export const getSSOExternalGroups = (params?: { search?: string }) =>
  api.get<z.infer<typeof SSOExternalGroupListResponseSchema>>(
    "/sso-group-mappings/external-groups",
    params ?? {},
    0,
    SSOExternalGroupListResponseSchema
  );

export const uploadSSOExternalGroups = (data: FormData) =>
  api.post<z.infer<typeof SSOBulkUploadResponseSchema>>(
    "/sso-group-mappings/external-groups/upload",
    data,
    SSOBulkUploadResponseSchema,
    { headers: { "Content-Type": "multipart/form-data" } }
  );

export const uploadSSOGroupMappings = (data: FormData) =>
  api.post<z.infer<typeof SSOBulkMappingUploadResponseSchema>>(
    "/sso-group-mappings/mappings/upload",
    data,
    SSOBulkMappingUploadResponseSchema,
    { headers: { "Content-Type": "multipart/form-data" } }
  );

// =====================================
// Databricks AI/ML Serving APIs
// =====================================

export const getDatabricksServingEndpoints = (params: DatabricksServingParams) =>
  api.get<ServingEndpointsResponse>(
    `${DATABRICKS_API_BASE}/serving`,
    params,
    0,
    ServingEndpointsResponseSchema
  );

export const getDatabricksServingUsageTrends = (params: DatabricksServingTrendsParams) =>
  api.get<OverallUsageTrendsResponse>(
    `${DATABRICKS_API_BASE}/serving/usage-trends`,
    params,
    0,
    OverallUsageTrendsResponseSchema
  );

export const getDatabricksServingCostAttribution = (params: DatabricksServingParams) =>
  api.get<CostAttributionResponse>(
    `${DATABRICKS_API_BASE}/serving/cost-attribution`,
    params,
    0,
    CostAttributionResponseSchema
  );

export const getDatabricksServingEndpointDetail = (params: DatabricksServingDetailParams) =>
  api.get<ServingEndpointDetailResponse>(
    `${DATABRICKS_API_BASE}/serving/${encodeURIComponent(params.endpoint_name)}`,
    { start_date: params.start_date, end_date: params.end_date },
    0,
    ServingEndpointDetailResponseSchema
  );

export const getDatabricksServingEndpointUsers = (params: DatabricksServingUsersParams) =>
  api.get<EndpointUsersResponse>(
    `${DATABRICKS_API_BASE}/serving/${encodeURIComponent(params.endpoint_name)}/users`,
    { start_date: params.start_date, end_date: params.end_date, limit: params.limit },
    0,
    EndpointUsersResponseSchema
  );

export const getAiMlServicesSummary = (params: AiMlServicesParams) =>
  api.get<AiMlServicesSummaryResponse>(
    `${DATABRICKS_API_BASE}/ai-ml-services/summary`,
    params,
    0,
    AiMlServicesSummaryResponseSchema
  );

export const getUnifiedAiMlEndpoints = (params: UnifiedAIMLEndpointsParams) =>
  api.get<UnifiedAIMLEndpointsListResponse>(
    `${DATABRICKS_API_BASE}/ai-ml-services/endpoints`,
    params,
    0,
    UnifiedAIMLEndpointsListResponseSchema
  );

export const getAimlEndpointFilters = (params: AimlEndpointFiltersParams) =>
  api.get<AimlEndpointFiltersResponse>(
    `${DATABRICKS_API_BASE}/ai-ml-services/endpoints/filters`,
    params,
    0,
    AimlEndpointFiltersResponseSchema
  );

export const getAimlEndpointNames = (params: AimlEndpointNamesParams) =>
  api.get<AimlEndpointNamesResponse>(
    `${DATABRICKS_API_BASE}/ai-ml-services/endpoints/filters/names`,
    params,
    0,
    AimlEndpointNamesResponseSchema
  );

export const getModelServingEndpointDetail = (params: ModelServingEndpointDetailParams) =>
  api.get<ModelServingEndpointDetail>(
    `${DATABRICKS_API_BASE}/ai-ml-services/endpoints/${encodeURIComponent(params.endpoint_name)}`,
    { service: "model_serving", start_date: params.start_date, end_date: params.end_date },
    0,
    ModelServingEndpointDetailSchema
  );

export const getModelServingEndpointTrend = (params: ModelServingEndpointDetailParams) =>
  api.get<ModelServingEndpointTrendResponse>(
    `${DATABRICKS_API_BASE}/ai-ml-services/endpoints/${encodeURIComponent(params.endpoint_name)}/trend`,
    {
      service: "model_serving",
      start_date: params.start_date,
      end_date: params.end_date,
      granularity: "auto",
    },
    0,
    ModelServingEndpointTrendResponseSchema
  );

export const getVectorSearchEndpointDetail = (params: VectorSearchEndpointDetailParams) =>
  api.get<VectorSearchEndpointDetail>(
    `${DATABRICKS_API_BASE}/ai-ml-services/endpoints/${encodeURIComponent(params.endpoint_name)}`,
    { service: "vector_search", start_date: params.start_date, end_date: params.end_date },
    0,
    VectorSearchEndpointDetailSchema
  );

export const getVectorSearchEndpointTrend = (params: VectorSearchEndpointDetailParams) =>
  api.get<VectorSearchEndpointTrendResponse>(
    `${DATABRICKS_API_BASE}/ai-ml-services/endpoints/${encodeURIComponent(params.endpoint_name)}/trend`,
    { service: "vector_search", start_date: params.start_date, end_date: params.end_date },
    0,
    VectorSearchEndpointTrendResponseSchema
  );

export const getAiGatewayEndpointDetail = (params: AiGatewayEndpointDetailParams) =>
  api.get<AiGatewayEndpointDetail>(
    `${DATABRICKS_API_BASE}/ai-ml-services/endpoints/${encodeURIComponent(params.endpoint_name)}`,
    { service: "ai_gateway", start_date: params.start_date, end_date: params.end_date },
    0,
    AiGatewayEndpointDetailSchema
  );

export const getAiGatewayEndpointTrend = (params: AiGatewayEndpointDetailParams) =>
  api.get<AiGatewayEndpointTrendResponse>(
    `${DATABRICKS_API_BASE}/ai-ml-services/endpoints/${encodeURIComponent(params.endpoint_name)}/trend`,
    { service: "ai_gateway", start_date: params.start_date, end_date: params.end_date },
    0,
    AiGatewayEndpointTrendResponseSchema
  );

export const getModelServingEndpointCallers = (params: EndpointCallersParams) =>
  api.get<EndpointCallersResponse>(
    `${DATABRICKS_API_BASE}/ai-ml-services/endpoints/${encodeURIComponent(params.endpoint_name)}/callers`,
    {
      start_date: params.start_date,
      end_date: params.end_date,
      limit: params.limit,
      offset: params.offset,
    },
    0,
    EndpointCallersResponseSchema
  );

export const getAIFunctionUsageBreakdown = (params: AIFunctionUsageBreakdownParams) =>
  api.get<AIFunctionUsageBreakdownResponse>(
    `${DATABRICKS_API_BASE}/ai-ml-services/endpoints/${encodeURIComponent(params.endpoint_name)}/usage-breakdown`,
    { start_date: params.start_date, end_date: params.end_date },
    0,
    AIFunctionUsageBreakdownResponseSchema
  );

// --- Entity Filter APIs ---

export interface EntityFilterParams {
  prefix?: string;
  page?: number;
  size?: number;
  workspace_id?: string;
  start_date?: string;
  end_date?: string;
}

export const getDatabricksClusterFilters = (params: EntityFilterParams = {}) =>
  api.get<DatabricksClusterFilterOptions>(
    `${DATABRICKS_API_BASE}/clusters/filters`,
    params,
    0,
    DatabricksClusterFilterOptionsSchema
  );

export const getDatabricksWarehouseFilters = (params: EntityFilterParams = {}) =>
  api.get<WarehouseFilterOptions>(
    `${DATABRICKS_API_BASE}/warehouses/filters`,
    params,
    0,
    WarehouseFilterOptionsSchema
  );

// Databricks access-rule picker: the catalog of accounts / workspaces a team's
// access rules can target. Powers the Teams page dropdowns for Databricks tenants.
export const DatabricksAccessRuleWorkspaceOptionSchema = z.object({
  workspace_id: z.string(),
  workspace_name: z.string(),
  account_id: z.string(),
});

export const DatabricksAccessRuleAccountOptionSchema = z.object({
  account_id: z.string(),
  workspace_count: z.number(),
});

export const DatabricksAccessRuleOptionsResponseSchema = z.object({
  accounts: z.array(DatabricksAccessRuleAccountOptionSchema),
  workspaces: z.array(DatabricksAccessRuleWorkspaceOptionSchema),
});

export type DatabricksAccessRuleOptionsResponse = z.infer<
  typeof DatabricksAccessRuleOptionsResponseSchema
>;

export const getDatabricksAccessRuleOptions = () =>
  api.get<DatabricksAccessRuleOptionsResponse>(
    `${DATABRICKS_API_BASE}/access-rules/options`,
    {},
    0,
    DatabricksAccessRuleOptionsResponseSchema
  );

export const getDatabricksJobFilterField = (field: string, params: EntityFilterParams = {}) =>
  api.get<PaginatedFilterOptions>(
    `${DATABRICKS_API_BASE}/jobs/filters/${field}`,
    params,
    0,
    PaginatedFilterOptionsSchema
  );

// Creators are returned as id+name pairs. The dropdown displays `name` (the
// readable email/principal name) but sends `id` (the principal id) back to the
// /jobs endpoint, which still filters on `creator_id`.
export const getDatabricksJobCreatorFilters = (params: EntityFilterParams = {}) =>
  api.get<PaginatedJobCreatorsResponse>(
    `${DATABRICKS_API_BASE}/jobs/filters/creators`,
    params,
    0,
    PaginatedJobCreatorsResponseSchema
  );

export const getDatabricksUserFilters = (params: EntityFilterParams = {}) =>
  api.get<UserFilterOptions>(
    `${DATABRICKS_API_BASE}/users/filters`,
    params,
    0,
    UserFilterOptionsSchema
  );

export const getDatabricksQueryFilters = (params: EntityFilterParams = {}) =>
  api.get<QueryFilterOptions>(
    `${DATABRICKS_API_BASE}/queries/filters`,
    params,
    0,
    QueryFilterOptionsSchema
  );

export const getDatabricksQueryStatementIds = (params: QueryStatementIdSearchParams) =>
  api.get<QueryStatementIdsResponse>(
    `${DATABRICKS_API_BASE}/queries/filters/statement-ids`,
    params,
    0,
    QueryStatementIdsResponseSchema
  );

// --- Query APIs ---

export const getDatabricksQuerySummary = (params: DatabricksQueriesParams) =>
  api.get<QuerySummaryResponse>(
    `${DATABRICKS_API_BASE}/queries/summary`,
    params,
    0,
    QuerySummaryResponseSchema
  );

export const getDatabricksQueryTrends = (
  params: DatabricksQueriesParams & { granularity?: string }
) =>
  api.get<QueryTrendsResponse>(
    `${DATABRICKS_API_BASE}/queries/trends`,
    params,
    0,
    QueryTrendsResponseSchema
  );

export const getDatabricksQueries = (params: DatabricksQueriesParams) =>
  api.get<QueriesResponse>(`${DATABRICKS_API_BASE}/queries`, params, 0, QueriesResponseSchema);

export const getDatabricksSlowQueries = (
  params: DatabricksQueriesParams & { threshold_ms?: number; limit?: number }
) =>
  api.get<SlowQueriesResponse>(
    `${DATABRICKS_API_BASE}/queries/slow`,
    params,
    0,
    SlowQueriesResponseSchema
  );

export const getDatabricksSpillQueries = (
  params: DatabricksQueriesParams & { min_spill_bytes?: number; limit?: number }
) =>
  api.get<SpillQueriesResponse>(
    `${DATABRICKS_API_BASE}/queries/spills`,
    params,
    0,
    SpillQueriesResponseSchema
  );

export const getDatabricksFullScanQueries = (
  params: DatabricksQueriesParams & { min_read_bytes?: number; limit?: number }
) =>
  api.get<FullScanQueriesResponse>(
    `${DATABRICKS_API_BASE}/queries/full-scans`,
    params,
    0,
    FullScanQueriesResponseSchema
  );

export const getDatabricksFailedQueries = (params: DatabricksQueriesParams & { limit?: number }) =>
  api.get<FailedQueriesResponse>(
    `${DATABRICKS_API_BASE}/queries/failed`,
    params,
    0,
    FailedQueriesResponseSchema
  );

export const getDatabricksStatementTypeBreakdown = (params: DatabricksQueriesParams) =>
  api.get<StatementTypeBreakdownResponse>(
    `${DATABRICKS_API_BASE}/queries/breakdown/statement-type`,
    params,
    0,
    StatementTypeBreakdownResponseSchema
  );

export const getDatabricksUserQueryBreakdown = (
  params: DatabricksQueriesParams & { limit?: number }
) =>
  api.get<UserQueryBreakdownResponse>(
    `${DATABRICKS_API_BASE}/queries/breakdown/user`,
    params,
    0,
    UserQueryBreakdownResponseSchema
  );

export const getDatabricksUnifiedQueries = (params: DatabricksUnifiedQueriesParams) =>
  api.get<UnifiedQueriesResponse>(
    `${DATABRICKS_API_BASE}/queries/unified`,
    params,
    0,
    UnifiedQueriesResponseSchema
  );

export const getDatabricksQueryDetail = (statementId: string) =>
  api.get<QueryDetailResponse>(
    `${DATABRICKS_API_BASE}/queries/${statementId}`,
    {},
    0,
    QueryDetailResponseSchema
  );

export const getDatabricksQuerySqlLineage = (statementId: string) =>
  api.get<QuerySqlLineageResponse>(
    `${DATABRICKS_API_BASE}/queries/${statementId}/sql-lineage`,
    {},
    0,
    QuerySqlLineageResponseSchema
  );

// Jobs APIs (Databricks)
export const getJobRunTrends = (params: JobRunTrendsParams) =>
  api.get<JobRunTrendsResponse>(
    `${DATABRICKS_API_BASE}/jobs/trends`,
    params,
    0,
    JobRunTrendsResponseSchema
  );

export const getJobs = (params: JobsParams = {}) =>
  api.get<JobsResponse>(`${DATABRICKS_API_BASE}/jobs`, params, 0, JobsResponseSchema);

export const getJobsSummary = (params: JobsSummaryParams) =>
  api.get<JobsSummaryResponse>(
    `${DATABRICKS_API_BASE}/jobs/summary`,
    params,
    0,
    JobsSummaryResponseSchema
  );

export const getJobCostTrends = (params: JobCostTrendsParams) =>
  api.get<JobCostTrendsResponse>(
    `${DATABRICKS_API_BASE}/jobs/trends/cost`,
    params,
    0,
    JobCostTrendsResponseSchema
  );

export const getCloudCostTrends = (params: CloudCostTrendsParams) =>
  api.get<CloudCostChartRow[]>(
    `${DATABRICKS_API_BASE}/cost/cloud/trends`,
    params,
    0,
    CloudCostTrendsResponseSchema
  );

export const getCloudCostProjection = (params: CloudCostProjectionParams) =>
  api.get<CloudCostProjectionRow[]>(
    `${DATABRICKS_API_BASE}/cost/cloud/projection`,
    params,
    0,
    CloudCostProjectionResponseSchema
  );

export const getDatabricksJobDetail = (
  jobId: string,
  params: { start_date?: string; end_date?: string } = {}
) =>
  api.get<JobDetailResponse>(
    `${DATABRICKS_API_BASE}/jobs/${jobId}`,
    params,
    0,
    JobDetailResponseSchema
  );

export const getDatabricksJobRuns = (
  jobId: string,
  params: {
    start_date?: string;
    end_date?: string;
    run_ids?: string[];
    trigger_types?: string[];
    result_state?: string[];
    cluster_ids?: string[];
    page?: number;
    page_size?: number;
    sortAttribute?: string;
    sortOrder?: "asc" | "desc" | "";
  } = {}
) =>
  api.get<JobRunsResponse>(
    `${DATABRICKS_API_BASE}/jobs/${jobId}/runs`,
    params,
    0,
    JobRunsResponseSchema
  );

export const getDatabricksJobStats = (
  jobId: string,
  params: { start_date?: string; end_date?: string } = {}
) =>
  api.get<JobStatsResponse>(
    `${DATABRICKS_API_BASE}/jobs/${jobId}/stats`,
    params,
    0,
    JobStatsResponseSchema
  );

// Distinct clusters used by this job's runs within the supplied window.
// Powers the Cluster filter dropdown on Run History so it surfaces every
// cluster across all pages, not just the runs on the current page.
export const getDatabricksJobRunClusters = (
  jobId: string,
  params: { start_date: string; end_date?: string }
) =>
  api.get<JobRunClustersResponse>(
    `${DATABRICKS_API_BASE}/jobs/${jobId}/runs/clusters`,
    params,
    0,
    JobRunClustersResponseSchema
  );

export const getDatabricksSingleJobRunTrends = (
  jobId: string,
  params: { start_date?: string; end_date?: string; granularity?: "day" | "month" } = {}
) =>
  api.get<JobRunTrendsResponse>(
    `${DATABRICKS_API_BASE}/jobs/${jobId}/trends`,
    params,
    0,
    JobRunTrendsResponseSchema
  );

export const getDatabricksResourceOpportunities = (params: {
  resource_uri: string;
  resource_type: number;
  instance_id?: string;
  workspace_id?: string;
  start_date?: string;
  end_date?: string;
  status_filter?: string;
  sort?: string;
  sort_order?: string;
}) =>
  api.get<GetOpportunitiesResourceResponse>(
    `${DATABRICKS_API_BASE}/opportunities/resource`,
    params,
    0,
    GetOpportunitiesResourceResponseSchema
  );

// Auto-Tune (Databricks): read + toggle
export const getDatabricksJobAutoRecommendations = (
  jobId: string,
  params: { instance_id: number | string; workspace_id?: string; limit?: number }
) =>
  api.get<DbxAutoRecommendationsResponse>(
    `${DATABRICKS_API_BASE}/jobs/${jobId}/auto-recommendations`,
    params,
    0,
    DbxAutoRecommendationsResponseSchema
  );

export const toggleDatabricksJobAutoTune = (
  jobId: string,
  params: {
    instance_id: number | string;
    workspace_id?: string;
    task_key?: string;
    force?: boolean;
  },
  body: {
    enabled: boolean;
    scope?: "all_tasks" | "specific_tasks";
    enabled_task_keys?: string[];
  }
) => {
  const qs = new URLSearchParams({ instance_id: String(params.instance_id) });
  if (params.workspace_id) qs.set("workspace_id", params.workspace_id);
  if (params.task_key) qs.set("task_key", params.task_key);
  if (params.force) qs.set("force", "true");
  return api.update<ToggleAutoTuneResponse>(
    `${DATABRICKS_API_BASE}/jobs/${jobId}/auto-tune/toggle?${qs.toString()}`,
    body,
    ToggleAutoTuneResponseSchema
  );
};

// Auto Tune (Databricks all-purpose clusters): status / recommendations /
// history / savings-trends reads + the toggle. Mirrors the jobs wrappers
// but scoped to ``/clusters/{cluster_id}/auto-tune/*``. The toggle records
// a config + audit change only — no Databricks cluster edit is invoked.
const clusterAutoTuneUrl = (clusterId: string, segment: string) =>
  `${DATABRICKS_API_BASE}/clusters/${clusterId}/auto-tune/${segment}`;

export const getClusterAutoTuneStatus = (
  clusterId: string,
  params: { instance_id: number | string; workspace_id?: string }
) =>
  api.get<AutoTuneStatusResponse>(
    clusterAutoTuneUrl(clusterId, "status"),
    params,
    0,
    AutoTuneStatusResponseSchema
  );

export const getClusterAutoTuneRecommendations = (
  clusterId: string,
  params: { instance_id: number | string; workspace_id?: string }
) =>
  api.get<ClusterAutoTuneRecommendationsResponse>(
    clusterAutoTuneUrl(clusterId, "recommendations"),
    params,
    0,
    ClusterAutoTuneRecommendationsResponseSchema
  );

export const getClusterAutoTuneHistory = (
  clusterId: string,
  params: {
    instance_id: number | string;
    workspace_id?: string;
    event_types?: string;
    period?: number;
    page?: number;
    page_size?: number;
  }
) =>
  api.get<AutoTuneHistoryResponse>(
    clusterAutoTuneUrl(clusterId, "history"),
    params,
    0,
    AutoTuneHistoryResponseSchema
  );

export const getClusterAutoTuneSavingsTrends = (
  clusterId: string,
  params: {
    instance_id: number | string;
    start_date: string;
    end_date: string;
    granularity?: string;
  }
) =>
  api.get<AutoTuneSavingsTrendsResponse>(
    clusterAutoTuneUrl(clusterId, "savings-trends"),
    params,
    0,
    AutoTuneSavingsTrendsResponseSchema
  );

export const toggleClusterAutoTune = (
  clusterId: string,
  params: { instance_id: number | string; workspace_id?: string },
  body: { enabled: boolean; rec_type?: "resize" | "auto_terminate" | null }
) => {
  const qs = new URLSearchParams({ instance_id: String(params.instance_id) });
  if (params.workspace_id) qs.set("workspace_id", params.workspace_id);
  return api.update<ClusterAutoTuneToggleResponse>(
    `${clusterAutoTuneUrl(clusterId, "toggle")}?${qs.toString()}`,
    body,
    ClusterAutoTuneToggleResponseSchema
  );
};

// Spark UI deep-link for a Databricks job run (or specific task run within
// it). BE always returns 200 with a `state` discriminator; real HTTP errors
// (auth, server crashes) route through the standard global error handler.
export const getDatabricksJobRunSparkUi = (
  jobId: string,
  runId: string,
  params: { task_run_id?: string } = {}
) =>
  api.get<JobRunSparkUiResponse>(
    `${DATABRICKS_API_BASE}/jobs/${jobId}/runs/${runId}/spark-ui`,
    params,
    0,
    JobRunSparkUiResponseSchema
  );

// Aggregate stats for a single task across the full filtered window
// (ignores pagination). Used by the TaskRunsSummary card.
export const getDatabricksTaskStats = (
  jobId: string,
  taskKey: string,
  params: DatabricksTaskStatsParams
) =>
  api.get<DatabricksTaskStats>(
    `${DATABRICKS_API_BASE}/jobs/${jobId}/tasks/${encodeURIComponent(taskKey)}/stats`,
    params,
    0,
    DatabricksTaskStatsSchema
  );

// Per-task runs-graph (Databricks). Shape mirrors the Snowflake
// runs_graph response so the shared `<Graph>` component renders both.
export const getDatabricksTaskRunsGraph = (
  jobId: string,
  taskKey: string,
  params: DatabricksTaskRunsGraphParams
) =>
  api.get<DatabricksTaskRunGraphPoint[]>(
    `${DATABRICKS_API_BASE}/jobs/${jobId}/tasks/${encodeURIComponent(taskKey)}/runs-graph`,
    params,
    0,
    z.array(DatabricksTaskRunGraphPointSchema)
  );

// Job-level task runs (Databricks). One aggregated row per task on the
// DAG; `run_count` is populated and `task_run_id` is null. `run_id` and
// `task_key` are NOT accepted here — use `getDatabricksJobTaskRunsDetail`
// for the run- or task-scoped view (altimate-backend #5400).
export const getDatabricksJobTaskRuns = (
  jobId: string,
  params: {
    start_date: string;
    end_date?: string;
    sortAttribute?: string;
    sortOrder?: string;
    page?: number;
    page_size?: number;
  }
) =>
  api.get<JobTaskRunsResponse>(
    `${DATABRICKS_API_BASE}/jobs/${jobId}/task-runs`,
    params,
    0,
    JobTaskRunsResponseSchema
  );

// Run- or task-scoped task runs (Databricks). One row per (task_key,
// job_run_id); `task_run_id` is populated and `run_count` is null.
// Exactly one of `run_id` / `task_key` must be set — the BE returns 400
// if both are omitted.
export const getDatabricksJobTaskRunsDetail = (
  jobId: string,
  params: {
    start_date: string;
    end_date?: string;
    run_id?: string;
    task_key?: string;
    sortAttribute?: string;
    sortOrder?: string;
    page?: number;
    page_size?: number;
  }
) =>
  api.get<JobTaskRunsResponse>(
    `${DATABRICKS_API_BASE}/jobs/${jobId}/task-runs/detail`,
    params,
    0,
    JobTaskRunsResponseSchema
  );

// Static job task list (every task in the workflow DAG, including
// tasks that have never run). Backed by the BE
// /jobs/{id}/tasks-inventory endpoint reading
// ``dbx_job_tasks_inventory``. The Auto Tune surfaces use this as
// the authoritative task list.
export const getDatabricksJobTasksInventory = (
  jobId: string,
  params: { instance_id: number | string }
) =>
  api.get<JobTasksInventoryResponse>(
    `${DATABRICKS_API_BASE}/jobs/${jobId}/tasks-inventory`,
    params,
    0,
    JobTasksInventoryResponseSchema
  );

// --- User APIs ---

export const getDatabricksUsers = (params: DatabricksUsersParams = {}) =>
  api.get<UsersResponse>(`${DATABRICKS_API_BASE}/users`, params, 0, UsersResponseSchema);

export const getDatabricksTopUsersByQueries = (params: DatabricksUsersParams = {}) =>
  api.get<TopUsersByQueriesResponse>(
    `${DATABRICKS_API_BASE}/users/top/queries`,
    params,
    0,
    TopUsersByQueriesResponseSchema
  );

export const getDatabricksTopUsersByDataRead = (params: DatabricksUsersParams = {}) =>
  api.get<TopUsersByDataReadResponse>(
    `${DATABRICKS_API_BASE}/users/top/data-read`,
    params,
    0,
    TopUsersByDataReadResponseSchema
  );

export const getDatabricksUserDetail = (params: DatabricksUserDetailParams) =>
  api.get<UserDetailResponse>(
    `${DATABRICKS_API_BASE}/users/${encodeURIComponent(params.user_name)}`,
    { start_date: params.start_date, end_date: params.end_date },
    0,
    UserDetailResponseSchema
  );

export const getDatabricksUserTrends = (params: DatabricksUserTrendsParams) =>
  api.get<UserTrendsResponse>(
    `${DATABRICKS_API_BASE}/users/${encodeURIComponent(params.user_name)}/trends`,
    {
      start_date: params.start_date,
      end_date: params.end_date,
      granularity: params.granularity,
    },
    0,
    UserTrendsResponseSchema
  );

export const getDatabricksUserStatementBreakdown = (params: DatabricksUserDetailParams) =>
  api.get<UserStatementBreakdownResponse>(
    `${DATABRICKS_API_BASE}/users/${encodeURIComponent(params.user_name)}/statement-breakdown`,
    { start_date: params.start_date, end_date: params.end_date },
    0,
    UserStatementBreakdownResponseSchema
  );

export const getDatabricksServicePrincipalConfig = () =>
  api.get<ServicePrincipalConfig>(
    `${DATABRICKS_API_BASE}/users/service-principal-config`,
    {},
    0,
    ServicePrincipalConfigSchema
  );

export const upsertDatabricksServicePrincipalConfig = (data: ServicePrincipalConfig) =>
  api.put<ServicePrincipalConfig, ServicePrincipalConfig>(
    `${DATABRICKS_API_BASE}/users/service-principal-config`,
    data,
    ServicePrincipalConfigSchema
  );

// Spark Analysis API (Databricks)
export const getDatabricksSparkAnalysis = (
  jobId: string,
  params: { run_id?: string; task_key?: string } = {}
) =>
  api.get<SparkAnalysisSummaryResponse>(
    `${DATABRICKS_API_BASE}/jobs/${jobId}/spark-analysis`,
    params,
    0,
    SparkAnalysisSummaryResponseSchema
  );

export const getDatabricksSparkOpportunities = (
  jobId: string,
  params: { cluster_id?: string } = {}
) =>
  api.get<SparkOpportunitiesResponse>(
    `${DATABRICKS_API_BASE}/jobs/${jobId}/spark-analysis/opportunities`,
    params,
    0,
    SparkOpportunitiesResponseSchema
  );

export const getDatabricksStageMetricsPaginated = (
  jobId: string,
  params: {
    run_id?: string;
    sortAttribute?: string;
    sortOrder?: string;
    page?: number;
    page_size?: number;
    stage_ids?: string;
  } = {}
) =>
  api.get<SparkStageMetricsPaginatedResponse>(
    `${DATABRICKS_API_BASE}/jobs/${jobId}/spark-analysis/stage-metrics`,
    params,
    0,
    SparkStageMetricsPaginatedResponseSchema
  );

export const getDatabricksSparkTasksPaginated = (
  jobId: string,
  params: {
    run_id?: string;
    sortAttribute?: string;
    sortOrder?: string;
    page?: number;
    page_size?: number;
    stage_ids?: string;
  } = {}
) =>
  api.get<SparkTasksPaginatedResponse>(
    `${DATABRICKS_API_BASE}/jobs/${jobId}/spark-analysis/tasks`,
    params,
    0,
    SparkTasksPaginatedResponseSchema
  );

// =====================================
// Databricks Summary APIs
// =====================================

export const getDatabricksSummaryCostOverview = (params: SummaryCostParams) =>
  api.get<SummaryCostOverviewResponse>(
    `${DATABRICKS_API_BASE}/cost/overview`,
    params,
    0,
    SummaryCostOverviewResponseSchema
  );

export const getDatabricksSummaryCostTrendsByProduct = (params: SummaryTrendsParams) =>
  api.get<SummaryProductTrendsResponse>(
    `${DATABRICKS_API_BASE}/cost/trends/by-product`,
    params,
    0,
    SummaryProductTrendsResponseSchema
  );

export const getDatabricksSummaryCostTrendsEstimate = (params: SummaryTrendsParams) =>
  api.get<SummaryCostEstimateTrendsResponse>(
    `${DATABRICKS_API_BASE}/cost/overall/estimate`,
    params,
    0,
    SummaryCostEstimateTrendsResponseSchema
  );

export const getDatabricksSummaryCostTotalEstimate = (params: SummaryCostParams) =>
  api.get<SummaryCostTotalEstimateResponse>(
    `${DATABRICKS_API_BASE}/cost/total/estimate`,
    params,
    0,
    SummaryCostTotalEstimateResponseSchema
  );

export const getDatabricksSummarySavingsOverall = (params: SummarySavingsParams) =>
  api.get<SummarySavingsOverallResponse>(
    `${DATABRICKS_API_BASE}/savings/overall`,
    params,
    0,
    SummarySavingsOverallResponseSchema
  );

export const getDatabricksSummarySavingsOverallEstimate = (params: SummarySavingsParams) =>
  api.get<SummarySavingsOverallEstimateResponse>(
    `${DATABRICKS_API_BASE}/savings/overall/estimate`,
    params,
    0,
    SummarySavingsOverallEstimateResponseSchema
  );
// Databricks Connection APIs
export const listDatabricksConnections = () =>
  api.get<DatabricksConnectionListResponse>(
    `${DATABRICKS_API_BASE}/connections`,
    {},
    0,
    DatabricksConnectionListResponseSchema
  );

export const getDatabricksConnection = (id: number) =>
  api.get<DatabricksConnectionResponse>(
    `${DATABRICKS_API_BASE}/connections/${id}`,
    {},
    0,
    DatabricksConnectionResponseSchema
  );

export const createDatabricksConnection = (data: DatabricksConnectionCreateRequest) =>
  api.post<DatabricksConnectionResponse>(
    `${DATABRICKS_API_BASE}/connections`,
    data,
    DatabricksConnectionResponseSchema
  );

export const deleteDatabricksConnection = (id: number) =>
  api.delete<StandardResponse>(
    `${DATABRICKS_API_BASE}/connections/${id}`,
    {},
    StandardResponseSchema
  );

export const testDatabricksConnection = (data: DatabricksConnectionTestRequest) =>
  api.post<DatabricksConnectionTestResponse>(
    `${DATABRICKS_API_BASE}/connections/test`,
    data,
    DatabricksConnectionTestResponseSchema
  );
